// client/src/coms/views/canvas_parts/canvas_room_view_parts/oilPaintGpu.js

import { syncPaintBrushLoadInfo } from './paintBrushUtils';

const MAX_HEIGHT = 4000;
const FLOOR_HEIGHT = 1;
const KNIFE_SIDE_MAX_HEIGHT = 300;
const KNIFE_FRONT_MAX_HEIGHT = 200;

function clamp(value, minimum, maximum) {
  return Math.max(minimum, Math.min(maximum, Number(value) || 0));
}

function compileShader(gl, type, source) {
  const shader = gl.createShader(type);
  if (!shader) throw new Error('Unable to create oil paint shader.');
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const message = gl.getShaderInfoLog(shader) || 'Oil paint shader compilation failed.';
    gl.deleteShader(shader);
    throw new Error(message);
  }
  return shader;
}

function createProgram(gl, vertexSource, fragmentSource) {
  const vertexShader = compileShader(gl, gl.VERTEX_SHADER, vertexSource);
  const fragmentShader = compileShader(gl, gl.FRAGMENT_SHADER, fragmentSource);
  const program = gl.createProgram();
  if (!program) throw new Error('Unable to create oil paint program.');
  gl.attachShader(program, vertexShader);
  gl.attachShader(program, fragmentShader);
  gl.linkProgram(program);
  gl.deleteShader(vertexShader);
  gl.deleteShader(fragmentShader);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    const message = gl.getProgramInfoLog(program) || 'Oil paint program link failed.';
    gl.deleteProgram(program);
    throw new Error(message);
  }
  return program;
}

function normaliseHexColour(value, fallback = '#d52b24') {
  const raw = String(value || '').trim();
  if (/^#[0-9a-f]{6}$/i.test(raw)) return raw.toLowerCase();
  if (/^#[0-9a-f]{3}$/i.test(raw)) {
    return `#${raw[1]}${raw[1]}${raw[2]}${raw[2]}${raw[3]}${raw[3]}`.toLowerCase();
  }
  return fallback;
}

function hexToColour(value) {
  const raw = normaliseHexColour(value).slice(1);
  return [
    Number.parseInt(raw.slice(0, 2), 16) / 255,
    Number.parseInt(raw.slice(2, 4), 16) / 255,
    Number.parseInt(raw.slice(4, 6), 16) / 255,
  ];
}

const QUAD_VERTEX = `
  attribute vec2 a_position;
  varying vec2 v_uv;
  void main() {
    v_uv = a_position * 0.5 + 0.5;
    gl_Position = vec4(a_position, 0.0, 1.0);
  }
`;

const DOCUMENT_VERTEX = `
  attribute vec2 a_position;
  uniform vec2 u_resolution;
  varying vec2 v_documentPosition;
  void main() {
    vec2 zeroToOne = a_position / u_resolution;
    vec2 clip = vec2(zeroToOne.x * 2.0 - 1.0, 1.0 - zeroToOne.y * 2.0);
    gl_Position = vec4(clip, 0.0, 1.0);
    v_documentPosition = a_position;
  }
`;

const COPY_FRAGMENT = `
  precision highp float;
  uniform sampler2D u_source;
  varying vec2 v_uv;
  void main() {
    gl_FragColor = texture2D(u_source, v_uv);
  }
`;


const FLUID_VELOCITY_SPLAT_FRAGMENT = `
  precision highp float;
  uniform sampler2D u_selectionMask;
  uniform vec2 u_resolution;
  uniform vec2 u_start;
  uniform vec2 u_end;
  uniform vec2 u_direction;
  uniform float u_radius;
  uniform float u_speed;
  uniform float u_selectionEnabled;
  varying vec2 v_documentPosition;

  vec2 toUv(vec2 point) {
    return vec2(point.x / u_resolution.x, 1.0 - point.y / u_resolution.y);
  }

  float segmentDistance(vec2 point, vec2 a, vec2 b) {
    vec2 segment = b - a;
    float lengthSquared = max(dot(segment, segment), 0.000001);
    float t = clamp(dot(point - a, segment) / lengthSquared, 0.0, 1.0);
    vec2 closest = a + segment * t;
    return distance(point, closest);
  }

  void main() {
    vec2 uv = toUv(v_documentPosition);
    if (u_selectionEnabled > 0.5 && texture2D(u_selectionMask, uv).r < 0.5) {
      gl_FragColor = vec4(0.0);
      return;
    }

    float radius = max(0.75, u_radius);
    float distanceFromStroke = segmentDistance(v_documentPosition, u_start, u_end);
    float mask = max(1.0 - distanceFromStroke / radius, 0.0);
    if (mask <= 0.0001) {
      gl_FragColor = vec4(0.0);
      return;
    }

    vec2 direction = length(u_direction) > 0.0001
      ? normalize(u_direction)
      : normalize(max(length(u_end - u_start), 0.0001) * vec2(1.0, 0.0) + (u_end - u_start));

    float velocityMagnitude = mix(0.12, 1.85, clamp(u_speed, 0.0, 1.0));
    vec2 velocity = clamp(direction * velocityMagnitude, vec2(-2.0), vec2(2.0));
    gl_FragColor = vec4(velocity, 0.0, mask);
  }
`;

const FLUID_ADVECT_FRAGMENT = `
  precision highp float;
  uniform sampler2D u_velocityTexture;
  uniform sampler2D u_inputTexture;
  uniform float u_deltaTime;
  uniform float u_dissipation;
  uniform vec2 u_resolution;
  uniform vec2 u_min;
  uniform vec2 u_max;
  varying vec2 v_uv;

  void main() {
    vec2 coordinates = gl_FragCoord.xy;
    vec2 velocity = texture2D(u_velocityTexture, coordinates / u_resolution).rg * 100.0;
    vec2 halfCoordinates = coordinates - velocity * 0.5 * u_deltaTime;
    vec2 halfVelocity = texture2D(
      u_velocityTexture,
      clamp(halfCoordinates, u_min, u_max) / u_resolution
    ).rg * 100.0;
    vec2 finalCoordinates = coordinates - halfVelocity * u_deltaTime;
    gl_FragColor = texture2D(
      u_inputTexture,
      clamp(finalCoordinates, u_min, u_max) / u_resolution
    ) * u_dissipation;
  }
`;

const FLUID_DIVERGENCE_FRAGMENT = `
  precision highp float;
  uniform sampler2D u_velocityTexture;
  uniform vec2 u_resolution;
  varying vec2 v_uv;

  void main() {
    vec2 coordinates = gl_FragCoord.xy / u_resolution;
    vec2 delta = 1.0 / u_resolution;
    vec2 left = texture2D(u_velocityTexture, coordinates + vec2(-delta.x, 0.0)).rg;
    vec2 right = texture2D(u_velocityTexture, coordinates + vec2(delta.x, 0.0)).rg;
    vec2 bottom = texture2D(u_velocityTexture, coordinates + vec2(0.0, -delta.y)).rg;
    vec2 top = texture2D(u_velocityTexture, coordinates + vec2(0.0, delta.y)).rg;
    float divergence = ((right.x - left.x) + (top.y - bottom.y)) * 0.5;
    gl_FragColor = vec4(divergence, 0.0, 0.0, 0.0);
  }
`;

const FLUID_JACOBI_FRAGMENT = `
  precision highp float;
  uniform vec2 u_resolution;
  uniform sampler2D u_pressureTexture;
  uniform sampler2D u_divergenceTexture;
  varying vec2 v_uv;

  void main() {
    vec2 coordinates = gl_FragCoord.xy / u_resolution;
    vec2 delta = 1.0 / u_resolution;
    float divergenceCenter = texture2D(u_divergenceTexture, coordinates).r;
    float left = texture2D(u_pressureTexture, coordinates + vec2(-delta.x, 0.0)).r;
    float right = texture2D(u_pressureTexture, coordinates + vec2(delta.x, 0.0)).r;
    float bottom = texture2D(u_pressureTexture, coordinates + vec2(0.0, -delta.y)).r;
    float top = texture2D(u_pressureTexture, coordinates + vec2(0.0, delta.y)).r;
    float newPressure = (left + right + bottom + top - divergenceCenter) * 0.25;
    gl_FragColor = vec4(newPressure, 0.0, 0.0, 0.0);
  }
`;

const FLUID_SUBTRACT_FRAGMENT = `
  precision highp float;
  uniform vec2 u_resolution;
  uniform sampler2D u_pressureTexture;
  uniform sampler2D u_velocityTexture;
  varying vec2 v_uv;

  void main() {
    vec2 coordinates = gl_FragCoord.xy / u_resolution;
    vec2 delta = 1.0 / u_resolution;
    float left = texture2D(u_pressureTexture, coordinates + vec2(-delta.x, 0.0)).r;
    float right = texture2D(u_pressureTexture, coordinates + vec2(delta.x, 0.0)).r;
    float bottom = texture2D(u_pressureTexture, coordinates + vec2(0.0, -delta.y)).r;
    float top = texture2D(u_pressureTexture, coordinates + vec2(0.0, delta.y)).r;
    vec2 gradient = vec2(right - left, top - bottom) * 0.5;
    vec2 currentVelocity = texture2D(u_velocityTexture, coordinates).rg;
    vec2 nextVelocity = currentVelocity - gradient;
    gl_FragColor = vec4(nextVelocity, 0.0, 0.0);
  }
`;

const TUBE_STROKE_FRAGMENT = `
  precision highp float;
  uniform sampler2D u_tubeStroke;
  uniform sampler2D u_selectionMask;
  uniform vec2 u_resolution;
  uniform vec2 u_start;
  uniform vec2 u_end;
  uniform float u_radius;
  uniform float u_segmentId;
  uniform float u_selectionEnabled;
  varying vec2 v_documentPosition;

  vec2 toUv(vec2 point) {
    return vec2(point.x / u_resolution.x, 1.0 - point.y / u_resolution.y);
  }

  void main() {
    vec2 uv = toUv(v_documentPosition);
    vec4 oldStroke = texture2D(u_tubeStroke, uv);

    if (u_selectionEnabled > 0.5 && texture2D(u_selectionMask, uv).r < 0.5) {
      gl_FragColor = oldStroke;
      return;
    }

    vec2 segment = u_end - u_start;
    float segmentLengthSquared = max(dot(segment, segment), 0.000001);
    float t = clamp(dot(v_documentPosition - u_start, segment) / segmentLengthSquared, 0.0, 1.0);
    vec2 closest = u_start + segment * t;
    float distanceFromCentre = length(v_documentPosition - closest);
    float radius = max(0.5, u_radius);
    float q = distanceFromCentre / radius;
    float capsule = 1.0 - smoothstep(0.94, 1.02, q);
    float dome = sqrt(max(0.0, 1.0 - q * q));
    float tubePeakHeight = max(1.0, radius);
    float profileHeight = 1.0 + (tubePeakHeight - 1.0) * dome;
    float oldHeight = oldStroke.a * ${MAX_HEIGHT.toFixed(1)};
    float oldSegmentId = oldStroke.r;
    float adjacentSegment = step(u_segmentId - 1.5, oldSegmentId);
    float insideTube = step(0.0001, capsule);
    float mergedHeight = max(oldHeight, mix(oldHeight, profileHeight, capsule));
    float replacedHeight = mix(oldHeight, profileHeight, insideTube);
    float nextHeight = mix(replacedHeight, mergedHeight, adjacentSegment);
    float nextSegmentId = insideTube > 0.5 ? u_segmentId : oldSegmentId;

    gl_FragColor = vec4(nextSegmentId, 0.0, 0.0, clamp(nextHeight / ${MAX_HEIGHT.toFixed(1)}, 0.0, 1.0));
  }
`;


const STICKINESS_FLOW_FRAGMENT = `
  precision highp float;
  uniform sampler2D u_wet;
  uniform sampler2D u_dry;
  uniform sampler2D u_selectionMask;
  uniform vec2 u_pixelStep;
  uniform vec4 u_bounds;
  uniform float u_strength;
  uniform float u_selectionEnabled;
  uniform vec2 u_focusCenter;
  uniform vec2 u_focusRadius;
  uniform float u_focusEnabled;
  varying vec2 v_uv;

  float totalHeight(vec2 point) {
    vec4 wet = texture2D(u_wet, point);
    vec4 dry = texture2D(u_dry, point);
    return (wet.a + dry.a) * ${MAX_HEIGHT.toFixed(1)};
  }

  float wetHeight(vec2 point) {
    return texture2D(u_wet, point).a * ${MAX_HEIGHT.toFixed(1)};
  }

  float paintSignal(vec2 point) {
    float height = max(0.0, totalHeight(point) - ${FLOOR_HEIGHT.toFixed(1)});
    return sqrt(height);
  }

  float occupied(vec2 point) {
    return step(${(FLOOR_HEIGHT + 0.01).toFixed(2)}, totalHeight(point));
  }

  bool insideBounds(vec2 point) {
    return point.x >= u_bounds.x
      && point.y >= u_bounds.y
      && point.x <= u_bounds.z
      && point.y <= u_bounds.w;
  }

  bool insideFocus(vec2 point) {
    if (u_focusEnabled < 0.5) return true;
    vec2 safeRadius = max(u_focusRadius, vec2(0.000001));
    vec2 delta = (point - u_focusCenter) / safeRadius;
    return dot(delta, delta) <= 1.0;
  }

  float localMass(vec2 point, float spreadPixels) {
    vec2 stepSize = u_pixelStep * spreadPixels;
    float centre = paintSignal(point) * 2.75;
    float cardinal = 0.0;
    cardinal += paintSignal(point + vec2(stepSize.x, 0.0));
    cardinal += paintSignal(point - vec2(stepSize.x, 0.0));
    cardinal += paintSignal(point + vec2(0.0, stepSize.y));
    cardinal += paintSignal(point - vec2(0.0, stepSize.y));
    float diagonal = 0.0;
    diagonal += paintSignal(point + vec2(stepSize.x, stepSize.y));
    diagonal += paintSignal(point + vec2(-stepSize.x, stepSize.y));
    diagonal += paintSignal(point + vec2(stepSize.x, -stepSize.y));
    diagonal += paintSignal(point - vec2(stepSize.x, stepSize.y));
    return centre + cardinal * 1.35 + diagonal * 0.82;
  }

  float neighbourSupport() {
    float support = 0.0;
    support += occupied(v_uv + vec2(u_pixelStep.x, 0.0));
    support += occupied(v_uv - vec2(u_pixelStep.x, 0.0));
    support += occupied(v_uv + vec2(0.0, u_pixelStep.y));
    support += occupied(v_uv - vec2(0.0, u_pixelStep.y));
    support += occupied(v_uv + u_pixelStep);
    support += occupied(v_uv - u_pixelStep);
    support += occupied(v_uv + vec2(u_pixelStep.x, -u_pixelStep.y));
    support += occupied(v_uv + vec2(-u_pixelStep.x, u_pixelStep.y));
    return support / 8.0;
  }

  void considerDirection(
    vec2 direction,
    float searchPixels,
    float currentMass,
    inout vec2 bestDirection,
    inout float bestMass
  ) {
    vec2 samplePoint = v_uv + direction * u_pixelStep * searchPixels;
    vec2 destination = v_uv + direction * u_pixelStep;
    if (!insideBounds(samplePoint) || !insideBounds(destination)) return;
    if (u_selectionEnabled > 0.5 && texture2D(u_selectionMask, destination).r < 0.5) return;

    float sampleMass = localMass(samplePoint, mix(1.0, 2.0, u_strength));
    float bridge = occupied(destination);
    sampleMass *= mix(0.84, 1.0, bridge);
    if (sampleMass > bestMass && sampleMass > currentMass) {
      bestMass = sampleMass;
      bestDirection = direction;
    }
  }

  void main() {
    vec4 wet = texture2D(u_wet, v_uv);
    vec4 dry = texture2D(u_dry, v_uv);
    float wetAmount = wet.a * ${MAX_HEIGHT.toFixed(1)};
    float total = (wet.a + dry.a) * ${MAX_HEIGHT.toFixed(1)};
    float movable = min(wetAmount, max(0.0, total - ${FLOOR_HEIGHT.toFixed(1)}));

    if (!insideBounds(v_uv)
      || !insideFocus(v_uv)
      || movable <= 0.0001
      || (u_selectionEnabled > 0.5 && texture2D(u_selectionMask, v_uv).r < 0.5)) {
      gl_FragColor = vec4(0.5, 0.5, 0.0, 1.0);
      return;
    }

    float support = neighbourSupport();
    float edgeFactor = 1.0 - smoothstep(0.58, 0.94, support);
    float isolatedBoost = 1.0 - smoothstep(0.12, 0.62, support);
    if (edgeFactor <= 0.0001) {
      gl_FragColor = vec4(0.5, 0.5, 0.0, 1.0);
      return;
    }

    const float diagonal = 0.70710678118;
    float searchPixels = mix(4.0, 15.0, pow(u_strength, 0.72));
    float currentMass = localMass(v_uv, mix(1.0, 2.0, u_strength));
    vec2 bestDirection = vec2(0.0);
    float bestMass = currentMass * mix(1.10, 1.015, u_strength);

    considerDirection(vec2(1.0, 0.0), searchPixels, currentMass, bestDirection, bestMass);
    considerDirection(vec2(-1.0, 0.0), searchPixels, currentMass, bestDirection, bestMass);
    considerDirection(vec2(0.0, 1.0), searchPixels, currentMass, bestDirection, bestMass);
    considerDirection(vec2(0.0, -1.0), searchPixels, currentMass, bestDirection, bestMass);
    considerDirection(vec2(diagonal, diagonal), searchPixels, currentMass, bestDirection, bestMass);
    considerDirection(vec2(-diagonal, diagonal), searchPixels, currentMass, bestDirection, bestMass);
    considerDirection(vec2(diagonal, -diagonal), searchPixels, currentMass, bestDirection, bestMass);
    considerDirection(vec2(-diagonal, -diagonal), searchPixels, currentMass, bestDirection, bestMass);

    if (length(bestDirection) < 0.5) {
      gl_FragColor = vec4(0.5, 0.5, 0.0, 1.0);
      return;
    }

    float gain = clamp((bestMass - currentMass) / max(1.0, bestMass), 0.0, 1.0);
    float baseFlow = mix(0.008, 0.060, pow(u_strength, 1.25));
    float flow = baseFlow
      * edgeFactor
      * mix(0.28, 1.0, gain)
      * mix(1.0, 1.65, isolatedBoost);
    flow = min(flow, movable > 2.0 ? 0.085 : 0.030);
    gl_FragColor = vec4(bestDirection * 0.5 + 0.5, clamp(flow, 0.0, 0.085), 1.0);
  }
`;

const STICKINESS_GATHER_FRAGMENT = `
  precision highp float;
  uniform sampler2D u_wet;
  uniform sampler2D u_dry;
  uniform sampler2D u_flow;
  uniform sampler2D u_selectionMask;
  uniform vec2 u_pixelStep;
  uniform vec4 u_bounds;
  uniform float u_strength;
  uniform float u_selectionEnabled;
  uniform vec2 u_focusCenter;
  uniform vec2 u_focusRadius;
  uniform float u_focusEnabled;
  varying vec2 v_uv;

  bool insideBounds(vec2 point) {
    return point.x >= u_bounds.x
      && point.y >= u_bounds.y
      && point.x <= u_bounds.z
      && point.y <= u_bounds.w;
  }

  bool insideFocus(vec2 point) {
    if (u_focusEnabled < 0.5) return true;
    vec2 safeRadius = max(u_focusRadius, vec2(0.000001));
    vec2 delta = (point - u_focusCenter) / safeRadius;
    return dot(delta, delta) <= 1.0;
  }

  float movableWetHeight(vec2 point) {
    vec4 wet = texture2D(u_wet, point);
    vec4 dry = texture2D(u_dry, point);
    float wetHeight = wet.a * ${MAX_HEIGHT.toFixed(1)};
    float total = (wet.a + dry.a) * ${MAX_HEIGHT.toFixed(1)};
    return min(wetHeight, max(0.0, total - ${FLOOR_HEIGHT.toFixed(1)}));
  }

  void addIncoming(
    vec2 sourceOffset,
    inout float incomingHeight,
    inout vec3 incomingColour
  ) {
    vec2 sourcePoint = v_uv + sourceOffset * u_pixelStep;
    if (!insideBounds(sourcePoint)) return;
    vec4 flow = texture2D(u_flow, sourcePoint);
    if (flow.b <= 0.00001) return;
    vec2 direction = flow.rg * 2.0 - 1.0;
    float directionLength = length(direction);
    if (directionLength < 0.5) return;
    vec2 expected = normalize(-sourceOffset);
    if (dot(normalize(direction), expected) < 0.92) return;
    vec4 sourceWet = texture2D(u_wet, sourcePoint);
    float amount = movableWetHeight(sourcePoint) * flow.b;
    if (amount <= 0.00001) return;
    incomingHeight += amount;
    incomingColour += sourceWet.rgb * amount;
  }

  void addCohesionSample(
    vec2 sourceOffset,
    float weight,
    inout float weightedHeight,
    inout vec3 weightedColour,
    inout float totalWeight,
    inout float support
  ) {
    vec2 sourcePoint = v_uv + sourceOffset * u_pixelStep;
    if (!insideBounds(sourcePoint)) return;
    if (u_selectionEnabled > 0.5 && texture2D(u_selectionMask, sourcePoint).r < 0.5) return;
    vec4 sourceWet = texture2D(u_wet, sourcePoint);
    float amount = movableWetHeight(sourcePoint);
    weightedHeight += amount * weight;
    weightedColour += sourceWet.rgb * amount * weight;
    totalWeight += weight;
    support += step(0.01, amount);
  }

  void main() {
    vec4 wet = texture2D(u_wet, v_uv);
    vec4 dry = texture2D(u_dry, v_uv);

    if (!insideBounds(v_uv)
      || !insideFocus(v_uv)
      || (u_selectionEnabled > 0.5 && texture2D(u_selectionMask, v_uv).r < 0.5)) {
      gl_FragColor = wet;
      return;
    }

    float wetHeight = wet.a * ${MAX_HEIGHT.toFixed(1)};
    float dryHeight = dry.a * ${MAX_HEIGHT.toFixed(1)};
    float ownMovable = movableWetHeight(v_uv);
    float fixedWetFloor = max(0.0, wetHeight - ownMovable);
    vec4 ownFlow = texture2D(u_flow, v_uv);
    float ownOutflow = ownMovable * ownFlow.b;
    float remainingHeight = max(0.0, wetHeight - ownOutflow);
    float incomingHeight = 0.0;
    vec3 incomingColour = vec3(0.0);

    addIncoming(vec2(-1.0, 0.0), incomingHeight, incomingColour);
    addIncoming(vec2(1.0, 0.0), incomingHeight, incomingColour);
    addIncoming(vec2(0.0, -1.0), incomingHeight, incomingColour);
    addIncoming(vec2(0.0, 1.0), incomingHeight, incomingColour);
    addIncoming(vec2(-1.0, -1.0), incomingHeight, incomingColour);
    addIncoming(vec2(1.0, -1.0), incomingHeight, incomingColour);
    addIncoming(vec2(-1.0, 1.0), incomingHeight, incomingColour);
    addIncoming(vec2(1.0, 1.0), incomingHeight, incomingColour);

    float flowHeight = remainingHeight + incomingHeight;
    vec3 flowColour = wet.rgb;
    float flowColourWeight = remainingHeight + incomingHeight;
    if (flowColourWeight > 0.00001) {
      flowColour = (wet.rgb * remainingHeight + incomingColour) / flowColourWeight;
    }

    float weightedHeight = 0.0;
    vec3 weightedColour = vec3(0.0);
    float totalWeight = 0.0;
    float support = 0.0;
    addCohesionSample(vec2(0.0, 0.0), 4.0, weightedHeight, weightedColour, totalWeight, support);
    addCohesionSample(vec2(-1.0, 0.0), 2.0, weightedHeight, weightedColour, totalWeight, support);
    addCohesionSample(vec2(1.0, 0.0), 2.0, weightedHeight, weightedColour, totalWeight, support);
    addCohesionSample(vec2(0.0, -1.0), 2.0, weightedHeight, weightedColour, totalWeight, support);
    addCohesionSample(vec2(0.0, 1.0), 2.0, weightedHeight, weightedColour, totalWeight, support);
    addCohesionSample(vec2(-1.0, -1.0), 1.0, weightedHeight, weightedColour, totalWeight, support);
    addCohesionSample(vec2(1.0, -1.0), 1.0, weightedHeight, weightedColour, totalWeight, support);
    addCohesionSample(vec2(-1.0, 1.0), 1.0, weightedHeight, weightedColour, totalWeight, support);
    addCohesionSample(vec2(1.0, 1.0), 1.0, weightedHeight, weightedColour, totalWeight, support);

    float localMovableAverage = totalWeight > 0.0001 ? weightedHeight / totalWeight : ownMovable;
    vec3 localColour = weightedHeight > 0.0001 ? weightedColour / weightedHeight : flowColour;
    float flowMovable = max(0.0, flowHeight - fixedWetFloor);
    float fillGap = step(3.0, support);
    float cohesionMask = max(step(0.01, ownMovable), fillGap);
    float cohesion = 0.0;
    float nextMovable = mix(flowMovable, localMovableAverage, cohesion);
    vec3 nextColour = mix(flowColour, localColour, cohesion);

    float nextHeight = fixedWetFloor + nextMovable;
    if (nextHeight > 0.0001 && nextHeight < ${FLOOR_HEIGHT.toFixed(1)}) {
      if (fillGap > 0.5 && localMovableAverage >= ${FLOOR_HEIGHT.toFixed(1)}) {
        nextHeight = ${FLOOR_HEIGHT.toFixed(1)};
      } else {
        nextHeight = 0.0;
      }
    }

    float maximumWetHeight = max(0.0, ${MAX_HEIGHT.toFixed(1)} - dryHeight);
    nextHeight = min(maximumWetHeight, nextHeight);
    if (nextHeight <= 0.00001) {
      gl_FragColor = vec4(0.0);
      return;
    }

    gl_FragColor = vec4(
      clamp(nextColour, 0.0, 1.0),
      clamp(nextHeight / ${MAX_HEIGHT.toFixed(1)}, 0.0, 1.0)
    );
  }
`;


const KNIFE_PICKUP_MASS_FRAGMENT = `
  precision highp float;
  uniform sampler2D u_sourceWet;
  uniform sampler2D u_sourceDry;
  uniform sampler2D u_oldSweep;
  uniform vec2 u_resolution;
  uniform vec2 u_start;
  uniform vec2 u_end;
  uniform vec2 u_startDirection;
  uniform vec2 u_endDirection;
  uniform float u_radius;
  varying vec2 v_documentPosition;

  vec2 toUv(vec2 point) {
    return vec2(point.x / u_resolution.x, 1.0 - point.y / u_resolution.y);
  }

  float edgeSign(vec2 point, vec2 first, vec2 second) {
    return (point.x - second.x) * (first.y - second.y)
      - (first.x - second.x) * (point.y - second.y);
  }

  float insideTriangle(vec2 point, vec2 first, vec2 second, vec2 third) {
    float firstSign = edgeSign(point, first, second);
    float secondSign = edgeSign(point, second, third);
    float thirdSign = edgeSign(point, third, first);
    float hasNegative = step(min(min(firstSign, secondSign), thirdSign), -0.0001);
    float hasPositive = step(0.0001, max(max(firstSign, secondSign), thirdSign));
    return 1.0 - hasNegative * hasPositive;
  }

  float sweptBlade(vec2 point) {
    vec2 startDirection = length(u_startDirection) > 0.0001 ? normalize(u_startDirection) : vec2(1.0, 0.0);
    vec2 endDirection = length(u_endDirection) > 0.0001 ? normalize(u_endDirection) : startDirection;
    vec2 startSide = vec2(-startDirection.y, startDirection.x) * u_radius;
    vec2 endSide = vec2(-endDirection.y, endDirection.x) * u_radius;
    vec2 startLeft = u_start - startSide;
    vec2 startRight = u_start + startSide;
    vec2 endLeft = u_end - endSide;
    vec2 endRight = u_end + endSide;
    return max(
      insideTriangle(point, startLeft, startRight, endRight),
      insideTriangle(point, startLeft, endRight, endLeft)
    );
  }

  void main() {
    vec2 uv = toUv(v_documentPosition);
    if (sweptBlade(v_documentPosition) < 0.5 || texture2D(u_oldSweep, uv).r > 0.5) {
      gl_FragColor = vec4(0.0);
      return;
    }
    vec4 wet = texture2D(u_sourceWet, uv);
    vec4 dry = texture2D(u_sourceDry, uv);
    float wetHeight = wet.a * ${MAX_HEIGHT.toFixed(1)};
    float dryHeight = dry.a * ${MAX_HEIGHT.toFixed(1)};
    float totalHeight = wetHeight + dryHeight;
    float removableHeight = max(0.0, totalHeight - ${FLOOR_HEIGHT.toFixed(1)});
    if (removableHeight <= 0.0001) {
      gl_FragColor = vec4(0.0);
      return;
    }
    vec3 colour = (wet.rgb * wetHeight + dry.rgb * dryHeight) / max(totalHeight, 0.000001);
    gl_FragColor = vec4(clamp(colour, 0.0, 1.0) * removableHeight, removableHeight);
  }
`;

const KNIFE_PICKUP_COUNT_FRAGMENT = `
  precision highp float;
  uniform sampler2D u_sourceWet;
  uniform sampler2D u_sourceDry;
  uniform sampler2D u_oldSweep;
  uniform vec2 u_resolution;
  uniform vec2 u_start;
  uniform vec2 u_end;
  uniform vec2 u_startDirection;
  uniform vec2 u_endDirection;
  uniform float u_radius;
  varying vec2 v_documentPosition;

  vec2 toUv(vec2 point) { return vec2(point.x / u_resolution.x, 1.0 - point.y / u_resolution.y); }
  float edgeSign(vec2 point, vec2 first, vec2 second) {
    return (point.x - second.x) * (first.y - second.y) - (first.x - second.x) * (point.y - second.y);
  }
  float insideTriangle(vec2 point, vec2 first, vec2 second, vec2 third) {
    float a = edgeSign(point, first, second);
    float b = edgeSign(point, second, third);
    float c = edgeSign(point, third, first);
    float hasNegative = step(min(min(a, b), c), -0.0001);
    float hasPositive = step(0.0001, max(max(a, b), c));
    return 1.0 - hasNegative * hasPositive;
  }
  float sweptBlade(vec2 point) {
    vec2 sd = length(u_startDirection) > 0.0001 ? normalize(u_startDirection) : vec2(1.0, 0.0);
    vec2 ed = length(u_endDirection) > 0.0001 ? normalize(u_endDirection) : sd;
    vec2 ss = vec2(-sd.y, sd.x) * u_radius;
    vec2 es = vec2(-ed.y, ed.x) * u_radius;
    vec2 sl = u_start - ss;
    vec2 sr = u_start + ss;
    vec2 el = u_end - es;
    vec2 er = u_end + es;
    return max(insideTriangle(point, sl, sr, er), insideTriangle(point, sl, er, el));
  }
  void main() {
    vec2 uv = toUv(v_documentPosition);
    if (sweptBlade(v_documentPosition) < 0.5 || texture2D(u_oldSweep, uv).r > 0.5) {
      gl_FragColor = vec4(0.0);
      return;
    }
    vec4 wet = texture2D(u_sourceWet, uv);
    vec4 dry = texture2D(u_sourceDry, uv);
    float totalHeight = (wet.a + dry.a) * ${MAX_HEIGHT.toFixed(1)};
    float floorPixel = step(0.0001, totalHeight) * (1.0 - step(${(FLOOR_HEIGHT + 0.01).toFixed(2)}, totalHeight));
    gl_FragColor = vec4(1.0, floorPixel, 0.0, 0.0);
  }
`;

const REDUCE_SUM_FRAGMENT = `
  precision highp float;
  uniform sampler2D u_source;
  uniform vec2 u_sourceSize;
  varying vec2 v_uv;
  void main() {
    vec2 destinationSize = ceil(u_sourceSize * 0.5);
    vec2 base = floor(v_uv * destinationSize) * 2.0;
    vec2 stepSize = 1.0 / u_sourceSize;
    vec2 uv0 = (base + vec2(0.5, 0.5)) * stepSize;
    vec2 uv1 = (base + vec2(1.5, 0.5)) * stepSize;
    vec2 uv2 = (base + vec2(0.5, 1.5)) * stepSize;
    vec2 uv3 = (base + vec2(1.5, 1.5)) * stepSize;
    gl_FragColor = texture2D(u_source, clamp(uv0, vec2(0.0), vec2(1.0)))
      + texture2D(u_source, clamp(uv1, vec2(0.0), vec2(1.0)))
      + texture2D(u_source, clamp(uv2, vec2(0.0), vec2(1.0)))
      + texture2D(u_source, clamp(uv3, vec2(0.0), vec2(1.0)));
  }
`;

const KNIFE_STATE_UPDATE_FRAGMENT = `
  precision highp float;
  uniform sampler2D u_oldState;
  uniform sampler2D u_pickupMass;
  uniform sampler2D u_pickupCounts;
  uniform float u_capacityUnits;
  uniform float u_radius;
  uniform float u_segmentDistance;
  uniform float u_pressure;
  varying vec2 v_uv;
  void main() {
    vec4 oldState = texture2D(u_oldState, vec2(0.5));
    vec4 mass = texture2D(u_pickupMass, vec2(0.5));
    vec4 counts = texture2D(u_pickupCounts, vec2(0.5));
    float previousUnits = max(0.0, oldState.a);
    float rawPickupUnits = max(0.0, mass.a);
    float pickupUnits = rawPickupUnits * 1.5;
    vec3 pickupColour = rawPickupUnits > 0.000001 ? mass.rgb / rawPickupUnits : vec3(0.0);
    float colourPickupLimit = previousUnits > 0.000001
      ? max(0.25, max(previousUnits * 0.018, u_capacityUnits * 0.0015))
      : pickupUnits;
    float colourPickupUnits = min(pickupUnits, colourPickupLimit);
    float colourWeight = previousUnits + colourPickupUnits;
    vec3 projectedColour = colourWeight > 0.000001
      ? (clamp(oldState.rgb, 0.0, 1.0) * previousUnits + clamp(pickupColour, 0.0, 1.0) * colourPickupUnits) / colourWeight
      : vec3(0.0);
    float projectedUnits = previousUnits + pickupUnits;
    float overflowAvailable = max(0.0, projectedUnits - u_capacityUnits);
    float overflowReleased = min(overflowAvailable, max(0.0, u_radius * u_segmentDistance * u_pressure * 2.4));
    float carriedUnits = max(0.0, projectedUnits - overflowReleased);
    float drainPixels = max(0.0, counts.r - counts.g);
    float travelLoss = min(carriedUnits, drainPixels * ${FLOOR_HEIGHT.toFixed(1)} * 1.85);
    float storedUnits = max(0.0, carriedUnits - travelLoss);
    gl_FragColor = vec4(storedUnits > 0.000001 ? projectedColour : vec3(0.0), storedUnits);
  }
`;

const KNIFE_SWEEP_MASK_FRAGMENT = `
  precision highp float;
  uniform sampler2D u_oldMask;
  uniform sampler2D u_selectionMask;
  uniform vec2 u_resolution;
  uniform vec2 u_start;
  uniform vec2 u_end;
  uniform vec2 u_startDirection;
  uniform vec2 u_endDirection;
  uniform float u_radius;
  uniform float u_selectionEnabled;
  varying vec2 v_documentPosition;

  vec2 toUv(vec2 point) {
    return vec2(point.x / u_resolution.x, 1.0 - point.y / u_resolution.y);
  }

  float edgeSign(vec2 point, vec2 first, vec2 second) {
    return (point.x - second.x) * (first.y - second.y)
      - (first.x - second.x) * (point.y - second.y);
  }

  float insideTriangle(vec2 point, vec2 first, vec2 second, vec2 third) {
    float firstSign = edgeSign(point, first, second);
    float secondSign = edgeSign(point, second, third);
    float thirdSign = edgeSign(point, third, first);
    float hasNegative = step(min(min(firstSign, secondSign), thirdSign), -0.0001);
    float hasPositive = step(0.0001, max(max(firstSign, secondSign), thirdSign));
    return 1.0 - hasNegative * hasPositive;
  }

  float sweptBlade(vec2 point, float radius) {
    vec2 startDirection = length(u_startDirection) > 0.0001
      ? normalize(u_startDirection)
      : vec2(1.0, 0.0);
    vec2 endDirection = length(u_endDirection) > 0.0001
      ? normalize(u_endDirection)
      : startDirection;
    vec2 startSide = vec2(-startDirection.y, startDirection.x) * radius;
    vec2 endSide = vec2(-endDirection.y, endDirection.x) * radius;
    vec2 startLeft = u_start - startSide;
    vec2 startRight = u_start + startSide;
    vec2 endLeft = u_end - endSide;
    vec2 endRight = u_end + endSide;
    return max(
      insideTriangle(point, startLeft, startRight, endRight),
      insideTriangle(point, startLeft, endRight, endLeft)
    );
  }

  void main() {
    vec2 uv = toUv(v_documentPosition);
    float oldMask = texture2D(u_oldMask, uv).r;

    if (u_selectionEnabled > 0.5 && texture2D(u_selectionMask, uv).r < 0.5) {
      gl_FragColor = vec4(oldMask);
      return;
    }

    float inSweep = sweptBlade(v_documentPosition, max(0.5, u_radius));
    float mask = max(oldMask, step(0.0001, inSweep));
    gl_FragColor = vec4(mask);
  }
`;

const KNIFE_DRY_CLEAR_FRAGMENT = `
  precision highp float;
  uniform sampler2D u_dry;
  uniform sampler2D u_sweepMask;
  varying vec2 v_uv;

  void main() {
    vec4 dry = texture2D(u_dry, v_uv);
    float swept = texture2D(u_sweepMask, v_uv).r;
    gl_FragColor = swept > 0.5 ? vec4(0.0) : dry;
  }
`;

const BRUSH_DRY_CLEAR_FRAGMENT = `
  precision highp float;
  uniform sampler2D u_dry;
  uniform sampler2D u_selectionMask;
  uniform vec2 u_resolution;
  uniform vec2 u_start;
  uniform vec2 u_end;
  uniform float u_radius;
  uniform float u_bristleCount;
  uniform float u_bristleThickness;
  uniform float u_seed;
  uniform float u_selectionEnabled;
  varying vec2 v_documentPosition;

  vec2 toUv(vec2 point) {
    return vec2(point.x / u_resolution.x, 1.0 - point.y / u_resolution.y);
  }

  float randomValue(vec2 point) {
    return fract(sin(dot(point, vec2(12.9898, 78.233)) + u_seed * 0.013) * 43758.5453);
  }

  void main() {
    vec2 uv = toUv(v_documentPosition);
    vec4 dry = texture2D(u_dry, uv);

    if (u_selectionEnabled > 0.5 && texture2D(u_selectionMask, uv).r < 0.5) {
      gl_FragColor = dry;
      return;
    }

    vec2 segment = u_end - u_start;
    float safeBristleCount = clamp(floor(u_bristleCount + 0.5), 1.0, 250.0);
    float safeThickness = clamp(u_bristleThickness, 0.01, 1.0);
    float radius = max(0.5, u_radius);
    float bristleSpacing = max(1.0, radius * sqrt(3.14159265359 / safeBristleCount));
    float bristleRadius = max(0.55, bristleSpacing * mix(0.12, 0.46, safeThickness));
    float nearestBristle = 1000000.0;

    for (int bristleIndex = 0; bristleIndex < 250; bristleIndex += 1) {
      float index = float(bristleIndex);
      if (index < safeBristleCount) {
        float radialPosition = sqrt((index + 0.5) / safeBristleCount);
        float angleJitter = (randomValue(vec2(index + 11.0, u_seed + 37.0)) - 0.5) * 0.42;
        float radialJitter = mix(0.88, 1.0, randomValue(vec2(index + 71.0, u_seed + 19.0)));
        float angle = index * 2.39996322973 + u_seed * 0.001 + angleJitter;
        vec2 bristleOffset = vec2(cos(angle), sin(angle)) * radius * 0.90 * radialPosition * radialJitter;
        vec2 bristleStart = u_start + bristleOffset;
        vec2 bristleEnd = u_end + bristleOffset;
        vec2 bristleSegment = bristleEnd - bristleStart;
        float bristleSegmentLengthSquared = max(dot(bristleSegment, bristleSegment), 0.000001);
        float bristleT = clamp(
          dot(v_documentPosition - bristleStart, bristleSegment) / bristleSegmentLengthSquared,
          0.0,
          1.0
        );
        vec2 bristleClosest = bristleStart + bristleSegment * bristleT;
        nearestBristle = min(nearestBristle, length(v_documentPosition - bristleClosest));
      }
    }

    float brushMask = 1.0 - smoothstep(
      max(0.0, bristleRadius - 0.75),
      bristleRadius + 0.75,
      nearestBristle
    );
    float brushHeightMask = step(0.01, brushMask);
    gl_FragColor = brushHeightMask > 0.5 ? vec4(0.0) : dry;
  }
`;

const IMPORTED_PAINT_FRAGMENT = `
  precision highp float;
  uniform sampler2D u_wet;
  uniform sampler2D u_dry;
  uniform sampler2D u_tip;
  uniform sampler2D u_selectionMask;
  uniform vec2 u_resolution;
  uniform vec2 u_center;
  uniform vec2 u_halfSize;
  uniform vec3 u_colour;
  uniform float u_rotation;
  uniform float u_opacity;
  uniform float u_pressure;
  uniform float u_selectionEnabled;
  varying vec2 v_documentPosition;

  vec2 toUv(vec2 point) {
    return vec2(point.x / u_resolution.x, 1.0 - point.y / u_resolution.y);
  }

  vec3 mixPaint(vec3 oldColour, float oldHeight, vec3 newColour, float newHeight) {
    float total = oldHeight + newHeight;
    if (total <= 0.000001) return newColour;
    return (oldColour * oldHeight + newColour * newHeight) / total;
  }

  void main() {
    vec2 uv = toUv(v_documentPosition);
    vec4 wet = texture2D(u_wet, uv);
    vec4 dry = texture2D(u_dry, uv);

    if (u_selectionEnabled > 0.5 && texture2D(u_selectionMask, uv).r < 0.5) {
      gl_FragColor = wet;
      return;
    }

    vec2 delta = v_documentPosition - u_center;
    float cosine = cos(-u_rotation);
    float sine = sin(-u_rotation);
    vec2 local = vec2(
      delta.x * cosine - delta.y * sine,
      delta.x * sine + delta.y * cosine
    );
    vec2 safeHalfSize = max(u_halfSize, vec2(0.5));
    vec2 tipUv = local / (safeHalfSize * 2.0) + 0.5;

    if (tipUv.x < 0.0 || tipUv.x > 1.0 || tipUv.y < 0.0 || tipUv.y > 1.0) {
      gl_FragColor = wet;
      return;
    }

    float tipMask = texture2D(u_tip, tipUv).a;
    float mask = clamp(tipMask * u_opacity, 0.0, 1.0);
    if (mask <= 0.001) {
      gl_FragColor = wet;
      return;
    }

    float wetHeight = wet.a * ${MAX_HEIGHT.toFixed(1)};
    float dryHeight = dry.a * ${MAX_HEIGHT.toFixed(1)};
    float pressureRange = clamp((u_pressure - 0.01) / 0.99, 0.0, 1.0);
    float targetPeakHeight = mix(10.0, 2.0, pressureRange);
    float heightProfile = pow(mask, 0.72);
    float depositedHeight = targetPeakHeight * heightProfile * mask * 0.22;
    float maximumWetHeight = max(0.0, ${MAX_HEIGHT.toFixed(1)} - dryHeight);
    float nextHeight = min(maximumWetHeight, wetHeight + depositedHeight);
    if (nextHeight > 0.0001 && nextHeight < ${FLOOR_HEIGHT.toFixed(1)}) {
      nextHeight = ${FLOOR_HEIGHT.toFixed(1)};
    }
    vec3 colour = depositedHeight > 0.0001
      ? mixPaint(wet.rgb, wetHeight, u_colour, depositedHeight)
      : wet.rgb;

    gl_FragColor = vec4(clamp(colour, 0.0, 1.0), clamp(nextHeight / ${MAX_HEIGHT.toFixed(1)}, 0.0, 1.0));
  }
`;

const IMPORTED_DRY_CLEAR_FRAGMENT = `
  precision highp float;
  uniform sampler2D u_dry;
  uniform sampler2D u_tip;
  uniform sampler2D u_selectionMask;
  uniform vec2 u_resolution;
  uniform vec2 u_center;
  uniform vec2 u_halfSize;
  uniform float u_rotation;
  uniform float u_opacity;
  uniform float u_selectionEnabled;
  varying vec2 v_documentPosition;

  vec2 toUv(vec2 point) {
    return vec2(point.x / u_resolution.x, 1.0 - point.y / u_resolution.y);
  }

  void main() {
    vec2 uv = toUv(v_documentPosition);
    vec4 dry = texture2D(u_dry, uv);

    if (u_selectionEnabled > 0.5 && texture2D(u_selectionMask, uv).r < 0.5) {
      gl_FragColor = dry;
      return;
    }

    vec2 delta = v_documentPosition - u_center;
    float cosine = cos(-u_rotation);
    float sine = sin(-u_rotation);
    vec2 local = vec2(
      delta.x * cosine - delta.y * sine,
      delta.x * sine + delta.y * cosine
    );
    vec2 safeHalfSize = max(u_halfSize, vec2(0.5));
    vec2 tipUv = local / (safeHalfSize * 2.0) + 0.5;

    if (tipUv.x < 0.0 || tipUv.x > 1.0 || tipUv.y < 0.0 || tipUv.y > 1.0) {
      gl_FragColor = dry;
      return;
    }

    float mask = clamp(texture2D(u_tip, tipUv).a * u_opacity, 0.0, 1.0);
    gl_FragColor = mask > 0.001 ? vec4(0.0) : dry;
  }
`;

const KNIFE_WET_FLOOR_FRAGMENT = `
  precision highp float;
  uniform sampler2D u_wet;
  uniform sampler2D u_dry;
  uniform sampler2D u_sweepMask;
  varying vec2 v_uv;

  void main() {
    vec4 wet = texture2D(u_wet, v_uv);
    float swept = texture2D(u_sweepMask, v_uv).r;

    if (swept <= 0.5) {
      gl_FragColor = wet;
      return;
    }

    vec4 dry = texture2D(u_dry, v_uv);
    float wetHeight = wet.a * 4000.0;
    float dryHeight = dry.a * 4000.0;
    float totalHeight = wetHeight + dryHeight;

    if (totalHeight <= 0.0001) {
      gl_FragColor = vec4(0.0);
      return;
    }

    vec3 colour = (wet.rgb * wetHeight + dry.rgb * dryHeight) / max(totalHeight, 0.0001);
    gl_FragColor = vec4(clamp(colour, 0.0, 1.0), 0.00025000);
  }
`;

const BRUSH_FLOW_FRAGMENT = `
  precision highp float;
  uniform sampler2D u_wet;
  uniform sampler2D u_dry;
  uniform sampler2D u_selectionMask;
  uniform vec2 u_resolution;
  uniform vec2 u_start;
  uniform vec2 u_end;
  uniform vec2 u_previousDirection;
  uniform float u_radius;
  uniform float u_pressure;
  uniform float u_bristleCount;
  uniform float u_bristleThickness;
  uniform float u_liquid;
  uniform float u_cohesion;
  uniform float u_speed;
  uniform float u_seed;
  uniform float u_selectionEnabled;
  varying vec2 v_documentPosition;

  vec2 toUv(vec2 point) {
    return vec2(point.x / u_resolution.x, 1.0 - point.y / u_resolution.y);
  }

  float randomValue(vec2 point) {
    return fract(sin(dot(point, vec2(12.9898, 78.233)) + u_seed * 0.013) * 43758.5453);
  }

  vec2 quantiseDirection(vec2 value) {
    const float diagonal = 0.70710678118;
    vec2 direction = length(value) > 0.0001 ? normalize(value) : vec2(1.0, 0.0);
    vec2 bestDirection = vec2(1.0, 0.0);
    float bestDot = dot(direction, bestDirection);
    float candidateDot = dot(direction, vec2(-1.0, 0.0));
    if (candidateDot > bestDot) { bestDot = candidateDot; bestDirection = vec2(-1.0, 0.0); }
    candidateDot = dot(direction, vec2(0.0, 1.0));
    if (candidateDot > bestDot) { bestDot = candidateDot; bestDirection = vec2(0.0, 1.0); }
    candidateDot = dot(direction, vec2(0.0, -1.0));
    if (candidateDot > bestDot) { bestDot = candidateDot; bestDirection = vec2(0.0, -1.0); }
    candidateDot = dot(direction, vec2(diagonal, diagonal));
    if (candidateDot > bestDot) { bestDot = candidateDot; bestDirection = vec2(diagonal, diagonal); }
    candidateDot = dot(direction, vec2(-diagonal, diagonal));
    if (candidateDot > bestDot) { bestDot = candidateDot; bestDirection = vec2(-diagonal, diagonal); }
    candidateDot = dot(direction, vec2(diagonal, -diagonal));
    if (candidateDot > bestDot) { bestDot = candidateDot; bestDirection = vec2(diagonal, -diagonal); }
    candidateDot = dot(direction, vec2(-diagonal, -diagonal));
    if (candidateDot > bestDot) bestDirection = vec2(-diagonal, -diagonal);
    return bestDirection;
  }

  void main() {
    vec2 uv = toUv(v_documentPosition);
    vec4 wet = texture2D(u_wet, uv);
    vec4 dry = texture2D(u_dry, uv);

    if (u_selectionEnabled > 0.5 && texture2D(u_selectionMask, uv).r < 0.5) {
      gl_FragColor = vec4(0.5, 0.5, 0.0, 1.0);
      return;
    }

    float wetHeight = wet.a * ${MAX_HEIGHT.toFixed(1)};
    float dryHeight = dry.a * ${MAX_HEIGHT.toFixed(1)};
    float movableHeight = dryHeight > 0.0001
      ? wetHeight
      : max(0.0, wetHeight - ${FLOOR_HEIGHT.toFixed(1)});
    if (movableHeight <= 0.0001) {
      gl_FragColor = vec4(0.5, 0.5, 0.0, 1.0);
      return;
    }

    vec2 segment = u_end - u_start;
    float segmentLength = length(segment);
    vec2 currentDirection = segmentLength > 0.0001
      ? segment / segmentLength
      : (length(u_previousDirection) > 0.0001 ? normalize(u_previousDirection) : vec2(1.0, 0.0));
    vec2 previousDirection = length(u_previousDirection) > 0.0001
      ? normalize(u_previousDirection)
      : currentDirection;
    vec2 side = vec2(-currentDirection.y, currentDirection.x);
    float radius = max(0.5, u_radius);
    float safeBristleCount = clamp(floor(u_bristleCount + 0.5), 1.0, 250.0);
    float safeThickness = clamp(u_bristleThickness, 0.01, 1.0);
    float flexibility = 1.0 - safeThickness;
    float bristleSpacing = max(1.0, radius * sqrt(3.14159265359 / safeBristleCount));
    float bristleRadius = max(0.55, bristleSpacing * mix(0.12, 0.46, safeThickness));
    float nearestBristle = 1000000.0;
    float nearestLateral = 0.0;
    float nearestSeed = 0.5;

    for (int bristleIndex = 0; bristleIndex < 250; bristleIndex += 1) {
      float index = float(bristleIndex);
      if (index < safeBristleCount) {
        float radialPosition = sqrt((index + 0.5) / safeBristleCount);
        float angleJitter = (randomValue(vec2(index + 11.0, u_seed + 37.0)) - 0.5) * 0.42;
        float radialJitter = mix(0.88, 1.0, randomValue(vec2(index + 71.0, u_seed + 19.0)));
        float angle = index * 2.39996322973 + u_seed * 0.001 + angleJitter;
        vec2 bristleOffset = vec2(cos(angle), sin(angle)) * radius * 0.90 * radialPosition * radialJitter;
        vec2 directionChange = previousDirection - currentDirection;
        vec2 bend = directionChange * radius * mix(0.04, 0.28, flexibility) * radialPosition;
        float dragDistance = radius * mix(0.025, 0.18, flexibility) * radialPosition * mix(0.35, 1.0, u_speed);
        vec2 bristleStart = u_start + bristleOffset + bend * 0.35 - previousDirection * dragDistance;
        vec2 bristleEnd = u_end + bristleOffset + bend - currentDirection * dragDistance;
        vec2 bristleSegment = bristleEnd - bristleStart;
        float bristleSegmentLengthSquared = max(dot(bristleSegment, bristleSegment), 0.000001);
        float bristleT = clamp(
          dot(v_documentPosition - bristleStart, bristleSegment) / bristleSegmentLengthSquared,
          0.0,
          1.0
        );
        vec2 bristleClosest = bristleStart + bristleSegment * bristleT;
        float bristleDistance = length(v_documentPosition - bristleClosest);
        if (bristleDistance < nearestBristle) {
          nearestBristle = bristleDistance;
          nearestLateral = dot(bristleOffset, side) / radius;
          nearestSeed = randomValue(vec2(index + 151.0, u_seed + 83.0));
        }
      }
    }

    float brushMask = 1.0 - smoothstep(
      max(0.0, bristleRadius - 0.75),
      bristleRadius + 0.75,
      nearestBristle
    );
    if (brushMask <= 0.0001) {
      gl_FragColor = vec4(0.5, 0.5, 0.0, 1.0);
      return;
    }

    float liquid = clamp(u_liquid, 0.0, 1.0);
    float cohesion = clamp(u_cohesion, 0.0, 1.0);
    float pressure = clamp(u_pressure, 0.01, 1.0);
    float contactForce = brushMask
      * (0.14 + pressure * mix(0.72, 1.08, u_speed))
      * mix(0.88, 1.12, cohesion);
    float yieldStress = mix(0.42, 0.055, pow(liquid, 1.35));
    float activation = clamp(
      (contactForce - yieldStress) / max(0.08, 1.0 - yieldStress),
      0.0,
      1.0
    );
    if (activation <= 0.0001) {
      gl_FragColor = vec4(0.5, 0.5, 0.0, 1.0);
      return;
    }

    float lateralAmount = abs(nearestLateral);
    float lateralSign = nearestLateral < 0.0 ? -1.0 : 1.0;
    vec2 outward = side * lateralSign;
    float sideWeight = pow(pressure, 1.25)
      * smoothstep(0.08, 0.92, lateralAmount)
      * mix(0.58, 0.24, liquid);
    float bristleWander = (nearestSeed - 0.5) * mix(0.12, 0.035, safeThickness);
    vec2 movementDirection = currentDirection * (1.0 - sideWeight)
      + outward * sideWeight
      + side * bristleWander
      + (previousDirection - currentDirection) * flexibility * 0.18;
    vec2 uvDirection = quantiseDirection(vec2(movementDirection.x, -movementDirection.y));
    vec2 destinationUv = uv + uvDirection / u_resolution;
    if (destinationUv.x < 0.0 || destinationUv.x > 1.0
      || destinationUv.y < 0.0 || destinationUv.y > 1.0
      || (u_selectionEnabled > 0.5 && texture2D(u_selectionMask, destinationUv).r < 0.5)) {
      gl_FragColor = vec4(0.5, 0.5, 0.0, 1.0);
      return;
    }

    float bodyMobility = mix(0.075, 0.285, pow(liquid, 1.12));
    float speedGrip = mix(0.82, 1.12, u_speed);
    float amountLimit = movableHeight > 3.0 ? 1.0 : movableHeight / 3.0;
    float flow = bodyMobility
      * activation
      * brushMask
      * speedGrip
      * mix(0.86, 1.10, cohesion)
      * amountLimit;
    gl_FragColor = vec4(uvDirection * 0.5 + 0.5, clamp(flow, 0.0, 0.34), 1.0);
  }
`;

const PAINT_RELAX_FLOW_FRAGMENT = `
  precision highp float;
  uniform sampler2D u_wet;
  uniform sampler2D u_dry;
  uniform sampler2D u_selectionMask;
  uniform vec2 u_pixelStep;
  uniform vec4 u_bounds;
  uniform float u_liquid;
  uniform float u_cohesion;
  uniform float u_selectionEnabled;
  varying vec2 v_uv;

  bool insideBounds(vec2 point) {
    return point.x >= u_bounds.x
      && point.y >= u_bounds.y
      && point.x <= u_bounds.z
      && point.y <= u_bounds.w;
  }

  float totalHeight(vec2 point) {
    vec4 wet = texture2D(u_wet, point);
    vec4 dry = texture2D(u_dry, point);
    return (wet.a + dry.a) * ${MAX_HEIGHT.toFixed(1)};
  }

  float wetHeight(vec2 point) {
    return texture2D(u_wet, point).a * ${MAX_HEIGHT.toFixed(1)};
  }

  float occupied(vec2 point) {
    return step(${(FLOOR_HEIGHT + 0.01).toFixed(2)}, totalHeight(point));
  }

  void considerLow(
    vec2 direction,
    inout vec2 bestDirection,
    inout float bestHeight
  ) {
    vec2 point = v_uv + direction * u_pixelStep;
    if (!insideBounds(point)) return;
    if (u_selectionEnabled > 0.5 && texture2D(u_selectionMask, point).r < 0.5) return;
    float candidateHeight = totalHeight(point);
    if (candidateHeight < bestHeight) {
      bestHeight = candidateHeight;
      bestDirection = direction;
    }
  }

  void considerCohesion(
    vec2 direction,
    inout vec2 bestDirection,
    inout float bestHeight
  ) {
    vec2 point = v_uv + direction * u_pixelStep;
    if (!insideBounds(point)) return;
    if (u_selectionEnabled > 0.5 && texture2D(u_selectionMask, point).r < 0.5) return;
    float candidateHeight = totalHeight(point);
    if (candidateHeight > ${FLOOR_HEIGHT.toFixed(1)} && candidateHeight > bestHeight) {
      bestHeight = candidateHeight;
      bestDirection = direction;
    }
  }

  void main() {
    vec4 wet = texture2D(u_wet, v_uv);
    vec4 dry = texture2D(u_dry, v_uv);
    if (!insideBounds(v_uv)
      || (u_selectionEnabled > 0.5 && texture2D(u_selectionMask, v_uv).r < 0.5)) {
      gl_FragColor = vec4(0.5, 0.5, 0.0, 1.0);
      return;
    }

    float currentWet = wet.a * ${MAX_HEIGHT.toFixed(1)};
    float currentDry = dry.a * ${MAX_HEIGHT.toFixed(1)};
    float movable = currentDry > 0.0001
      ? currentWet
      : max(0.0, currentWet - ${FLOOR_HEIGHT.toFixed(1)});
    if (movable <= 0.0001) {
      gl_FragColor = vec4(0.5, 0.5, 0.0, 1.0);
      return;
    }

    const float diagonal = 0.70710678118;
    float currentHeight = currentWet + currentDry;
    vec2 lowDirection = vec2(0.0);
    float lowestHeight = currentHeight;
    considerLow(vec2(1.0, 0.0), lowDirection, lowestHeight);
    considerLow(vec2(-1.0, 0.0), lowDirection, lowestHeight);
    considerLow(vec2(0.0, 1.0), lowDirection, lowestHeight);
    considerLow(vec2(0.0, -1.0), lowDirection, lowestHeight);
    considerLow(vec2(diagonal, diagonal), lowDirection, lowestHeight);
    considerLow(vec2(-diagonal, diagonal), lowDirection, lowestHeight);
    considerLow(vec2(diagonal, -diagonal), lowDirection, lowestHeight);
    considerLow(vec2(-diagonal, -diagonal), lowDirection, lowestHeight);

    float support = 0.0;
    support += occupied(v_uv + vec2(u_pixelStep.x, 0.0));
    support += occupied(v_uv - vec2(u_pixelStep.x, 0.0));
    support += occupied(v_uv + vec2(0.0, u_pixelStep.y));
    support += occupied(v_uv - vec2(0.0, u_pixelStep.y));
    support += occupied(v_uv + u_pixelStep);
    support += occupied(v_uv - u_pixelStep);
    support += occupied(v_uv + vec2(u_pixelStep.x, -u_pixelStep.y));
    support += occupied(v_uv + vec2(-u_pixelStep.x, u_pixelStep.y));

    float liquid = clamp(u_liquid, 0.0, 1.0);
    float cohesion = clamp(u_cohesion, 0.0, 1.0);
    float heightDifference = max(0.0, currentHeight - lowestHeight);
    float levellingThreshold = mix(24.0, 0.02, pow(liquid, 1.15));
    float levellingActivation = clamp(
      (heightDifference - levellingThreshold) / max(1.0, currentHeight),
      0.0,
      1.0
    );
    float levellingFlow = pow(liquid, 1.25) * levellingActivation * mix(0.08, 0.46, liquid);

    vec2 flowDirection = lowDirection;
    float flow = levellingFlow;
    if (flow <= 0.0001 && support < 3.0 && cohesion > 0.0001) {
      vec2 cohesionDirection = vec2(0.0);
      float highestNeighbour = 0.0;
      considerCohesion(vec2(1.0, 0.0), cohesionDirection, highestNeighbour);
      considerCohesion(vec2(-1.0, 0.0), cohesionDirection, highestNeighbour);
      considerCohesion(vec2(0.0, 1.0), cohesionDirection, highestNeighbour);
      considerCohesion(vec2(0.0, -1.0), cohesionDirection, highestNeighbour);
      considerCohesion(vec2(diagonal, diagonal), cohesionDirection, highestNeighbour);
      considerCohesion(vec2(-diagonal, diagonal), cohesionDirection, highestNeighbour);
      considerCohesion(vec2(diagonal, -diagonal), cohesionDirection, highestNeighbour);
      considerCohesion(vec2(-diagonal, -diagonal), cohesionDirection, highestNeighbour);
      if (length(cohesionDirection) > 0.5) {
        flowDirection = cohesionDirection;
        float isolated = 1.0 - smoothstep(0.0, 3.0, support);
        float smallPaint = 1.0 - smoothstep(4.0, 24.0, movable);
        flow = cohesion * isolated * mix(0.012, 0.035, smallPaint);
      }
    }

    if (flow <= 0.0001 || length(flowDirection) < 0.5) {
      gl_FragColor = vec4(0.5, 0.5, 0.0, 1.0);
      return;
    }

    gl_FragColor = vec4(flowDirection * 0.5 + 0.5, clamp(flow, 0.0, 0.16), 1.0);
  }
`;

const MASS_GATHER_FRAGMENT = `
  precision highp float;
  uniform sampler2D u_wet;
  uniform sampler2D u_dry;
  uniform sampler2D u_flow;
  uniform sampler2D u_selectionMask;
  uniform vec2 u_pixelStep;
  uniform vec4 u_bounds;
  uniform float u_selectionEnabled;
  varying vec2 v_uv;

  bool insideBounds(vec2 point) {
    return point.x >= u_bounds.x
      && point.y >= u_bounds.y
      && point.x <= u_bounds.z
      && point.y <= u_bounds.w;
  }

  float movableWetHeight(vec2 point) {
    vec4 wet = texture2D(u_wet, point);
    vec4 dry = texture2D(u_dry, point);
    float wetHeight = wet.a * ${MAX_HEIGHT.toFixed(1)};
    float dryHeight = dry.a * ${MAX_HEIGHT.toFixed(1)};
    return dryHeight > 0.0001
      ? wetHeight
      : max(0.0, wetHeight - ${FLOOR_HEIGHT.toFixed(1)});
  }

  void addIncoming(
    vec2 sourceOffset,
    inout float incomingHeight,
    inout vec3 incomingPigment
  ) {
    vec2 sourcePoint = v_uv + sourceOffset * u_pixelStep;
    if (!insideBounds(sourcePoint)) return;
    if (u_selectionEnabled > 0.5 && texture2D(u_selectionMask, sourcePoint).r < 0.5) return;
    vec4 sourceFlow = texture2D(u_flow, sourcePoint);
    if (sourceFlow.b <= 0.00001) return;
    vec2 direction = sourceFlow.rg * 2.0 - 1.0;
    if (length(direction) < 0.5) return;
    vec2 expected = normalize(-sourceOffset);
    if (dot(normalize(direction), expected) < 0.99) return;
    vec4 sourceWet = texture2D(u_wet, sourcePoint);
    float amount = movableWetHeight(sourcePoint) * sourceFlow.b;
    if (amount <= 0.00001) return;
    incomingHeight += amount;
    incomingPigment += sourceWet.rgb * amount;
  }

  void main() {
    vec4 wet = texture2D(u_wet, v_uv);
    vec4 dry = texture2D(u_dry, v_uv);
    if (!insideBounds(v_uv)
      || (u_selectionEnabled > 0.5 && texture2D(u_selectionMask, v_uv).r < 0.5)) {
      gl_FragColor = wet;
      return;
    }

    float wetHeight = wet.a * ${MAX_HEIGHT.toFixed(1)};
    float dryHeight = dry.a * ${MAX_HEIGHT.toFixed(1)};
    float movable = movableWetHeight(v_uv);
    vec4 ownFlow = texture2D(u_flow, v_uv);
    float outflow = movable * ownFlow.b;
    float remainingHeight = max(0.0, wetHeight - outflow);
    float incomingHeight = 0.0;
    vec3 incomingPigment = vec3(0.0);

    addIncoming(vec2(-1.0, 0.0), incomingHeight, incomingPigment);
    addIncoming(vec2(1.0, 0.0), incomingHeight, incomingPigment);
    addIncoming(vec2(0.0, -1.0), incomingHeight, incomingPigment);
    addIncoming(vec2(0.0, 1.0), incomingHeight, incomingPigment);
    addIncoming(vec2(-1.0, -1.0), incomingHeight, incomingPigment);
    addIncoming(vec2(1.0, -1.0), incomingHeight, incomingPigment);
    addIncoming(vec2(-1.0, 1.0), incomingHeight, incomingPigment);
    addIncoming(vec2(1.0, 1.0), incomingHeight, incomingPigment);

    float nextHeight = remainingHeight + incomingHeight;
    float maximumWetHeight = max(0.0, ${MAX_HEIGHT.toFixed(1)} - dryHeight);
    nextHeight = min(maximumWetHeight, nextHeight);
    if (nextHeight <= 0.00001) {
      gl_FragColor = vec4(0.0);
      return;
    }

    vec3 pigment = wet.rgb * remainingHeight + incomingPigment;
    vec3 nextColour = pigment / max(remainingHeight + incomingHeight, 0.00001);
    gl_FragColor = vec4(
      clamp(nextColour, 0.0, 1.0),
      clamp(nextHeight / ${MAX_HEIGHT.toFixed(1)}, 0.0, 1.0)
    );
  }
`;

const PAINT_UPDATE_FRAGMENT = `
  precision highp float;
  uniform sampler2D u_wet;
  uniform sampler2D u_dry;
  uniform sampler2D u_baseWet;
  uniform sampler2D u_tubeStroke;
  uniform sampler2D u_brushLoad;
  uniform sampler2D u_knifeLoad;
  uniform sampler2D u_knifeState;
  uniform sampler2D u_knifePickupMass;
  uniform sampler2D u_selectionMask;
  uniform sampler2D u_brushMask;
  uniform vec2 u_resolution;
  uniform vec2 u_start;
  uniform vec2 u_end;
  uniform vec3 u_colour;
  uniform float u_radius;
  uniform float u_operation;
  uniform float u_pressure;
  uniform float u_bristleCount;
  uniform float u_bristleThickness;
  uniform float u_brushSnakeBody;
  uniform float u_brushDirectBristle;
  uniform float u_useBrushMask;
  uniform float u_brushMaskSidePass;
  uniform float u_brushRadius;
  uniform float u_brushSnakePart;
  uniform float u_brushSnakeParts;
  uniform float u_infinitePaint;
  uniform float u_sideDeposits;
  uniform float u_seed;
  uniform float u_selectionEnabled;
  uniform float u_floorHeight;
  uniform float u_liquid;
  uniform float u_cohesion;
  uniform float u_speed;
  uniform vec2 u_previousDirection;
  uniform vec2 u_knifeStartDirection;
  uniform vec2 u_knifeDirection;
  uniform float u_knifePaintUnits;
  uniform float u_knifeCapacityUnits;
  uniform float u_knifeOverflowUnits;
  varying vec2 v_documentPosition;

  vec2 toUv(vec2 point) {
    return vec2(point.x / u_resolution.x, 1.0 - point.y / u_resolution.y);
  }

  float randomValue(vec2 point) {
    return fract(sin(dot(point, vec2(12.9898, 78.233)) + u_seed * 0.013) * 43758.5453);
  }

  vec3 mixPaint(vec3 oldColour, float oldHeight, vec3 newColour, float newHeight) {
    float total = oldHeight + newHeight;
    if (total <= 0.000001) return newColour;
    return (oldColour * oldHeight + newColour * newHeight) / total;
  }

  float edgeSign(vec2 point, vec2 first, vec2 second) {
    return (point.x - second.x) * (first.y - second.y)
      - (first.x - second.x) * (point.y - second.y);
  }

  float insideTriangle(vec2 point, vec2 first, vec2 second, vec2 third) {
    float firstSign = edgeSign(point, first, second);
    float secondSign = edgeSign(point, second, third);
    float thirdSign = edgeSign(point, third, first);
    float hasNegative = step(min(min(firstSign, secondSign), thirdSign), -0.0001);
    float hasPositive = step(0.0001, max(max(firstSign, secondSign), thirdSign));
    return 1.0 - hasNegative * hasPositive;
  }

  float sweptKnifeBlade(vec2 point, float radius) {
    vec2 rawSegment = u_end - u_start;
    vec2 startDirection = length(u_knifeStartDirection) > 0.0001
      ? normalize(u_knifeStartDirection)
      : (length(rawSegment) > 0.0001 ? normalize(rawSegment) : vec2(1.0, 0.0));
    vec2 endDirection = length(u_knifeDirection) > 0.0001
      ? normalize(u_knifeDirection)
      : startDirection;
    vec2 startSide = vec2(-startDirection.y, startDirection.x) * radius;
    vec2 endSide = vec2(-endDirection.y, endDirection.x) * radius;
    vec2 startLeft = u_start - startSide;
    vec2 startRight = u_start + startSide;
    vec2 endLeft = u_end - endSide;
    vec2 endRight = u_end + endSide;
    return max(
      insideTriangle(point, startLeft, startRight, endRight),
      insideTriangle(point, startLeft, endRight, endLeft)
    );
  }

  void main() {
    vec2 uv = toUv(v_documentPosition);
    vec4 wet = texture2D(u_wet, uv);
    vec4 dry = texture2D(u_dry, uv);

    if (u_selectionEnabled > 0.5 && texture2D(u_selectionMask, uv).r < 0.5) {
      gl_FragColor = wet;
      return;
    }

    vec2 segment = u_end - u_start;
    float segmentLength = length(segment);
    vec2 rawDirection = segmentLength > 0.0001 ? segment / segmentLength : vec2(1.0, 0.0);
    vec2 direction = u_operation > 1.5 && length(u_knifeDirection) > 0.0001
      ? normalize(u_knifeDirection)
      : rawDirection;
    vec2 side = vec2(-direction.y, direction.x);
    float segmentLengthSquared = max(dot(segment, segment), 0.000001);
    float t = clamp(dot(v_documentPosition - u_start, segment) / segmentLengthSquared, 0.0, 1.0);
    vec2 closest = u_start + segment * t;
    vec2 relative = v_documentPosition - closest;
    float distanceFromCentre = length(relative);
    float radius = max(0.5, u_radius);
    float q = distanceFromCentre / radius;
    float capsule = 1.0 - smoothstep(0.94, 1.02, q);
    float oldHeight = wet.a * ${MAX_HEIGHT.toFixed(1)};
    float dryHeight = dry.a * ${MAX_HEIGHT.toFixed(1)};
    vec3 oldColour = wet.rgb;
    float nextHeight = oldHeight;
    vec3 nextColour = oldColour;
    float knifeSweep = 0.0;

    if (u_operation < 0.5) {
      float tubeStrokeHeight = texture2D(u_tubeStroke, uv).a * ${MAX_HEIGHT.toFixed(1)};
      nextHeight = mix(oldHeight, tubeStrokeHeight, capsule);
      nextColour = capsule > 0.0001 ? u_colour : oldColour;
    } else if (u_operation < 1.5) {
      float safeBristleCount = clamp(floor(u_bristleCount + 0.5), 1.0, 250.0);
      float safeThickness = clamp(u_bristleThickness, 0.01, 1.0);
      float flexibility = 1.0 - safeThickness;
      float bristleRadius = radius;
      float nearestBristle = distanceFromCentre;
      float nearestBristleT = t;
      float nearestBristleSeed = 0.5;
      float brushMask = capsule;

      if (u_useBrushMask > 0.5) {
        float centreMask = texture2D(u_brushMask, uv).r;
        if (u_brushMaskSidePass > 0.5) {
          brushMask = centreMask;
        } else {
          vec2 maskPixel = vec2(1.0 / u_resolution.x, 1.0 / u_resolution.y);
          float nearMask = max(
            max(texture2D(u_brushMask, uv + vec2(maskPixel.x, 0.0)).r,
                texture2D(u_brushMask, uv - vec2(maskPixel.x, 0.0)).r),
            max(texture2D(u_brushMask, uv + vec2(0.0, maskPixel.y)).r,
                texture2D(u_brushMask, uv - vec2(0.0, maskPixel.y)).r)
          );
          float farMask = max(
            max(texture2D(u_brushMask, uv + vec2(maskPixel.x * 2.0, 0.0)).r,
                texture2D(u_brushMask, uv - vec2(maskPixel.x * 2.0, 0.0)).r),
            max(texture2D(u_brushMask, uv + vec2(0.0, maskPixel.y * 2.0)).r,
                texture2D(u_brushMask, uv - vec2(0.0, maskPixel.y * 2.0)).r)
          );
          float liquidSpreadMask = max(centreMask, max(nearMask * 0.62, farMask * 0.28));
          brushMask = mix(centreMask, liquidSpreadMask, clamp(u_liquid, 0.0, 1.0));
        }
        nearestBristle = 0.0;
        nearestBristleT = t;
        nearestBristleSeed = 0.5;
      } else if (u_brushDirectBristle > 0.5) {
        nearestBristle = distanceFromCentre;
        nearestBristleT = t;
        nearestBristleSeed = 0.5;
        brushMask = 1.0 - smoothstep(
          max(0.0, bristleRadius - 0.55),
          bristleRadius + 0.55,
          nearestBristle
        );
      } else if (u_brushSnakeBody > 0.5) {
        nearestBristle = 1000000.0;
        nearestBristleT = 0.0;
        nearestBristleSeed = 0.5;
        float spreadRadius = max(radius, u_brushRadius);
        float bodyTaper = 1.0 - clamp(u_brushSnakePart / max(1.0, u_brushSnakeParts), 0.0, 1.0) * 0.22;
        vec2 bodySide = vec2(-direction.y, direction.x);

        for (int bristleIndex = 0; bristleIndex < 250; bristleIndex += 1) {
          float index = float(bristleIndex);
          if (index < safeBristleCount) {
            float seed = randomValue(vec2(index + 11.0, u_seed + 37.0));
            float radialPosition = sqrt((index + 0.5) / safeBristleCount);
            float angle = index * 2.39996322973 + seed * 0.9;
            float radialJitter = mix(0.88, 1.0, randomValue(vec2(index + 71.0, u_seed + 19.0)));
            vec2 localOffset = vec2(cos(angle), sin(angle)) * spreadRadius * 0.88 * radialPosition * radialJitter * bodyTaper;
            vec2 worldOffset = direction * localOffset.x + bodySide * localOffset.y;
            vec2 bristleStart = u_start + worldOffset;
            vec2 bristleEnd = u_end + worldOffset;
            vec2 bristleSegment = bristleEnd - bristleStart;
            float bristleSegmentLengthSquared = max(dot(bristleSegment, bristleSegment), 0.000001);
            float bristleT = clamp(dot(v_documentPosition - bristleStart, bristleSegment) / bristleSegmentLengthSquared, 0.0, 1.0);
            vec2 bristleClosest = bristleStart + bristleSegment * bristleT;
            float bristleDistance = length(v_documentPosition - bristleClosest);
            if (bristleDistance < nearestBristle) {
              nearestBristle = bristleDistance;
              nearestBristleT = bristleT;
              nearestBristleSeed = seed;
            }
          }
        }

        brushMask = 1.0 - smoothstep(
          max(0.0, bristleRadius - 0.55),
          bristleRadius + 0.55,
          nearestBristle
        );
      } else {
        float bristleSpacing = max(1.0, radius * sqrt(3.14159265359 / safeBristleCount));
        bristleRadius = max(0.55, bristleSpacing * mix(0.12, 0.46, safeThickness));
        nearestBristle = 1000000.0;
        nearestBristleT = 0.0;
        nearestBristleSeed = 0.0;
        vec2 previousDirection = length(u_previousDirection) > 0.0001
          ? normalize(u_previousDirection)
          : direction;

        for (int bristleIndex = 0; bristleIndex < 250; bristleIndex += 1) {
          float index = float(bristleIndex);
          if (index < safeBristleCount) {
            float radialPosition = sqrt((index + 0.5) / safeBristleCount);
            float angleJitter = (randomValue(vec2(index + 11.0, u_seed + 37.0)) - 0.5) * 0.42;
            float radialJitter = mix(0.88, 1.0, randomValue(vec2(index + 71.0, u_seed + 19.0)));
            float angle = index * 2.39996322973 + u_seed * 0.001 + angleJitter;
            vec2 bristleOffset = vec2(cos(angle), sin(angle)) * radius * 0.90 * radialPosition * radialJitter;
            vec2 directionChange = previousDirection - direction;
            vec2 bend = directionChange * radius * mix(0.04, 0.28, flexibility) * radialPosition;
            float dragDistance = radius * mix(0.025, 0.18, flexibility) * radialPosition * mix(0.35, 1.0, u_speed);
            vec2 bristleStart = u_start + bristleOffset + bend * 0.35 - previousDirection * dragDistance;
            vec2 bristleEnd = u_end + bristleOffset + bend - direction * dragDistance;
            vec2 bristleSegment = bristleEnd - bristleStart;
            float bristleSegmentLengthSquared = max(dot(bristleSegment, bristleSegment), 0.000001);
            float bristleT = clamp(
              dot(v_documentPosition - bristleStart, bristleSegment) / bristleSegmentLengthSquared,
              0.0,
              1.0
            );
            vec2 bristleClosest = bristleStart + bristleSegment * bristleT;
            float bristleDistance = length(v_documentPosition - bristleClosest);
            if (bristleDistance < nearestBristle) {
              nearestBristle = bristleDistance;
              nearestBristleT = bristleT;
              nearestBristleSeed = randomValue(vec2(index + 151.0, u_seed + 83.0));
            }
          }
        }

        brushMask = 1.0 - smoothstep(
          max(0.0, bristleRadius - 0.75),
          bristleRadius + 0.75,
          nearestBristle
        );
      }
      vec4 load = texture2D(u_brushLoad, vec2(0.5));
      float loadStrength = u_infinitePaint > 0.5 ? 1.0 : load.a;
      vec3 loadedColour = u_infinitePaint > 0.5 ? u_colour : load.rgb;
      float pressureRange = clamp((u_pressure - 0.01) / 0.99, 0.0, 1.0);
      float targetHeight = mix(50.0, 2.0, pressureRange);
      float maximumWetHeight = max(0.0, ${MAX_HEIGHT.toFixed(1)} - dryHeight);

      if (u_brushMaskSidePass > 0.5) {
        float sideMask = clamp(brushMask, 0.0, 1.0) * loadStrength;
        float sideTargetHeight = max(targetHeight * 2.0, oldHeight * 2.0);
        nextHeight = min(
          maximumWetHeight,
          mix(oldHeight, sideTargetHeight, sideMask)
        );
        nextColour = sideMask > 0.0001 ? loadedColour : oldColour;
        if (nextHeight > 0.0001 && nextHeight < ${FLOOR_HEIGHT.toFixed(1)}) {
          nextHeight = ${FLOOR_HEIGHT.toFixed(1)};
        }
      } else {

      // The brush owns the height beneath its bristles. It replaces the old
      // stroke instead of adding another layer, including when it crosses
      // paint laid earlier in this same stroke.
      float overwriteMask = clamp(brushMask, 0.0, 1.0);

      // A loaded oil brush does not leave a flat slab at light pressure. The
      // paint body stays broad, but each bristle leaves a shallow longitudinal
      // ridge/groove across that body. Keep those height variations strongest
      // at minimum pressure and progressively flatten them as pressure rises.
      float normalisedBristleDistance = clamp(
        nearestBristle / max(bristleRadius, 0.0001),
        0.0,
        1.0
      );
      float bristleCrown = sqrt(max(
        0.0,
        1.0 - normalisedBristleDistance * normalisedBristleDistance
      ));
      vec2 localDelta = v_documentPosition - u_end;
      float forwardPixels = dot(localDelta, direction);
      float lateralPixels = dot(localDelta, side);
      float fineGroove = 0.5 + 0.5 * cos(
        lateralPixels * mix(1.15, 2.10, safeThickness)
          + nearestBristleSeed * 6.2831853
      );
      float lightPressureTexture = mix(
        0.68,
        1.08,
        clamp(bristleCrown * 0.78 + fineGroove * 0.22, 0.0, 1.0)
      );
      float highPressureTexture = mix(0.88, 1.02, bristleCrown);
      float surfaceProfile = mix(lightPressureTexture, highPressureTexture, pressureRange);
      float edgeBody = smoothstep(0.0, 0.34, overwriteMask);
      float texturedTargetHeight = targetHeight
        * mix(0.72, surfaceProfile, edgeBody)
        * loadStrength;
      float insideHeight = mix(oldHeight, texturedTargetHeight, overwriteMask);
      vec3 insideColour = mixPaint(
        oldColour,
        oldHeight * (1.0 - overwriteMask),
        loadedColour,
        texturedTargetHeight * overwriteMask
      );

      // Sample paint immediately behind the moving brush. Any height above
      // the new target is treated as paint pushed out by the bristles, then
      // deposited as a compact ridge in front and smaller ridges at the sides.
      float hasMotion = step(0.01, segmentLength);
      float sourceDistance = max(radius * 0.38, min(radius * 0.92, segmentLength + radius * 0.28));
      vec2 sourceUv = toUv(v_documentPosition - direction * sourceDistance);
      vec4 sourceWet = texture2D(u_wet, sourceUv);
      vec4 sourceDry = texture2D(u_dry, sourceUv);
      float sourceWetHeight = sourceWet.a * ${MAX_HEIGHT.toFixed(1)};
      float sourceDryHeight = sourceDry.a * ${MAX_HEIGHT.toFixed(1)};
      float sourceTotalHeight = sourceWetHeight + sourceDryHeight;
      float displacedHeight = max(0.0, sourceTotalHeight - targetHeight);
      vec3 displacedColour = sourceTotalHeight > 0.0001
        ? (sourceWet.rgb * sourceWetHeight + sourceDry.rgb * sourceDryHeight) / sourceTotalHeight
        : loadedColour;

      float forwardNormal = forwardPixels / max(1.0, radius);
      float lateralNormal = lateralPixels / max(1.0, radius);

      // Build the displaced paint like the palette knife: long side ridges that
      // run along both edges of the stroke, plus a compact front roll. The side
      // ridges need to follow the full swept segment, not just the current end
      // point, otherwise they disappear into the brush body.
      float frontX = (forwardPixels - radius * 0.54) / max(1.0, radius * 0.62);
      float frontY = lateralPixels / max(1.0, radius * 0.92);
      float frontBody = pow(max(0.0, 1.0 - (frontX * frontX + frontY * frontY)), 0.34);
      float frontCutoff = smoothstep(-0.34, 0.02, frontX);
      float frontShoulders = 1.0 - smoothstep(0.72, 1.08, abs(lateralNormal));
      float frontMask = frontBody * frontCutoff * mix(0.74, 1.0, frontShoulders) * hasMotion;

      vec2 strokeSegment = u_end - u_start;
      float strokeLengthSquared = max(dot(strokeSegment, strokeSegment), 0.000001);
      float strokeT = clamp(dot(v_documentPosition - u_start, strokeSegment) / strokeLengthSquared, 0.0, 1.0);
      vec2 strokeClosest = u_start + strokeSegment * strokeT;
      vec2 sweepDelta = v_documentPosition - strokeClosest;
      float sweepLateralPixels = dot(sweepDelta, side);
      float sweepLateral = sweepLateralPixels / max(1.0, radius);
      float absoluteSweepLateral = abs(sweepLateral);
      float endFade = smoothstep(0.0, 0.06, strokeT) * (1.0 - smoothstep(0.94, 1.0, strokeT));
      float midStrokeBoost = mix(0.92, 1.22, 1.0 - abs(strokeT * 2.0 - 1.0));

      // Side deposits must live clearly outside the brush footprint. These bands
      // begin just beyond the brush edge and track along the swept segment as
      // distinct left/right rails.
      float railInner = smoothstep(0.90, 1.01, absoluteSweepLateral);
      float railOuter = 1.0 - smoothstep(1.34, 1.52, absoluteSweepLateral);
      float outerSideBand = railInner * railOuter;
      float railCore = smoothstep(0.98, 1.08, absoluteSweepLateral)
        * (1.0 - smoothstep(1.22, 1.38, absoluteSweepLateral));
      float leftHalf = step(sweepLateral, -0.001);
      float rightHalf = step(0.001, sweepLateral);
      float sideSpine = (0.92 + railCore * 0.48) * midStrokeBoost;
      float sideDepositsEnabled = step(0.5, u_sideDeposits);
      float leftMask = clamp(outerSideBand * leftHalf * endFade * sideSpine, 0.0, 1.0)
        * hasMotion * sideDepositsEnabled;
      float rightMask = clamp(outerSideBand * rightHalf * endFade * sideSpine, 0.0, 1.0)
        * hasMotion * sideDepositsEnabled;

      float outsideBrush = 1.0 - overwriteMask;

      // Pressure controls how much paint is squeezed away from the footprint.
      // Light pressure keeps most of the 50-unit body under the bristles;
      // full pressure leaves a 2-unit film and forces much more paint into the
      // front and side ridges. The side ridges can reach roughly double the
      // brush-body height, but only when there is actual paint available to
      // move there, mirroring the palette knife behaviour.
      float pressurePush = mix(0.30, 1.0, pressureRange);
      float loadedPushHeight = mix(10.0, 40.0, pressureRange) * loadStrength;
      float displacedPushHeight = displacedHeight * mix(0.42, 0.98, pressureRange);
      float availablePushHeight = loadedPushHeight + displacedPushHeight;
      float availablePaintMask = step(0.0001, availablePushHeight);
      float cohesionPush = pressurePush * mix(0.80, 1.08, clamp(u_cohesion, 0.0, 1.0));

      float frontCoverage = clamp(frontMask * outsideBrush * availablePaintMask, 0.0, 1.0);
      float leftCoverage = clamp(leftMask * outsideBrush * availablePaintMask * 2.8, 0.0, 1.0);
      float rightCoverage = clamp(rightMask * outsideBrush * availablePaintMask * 2.8, 0.0, 1.0);

      float frontPushedHeight = availablePushHeight
        * frontMask
        * mix(0.54, 0.74, pressureRange)
        * cohesionPush;
      float leftPushedHeight = availablePushHeight
        * leftMask
        * mix(0.62, 0.96, pressureRange)
        * cohesionPush;
      float rightPushedHeight = availablePushHeight
        * rightMask
        * mix(0.62, 0.96, pressureRange)
        * cohesionPush;

      float sideHeightCap = targetHeight * 2.0;
      float frontTargetHeight = max(
        targetHeight,
        min(50.0, targetHeight + frontPushedHeight)
      );
      float sideBaseTarget = mix(targetHeight * 1.72, sideHeightCap, pressurePush);
      float leftTargetHeight = leftCoverage > 0.0001
        ? min(sideHeightCap, max(sideBaseTarget, targetHeight + leftPushedHeight))
        : oldHeight;
      float rightTargetHeight = rightCoverage > 0.0001
        ? min(sideHeightCap, max(sideBaseTarget, targetHeight + rightPushedHeight))
        : oldHeight;

      // Do not add ridge height repeatedly. Ridge pixels are assigned a fresh
      // target height based on the available paint, so repeated passes replace
      // the local ridge instead of building a tower.
      float totalRidgeCoverage = clamp(frontCoverage + leftCoverage + rightCoverage, 0.0, 1.0);
      float ridgeWeight = max(frontCoverage + leftCoverage + rightCoverage, 0.0001);
      float weightedRidgeTarget = (
        frontTargetHeight * frontCoverage
        + leftTargetHeight * leftCoverage
        + rightTargetHeight * rightCoverage
      ) / ridgeWeight;
      float outsideHeight = mix(oldHeight, weightedRidgeTarget, totalRidgeCoverage);
      vec3 pushedColour = displacedHeight > 0.0001
        ? mix(loadedColour, displacedColour, clamp(displacedPushHeight / max(availablePushHeight, 0.0001), 0.0, 1.0))
        : loadedColour;
      vec3 outsideColour = totalRidgeCoverage > 0.0001
        ? mixPaint(oldColour, oldHeight * (1.0 - totalRidgeCoverage), pushedColour, weightedRidgeTarget * totalRidgeCoverage)
        : oldColour;

      float innerRailTrough = sideDepositsEnabled
        * hasMotion
        * smoothstep(0.76, 0.90, absoluteSweepLateral)
        * (1.0 - smoothstep(0.94, 1.02, absoluteSweepLateral))
        * endFade;
      float separatedInsideHeight = max(0.0, insideHeight * (1.0 - innerRailTrough * 0.16));
      nextHeight = min(maximumWetHeight, mix(outsideHeight, separatedInsideHeight, overwriteMask));
      nextColour = mix(outsideColour, insideColour, overwriteMask);
      if (nextHeight > 0.0001 && nextHeight < ${FLOOR_HEIGHT.toFixed(1)}) {
        nextHeight = ${FLOOR_HEIGHT.toFixed(1)};
      }
      }
    } else {
      float inSweep = sweptKnifeBlade(v_documentPosition, radius);
      knifeSweep = inSweep;
      float knifePressure = clamp(u_pressure, 0.01, 1.0);
      float floorHeight = u_floorHeight;
      float sourceTotalHeight = oldHeight + dryHeight;
      float leaveHeight = sourceTotalHeight > 0.0001 ? floorHeight : 0.0;
      float scrapedHeight = max(0.0, sourceTotalHeight - leaveHeight) * inSweep;
      nextHeight = mix(oldHeight, leaveHeight, inSweep);

      float hasMotion = step(0.01, segmentLength);
      float pushEnabled = hasMotion;
      float currentTotalHeight = oldHeight + dryHeight;

      vec2 bladeDelta = v_documentPosition - u_end;
      float bladeForwardPixels = dot(bladeDelta, direction);
      float bladeLateralPixels = dot(bladeDelta, side);
      float bladeForward = bladeForwardPixels / radius;
      float bladeLateral = bladeLateralPixels / radius;
      float absoluteBladeLateral = abs(bladeLateral);
      float motionFactor = clamp(segmentLength / max(radius * 0.65, 1.0), 0.0, 1.0);
      float contactInset = clamp(segmentLength * 0.5, 0.75, radius * 0.24);

      float frontAcross = 1.0 - smoothstep(0.86, 1.02, absoluteBladeLateral);
      vec2 frontContactPoint = v_documentPosition
        - direction * (max(0.0, bladeForwardPixels) + contactInset);
      vec2 frontSourceUv = toUv(frontContactPoint);
      vec4 frontSourceWet = texture2D(u_wet, frontSourceUv);
      vec4 frontSourceDry = texture2D(u_dry, frontSourceUv);
      float frontSourceWetHeight = frontSourceWet.a * ${MAX_HEIGHT.toFixed(1)};
      float frontSourceDryHeight = frontSourceDry.a * ${MAX_HEIGHT.toFixed(1)};
      float frontSourceHeight = frontSourceWetHeight + frontSourceDryHeight;
      float frontContact = step(floorHeight + 0.001, frontSourceHeight);

      float lateralSign = bladeLateralPixels < 0.0 ? -1.0 : 1.0;
      vec2 sideContactPoint = u_end
        + side * lateralSign * radius * 0.90
        - direction * contactInset;
      vec2 sideSourceUv = toUv(sideContactPoint);
      vec4 sideSourceWet = texture2D(u_wet, sideSourceUv);
      vec4 sideSourceDry = texture2D(u_dry, sideSourceUv);
      float sideSourceWetHeight = sideSourceWet.a * ${MAX_HEIGHT.toFixed(1)};
      float sideSourceDryHeight = sideSourceDry.a * ${MAX_HEIGHT.toFixed(1)};
      float sideSourceHeight = sideSourceWetHeight + sideSourceDryHeight;
      float sideContact = step(floorHeight + 0.001, sideSourceHeight);

      vec3 sideSourceColour = sideSourceHeight > 0.0001
        ? (sideSourceWet.rgb * sideSourceWetHeight + sideSourceDry.rgb * sideSourceDryHeight) / sideSourceHeight
        : oldColour;
      vec3 frontSourceColour = frontSourceHeight > 0.0001
        ? (frontSourceWet.rgb * frontSourceWetHeight + frontSourceDry.rgb * frontSourceDryHeight) / frontSourceHeight
        : oldColour;

      vec4 oldKnifeState = texture2D(u_knifeState, vec2(0.5));
      vec4 pickupMass = texture2D(u_knifePickupMass, vec2(0.5));
      float previousUnits = max(0.0, oldKnifeState.a);
      float rawPickupUnits = max(0.0, pickupMass.a);
      float pickupUnits = rawPickupUnits * 1.5;
      vec3 pickupColour = rawPickupUnits > 0.000001 ? pickupMass.rgb / rawPickupUnits : vec3(0.0);
      float capacityUnits = max(1.0, u_knifeCapacityUnits);
      float colourPickupLimit = previousUnits > 0.000001
        ? max(0.25, max(previousUnits * 0.018, capacityUnits * 0.0015))
        : pickupUnits;
      float colourPickupUnits = min(pickupUnits, colourPickupLimit);
      float colourWeight = previousUnits + colourPickupUnits;
      vec3 projectedColour = colourWeight > 0.000001
        ? (clamp(oldKnifeState.rgb, 0.0, 1.0) * previousUnits + clamp(pickupColour, 0.0, 1.0) * colourPickupUnits) / colourWeight
        : vec3(0.0);
      float projectedUnits = previousUnits + pickupUnits;
      float overflowAvailable = max(0.0, projectedUnits - capacityUnits);
      float overflowReleased = min(overflowAvailable, max(0.0, radius * segmentLength * knifePressure * 2.4));
      float carriedUnits = max(0.0, projectedUnits - overflowReleased);
      vec4 knifeLoad = vec4(projectedColour, carriedUnits > 0.000001 ? 1.0 : 0.0);
      float loadRatio = clamp(carriedUnits / capacityUnits, 0.0, 1.0);
      float overflowRatio = clamp(overflowReleased / capacityUnits, 0.0, 2.0);

      float movedRatio = clamp(carriedUnits / max(1.0, radius * radius + carriedUnits), 0.0, 1.0);
      float frontShare = mix(0.90, 0.96, movedRatio);
      float sideShare = max(0.0, 1.0 - frontShare);
      float sideBias = 0.04 * sin(u_seed * 1.3);
      float leftShare = clamp(0.5 + sideBias, 0.46, 0.54);
      float rightShare = 1.0 - leftShare;

      float broadNoise = 0.82
        + 0.13 * sin(v_documentPosition.x * 0.071 + v_documentPosition.y * 0.049 + u_seed * 0.73)
        + 0.09 * sin(v_documentPosition.x * 0.037 - v_documentPosition.y * 0.061 + u_seed * 1.41);
      float fineNoise = 0.88
        + 0.08 * sin(v_documentPosition.x * 0.19 + v_documentPosition.y * 0.13 + u_seed * 2.17)
        + 0.05 * sin(v_documentPosition.x * 0.11 - v_documentPosition.y * 0.23 + u_seed * 0.91);

      float frontCentre = radius * 0.60;
      float frontX = (bladeForwardPixels - frontCentre) / max(1.0, radius * 0.50);
      float frontY = bladeLateralPixels / max(1.0, radius * 0.88);
      float frontDistance = frontX * frontX + frontY * frontY;
      float frontBody = pow(max(0.0, 1.0 - frontDistance), 0.40);
      float frontCutoff = smoothstep(-0.66, -0.08, frontX);
      float noseWeight = mix(0.76, 1.06, smoothstep(-0.24, 0.84, frontX));
      float shoulderBreak = 1.0 - 0.14 * pow(abs(frontY), 1.12);
      float foldedRidge = 0.84
        + 0.18 * sin(frontY * 7.2 + frontX * 1.7 + u_seed * 0.37)
        + 0.07 * sin(frontY * 12.8 - frontX * 2.7 + u_seed * 1.13);
      float frontCoreX = (bladeForwardPixels - radius * 0.46) / max(1.0, radius * 0.42);
      float frontCoreY = bladeLateralPixels / max(1.0, radius * 0.72);
      float frontCore = pow(max(0.0, 1.0 - (frontCoreX * frontCoreX + frontCoreY * frontCoreY)), 0.28);
      float frontBridgeX = (bladeForwardPixels - radius * 0.12) / max(1.0, radius * 0.76);
      float frontBridgeY = bladeLateralPixels / max(1.0, radius * 0.50);
      float frontBridge = pow(max(0.0, 1.0 - (frontBridgeX * frontBridgeX + frontBridgeY * frontBridgeY)), 0.92);
      float frontWebX = (bladeForwardPixels - radius * 0.28) / max(1.0, radius * 0.58);
      float frontWebY = bladeLateralPixels / max(1.0, radius * 0.90);
      float frontWeb = pow(max(0.0, 1.0 - (frontWebX * frontWebX * 0.72 + frontWebY * frontWebY)), 0.84);
      float frontMask = max(
        frontBody
          * frontCutoff
          * noseWeight
          * shoulderBreak
          * clamp(broadNoise * fineNoise * foldedRidge, 0.56, 1.24),
        max(frontCore * 1.22 + frontBridge * 0.56, frontWeb * 0.88)
      ) * pushEnabled;

      float sideForward = (bladeForwardPixels - radius * 0.02) / max(1.0, radius * 0.70);
      float leftLateral = (bladeLateralPixels + radius * 0.92) / max(1.0, radius * 0.13);
      float rightLateral = (bladeLateralPixels - radius * 0.92) / max(1.0, radius * 0.13);
      float leftDistance = leftLateral * leftLateral + sideForward * sideForward;
      float rightDistance = rightLateral * rightLateral + sideForward * sideForward;
      float brokenSmear = smoothstep(0.38, 0.78,
        0.5
          + 0.24 * sin(bladeForwardPixels * 0.17 + u_seed * 0.83)
          + 0.13 * sin(bladeForwardPixels * 0.071 - u_seed * 1.27)
      );
      float sideTaper = smoothstep(-0.62, -0.12, sideForward)
        * (1.0 - smoothstep(0.18, 0.70, sideForward));
      float sidePersistence = mix(0.36, 0.78, loadRatio);
      float frontCornerSuppression = 1.0 - smoothstep(0.16, 0.78, frontMask);
      float leftMask = pow(max(0.0, 1.0 - leftDistance), 1.72)
        * brokenSmear
        * sideTaper
        * sidePersistence
        * mix(0.28, 1.0, frontCornerSuppression)
        * clamp(fineNoise, 0.80, 1.04)
        * pushEnabled;
      float rightMask = pow(max(0.0, 1.0 - rightDistance), 1.72)
        * (0.72 + 0.20 * brokenSmear)
        * sideTaper
        * sidePersistence
        * mix(0.28, 1.0, frontCornerSuppression)
        * clamp(broadNoise, 0.80, 1.04)
        * pushEnabled;
      vec2 pixelStep = vec2(1.0 / u_resolution.x, 1.0 / u_resolution.y);
      float nearbyPaint = 0.0;
      nearbyPaint += step(floorHeight + 0.001, (texture2D(u_wet, uv + vec2(pixelStep.x, 0.0)).a + texture2D(u_dry, uv + vec2(pixelStep.x, 0.0)).a) * ${MAX_HEIGHT.toFixed(1)});
      nearbyPaint += step(floorHeight + 0.001, (texture2D(u_wet, uv - vec2(pixelStep.x, 0.0)).a + texture2D(u_dry, uv - vec2(pixelStep.x, 0.0)).a) * ${MAX_HEIGHT.toFixed(1)});
      nearbyPaint += step(floorHeight + 0.001, (texture2D(u_wet, uv + vec2(0.0, pixelStep.y)).a + texture2D(u_dry, uv + vec2(0.0, pixelStep.y)).a) * ${MAX_HEIGHT.toFixed(1)});
      nearbyPaint += step(floorHeight + 0.001, (texture2D(u_wet, uv - vec2(0.0, pixelStep.y)).a + texture2D(u_dry, uv - vec2(0.0, pixelStep.y)).a) * ${MAX_HEIGHT.toFixed(1)});
      float contactCohesion = smoothstep(0.5, 3.0, nearbyPaint) * clamp(u_cohesion, 0.0, 1.0);
      frontMask = clamp(frontMask * mix(1.0, 1.18, contactCohesion), 0.0, 1.28);
      leftMask = clamp(leftMask * mix(1.0, 1.08, contactCohesion), 0.0, 1.08);
      rightMask = clamp(rightMask * mix(1.0, 1.08, contactCohesion), 0.0, 1.08);

      float frontArea = max(1.0, 3.14159265 * radius * 0.62 * radius * 0.38);
      float sideArea = max(1.0, 3.14159265 * radius * 0.13 * radius * 0.58);
      float frontRetention = mix(1.20, 1.48, 1.0 - loadRatio);
      float frontAddedHeight = carriedUnits * frontShare / frontArea * frontMask * frontRetention;
      float leftAddedHeight = carriedUnits * sideShare * leftShare / sideArea * leftMask;
      float rightAddedHeight = carriedUnits * sideShare * rightShare / sideArea * rightMask;

      float overflowArea = max(1.0, frontArea * 0.72);
      float overflowAddedHeight = overflowReleased / overflowArea
        * frontMask
        * mix(0.45, 0.82, motionFactor);

      float addedHeight = max(
        0.0,
        frontAddedHeight + leftAddedHeight + rightAddedHeight + overflowAddedHeight
      );
      nextHeight = max(nextHeight, max(0.0, currentTotalHeight + addedHeight - dryHeight));

      float validKnifeLoad = step(0.0001, knifeLoad.a);
      vec3 sampledDepositColour = oldColour;
      if (frontContact > 0.5) {
        sampledDepositColour = frontSourceColour;
      } else if (sideContact > 0.5) {
        sampledDepositColour = sideSourceColour;
      }
      vec3 carriedDepositColour = mix(sampledDepositColour, knifeLoad.rgb, validKnifeLoad);
      vec3 depositColour = mix(oldColour, carriedDepositColour, clamp(0.35 + knifeLoad.a * 0.55, 0.35, 0.90));
      nextColour = addedHeight > 0.0001
        ? mixPaint(oldColour, oldHeight, depositColour, addedHeight)
        : oldColour;

      float existingFloorPaint = inSweep
        * step(0.0001, sourceTotalHeight)
        * (1.0 - step(floorHeight + 0.01, sourceTotalHeight))
        * validKnifeLoad;
      float thinLayerAmount = mix(
        0.18,
        0.45,
        clamp(knifeLoad.a, 0.0, 1.0)
      );
      vec3 thinLayerColour = mixPaint(
        nextColour,
        floorHeight,
        knifeLoad.rgb,
        thinLayerAmount
      );
      nextColour = mix(nextColour, thinLayerColour, existingFloorPaint);

      float scrapedSource = inSweep * step(0.0001, sourceTotalHeight);
      float preserveMergedBlob = pushEnabled
        * step(floorHeight + 0.01, sourceTotalHeight)
        * step(0.0001, addedHeight);
      float requiredFloor = floorHeight * scrapedSource;
      nextHeight = max(nextHeight, requiredFloor * (1.0 - preserveMergedBlob));
    }

    float brushOverwrite = step(0.5, u_operation) * (1.0 - step(1.5, u_operation));
    float maximumWetHeight = knifeSweep > 0.0 || brushOverwrite > 0.5
      ? ${MAX_HEIGHT.toFixed(1)}
      : max(0.0, ${MAX_HEIGHT.toFixed(1)} - dryHeight);
    nextHeight = clamp(nextHeight, 0.0, maximumWetHeight);

    if (nextHeight < 0.001) {
      gl_FragColor = vec4(0.0);
      return;
    }

    gl_FragColor = vec4(clamp(nextColour, 0.0, 1.0), clamp(nextHeight / ${MAX_HEIGHT.toFixed(1)}, 0.0, 1.0));
  }
`;

const DRY_FRAGMENT = `
  precision highp float;
  uniform sampler2D u_wet;
  uniform sampler2D u_dry;
  varying vec2 v_uv;
  void main() {
    vec4 wet = texture2D(u_wet, v_uv);
    vec4 dry = texture2D(u_dry, v_uv);
    float total = min(1.0, wet.a + dry.a);
    if (total <= 0.000001) {
      gl_FragColor = vec4(0.0);
      return;
    }
    vec3 colour = (dry.rgb * dry.a + wet.rgb * wet.a) / max(0.000001, dry.a + wet.a);
    gl_FragColor = vec4(colour, total);
  }
`;

const DISPLAY_FRAGMENT = `
  precision highp float;
  uniform sampler2D u_wet;
  uniform sampler2D u_dry;
  uniform vec2 u_pixelStep;
  uniform vec2 u_canvasRatio;
  uniform float u_relief;
  uniform float u_gloss;
  uniform float u_liquid;
  uniform float u_lightAngle;
  uniform float u_lightElevation;
  uniform float u_opacity;
  uniform float u_viewMode;
  varying vec2 v_uv;

  float surfaceHeight(vec2 point) {
    return (texture2D(u_wet, point).a + texture2D(u_dry, point).a) * ${MAX_HEIGHT.toFixed(1)};
  }

  void main() {
    vec4 wet = texture2D(u_wet, v_uv);
    vec4 dry = texture2D(u_dry, v_uv);
    float wetHeight = wet.a * ${MAX_HEIGHT.toFixed(1)};
    float dryHeight = dry.a * ${MAX_HEIGHT.toFixed(1)};
    float heightValue = wetHeight + dryHeight;
    if (heightValue < 0.00001) discard;

    vec3 base = (wet.rgb * wetHeight + dry.rgb * dryHeight) / max(heightValue, 0.00001);

    if (u_viewMode > 1.5) {
      float shown = pow(clamp(heightValue / 220.0, 0.0, 1.0), 0.42);
      float alpha = clamp(u_opacity, 0.0, 1.0);
      gl_FragColor = vec4(vec3(shown) * alpha, alpha);
      return;
    }

    if (u_viewMode > 0.5) {
      float alpha = clamp(u_opacity, 0.0, 1.0);
      gl_FragColor = vec4(base * alpha, alpha);
      return;
    }

    float hLeftNear = surfaceHeight(v_uv - vec2(u_pixelStep.x, 0.0));
    float hRightNear = surfaceHeight(v_uv + vec2(u_pixelStep.x, 0.0));
    float hUpNear = surfaceHeight(v_uv - vec2(0.0, u_pixelStep.y));
    float hDownNear = surfaceHeight(v_uv + vec2(0.0, u_pixelStep.y));
    float hLeftWide = surfaceHeight(v_uv - vec2(u_pixelStep.x * 2.0, 0.0));
    float hRightWide = surfaceHeight(v_uv + vec2(u_pixelStep.x * 2.0, 0.0));
    float hUpWide = surfaceHeight(v_uv - vec2(0.0, u_pixelStep.y * 2.0));
    float hDownWide = surfaceHeight(v_uv + vec2(0.0, u_pixelStep.y * 2.0));

    vec2 detailedSlope = vec2(hRightNear - hLeftNear, hDownNear - hUpNear);
    vec2 smoothSlope = vec2(hRightWide - hLeftWide, hDownWide - hUpWide) * 0.5;
    vec2 slope = mix(detailedSlope, smoothSlope, 0.25) * max(0.18, u_relief / 2.2);
    vec3 normal = normalize(vec3(-slope.x, -slope.y, 1.0));

    vec2 lampXY = vec2(cos(u_lightAngle), sin(u_lightAngle));
    vec2 lampPosition = vec2(0.5) - lampXY * 0.82;
    float lampDepth = max(0.05, tan(u_lightElevation) * 0.55);
    vec3 lightVector = vec3((v_uv - lampPosition) * u_canvasRatio, -lampDepth);
    vec3 lightDirection = normalize(lightVector);
    vec3 viewDirection = vec3(0.0, 0.0, 1.0);
    vec3 halfVector = normalize(lightDirection + viewDirection);

    float diffuse = max(0.0, dot(lightDirection, normal));
    float ambient = 0.82;
    float directional = 0.15 * diffuse;
    vec3 litColour = base * (ambient + directional);

    float ndoth = max(dot(normal, halfVector), 0.0);
    float ndotv = max(dot(normal, viewDirection), 0.0);
    float fresnel = 0.05 + (1.0 - 0.05) * pow(1.0 - ndotv, 5.0);
    float roughness = mix(0.14, 0.03, clamp(u_gloss, 0.0, 1.0));
    float glossPower = mix(18.0, 170.0, 1.0 - roughness);
    float specular = pow(ndoth, glossPower) * mix(0.12, 0.60, clamp(u_gloss, 0.0, 1.0));
    litColour += vec3(specular * fresnel);

    float alpha = clamp(u_opacity, 0.0, 1.0);
    gl_FragColor = vec4(clamp(litColour, 0.0, 1.0) * alpha, alpha);
  }
`;

const LOAD_CAPTURE_FRAGMENT = `
  precision highp float;
  uniform sampler2D u_source;
  uniform sampler2D u_oldLoad;
  uniform vec2 u_sourceUv;
  uniform float u_addedAmount;
  varying vec2 v_uv;
  void main() {
    vec4 oldLoad = texture2D(u_oldLoad, vec2(0.5));
    vec4 sampled = texture2D(u_source, u_sourceUv);
    float sampledAmount = clamp(sampled.a * u_addedAmount, 0.0, 1.0);
    float total = min(1.0, oldLoad.a + sampledAmount);
    vec3 colour = total > 0.000001
      ? (oldLoad.rgb * oldLoad.a + sampled.rgb * sampledAmount) / max(0.000001, oldLoad.a + sampledAmount)
      : sampled.rgb;
    gl_FragColor = vec4(colour, total);
  }
`;

const ENCODE_COLOUR_FRAGMENT = `
  precision highp float;
  uniform sampler2D u_source;
  varying vec2 v_uv;
  void main() {
    vec4 paint = texture2D(u_source, v_uv);
    gl_FragColor = vec4(paint.rgb, paint.a > 0.000001 ? 1.0 : 0.0);
  }
`;

const ENCODE_HEIGHT_FRAGMENT = `
  precision highp float;
  uniform sampler2D u_source;
  varying vec2 v_uv;
  void main() {
    float units = clamp(texture2D(u_source, v_uv).a * ${MAX_HEIGHT.toFixed(1)}, 0.0, ${MAX_HEIGHT.toFixed(1)});
    float value = floor(units + 0.5);
    float high = floor(value / 256.0);
    float low = value - high * 256.0;
    gl_FragColor = vec4(high / 255.0, low / 255.0, 0.0, 1.0);
  }
`;

const DECODE_FRAGMENT = `
  precision highp float;
  uniform sampler2D u_colourImage;
  uniform sampler2D u_heightImage;
  varying vec2 v_uv;
  void main() {
    vec4 colour = texture2D(u_colourImage, v_uv);
    vec4 heightData = texture2D(u_heightImage, v_uv);
    float high = floor(heightData.r * 255.0 + 0.5);
    float low = floor(heightData.g * 255.0 + 0.5);
    float units = high * 256.0 + low;
    float height = clamp(units / ${MAX_HEIGHT.toFixed(1)}, 0.0, 1.0);
    gl_FragColor = vec4(colour.rgb, colour.a > 0.0001 ? height : 0.0);
  }
`;


const BRUSH_MASK_FRAGMENT = `
  precision highp float;
  uniform vec2 u_resolution;
  uniform vec2 u_previousPoint;
  uniform vec2 u_currentPoint;
  uniform vec2 u_previousDirection;
  uniform vec2 u_currentDirection;
  uniform float u_brushRadius;
  uniform float u_bristleRadius;
  uniform float u_bristleLength;
  uniform float u_bristleCount;
  varying vec2 v_documentPosition;

  float randomValue(float value) {
    return fract(sin(value * 12.9898) * 43758.5453);
  }

  float segmentDistance(vec2 point, vec2 first, vec2 second) {
    vec2 segment = second - first;
    float denominator = max(dot(segment, segment), 0.000001);
    float amount = clamp(dot(point - first, segment) / denominator, 0.0, 1.0);
    return length(point - (first + segment * amount));
  }

  float edgeSign(vec2 point, vec2 first, vec2 second) {
    return (point.x - second.x) * (first.y - second.y)
      - (first.x - second.x) * (point.y - second.y);
  }

  float insideTriangle(vec2 point, vec2 first, vec2 second, vec2 third) {
    float firstSign = edgeSign(point, first, second);
    float secondSign = edgeSign(point, second, third);
    float thirdSign = edgeSign(point, third, first);
    bool hasNegative = firstSign < 0.0 || secondSign < 0.0 || thirdSign < 0.0;
    bool hasPositive = firstSign > 0.0 || secondSign > 0.0 || thirdSign > 0.0;
    return hasNegative && hasPositive ? 0.0 : 1.0;
  }

  void main() {
    float safeCount = clamp(floor(u_bristleCount + 0.5), 1.0, 250.0);
    vec2 previousDirection = length(u_previousDirection) > 0.0001
      ? normalize(u_previousDirection)
      : vec2(1.0, 0.0);
    vec2 currentDirection = length(u_currentDirection) > 0.0001
      ? normalize(u_currentDirection)
      : previousDirection;
    vec2 previousSide = vec2(-previousDirection.y, previousDirection.x);
    vec2 currentSide = vec2(-currentDirection.y, currentDirection.x);
    float nearest = 1000000.0;
    float insideSweep = 0.0;

    for (int bristleIndex = 0; bristleIndex < 250; bristleIndex += 1) {
      float index = float(bristleIndex);
      if (index < safeCount) {
        float seed = randomValue(index + 1.0);
        float radial = sqrt((index + 0.5) / safeCount);
        float angle = index * 2.399963229728653 + seed * 0.9;
        vec2 localOffset = vec2(cos(angle), sin(angle)) * u_brushRadius * 0.88 * radial;
        vec2 previousOffset = previousDirection * localOffset.x + previousSide * localOffset.y;
        vec2 currentOffset = currentDirection * localOffset.x + currentSide * localOffset.y;
        vec2 previousBase = u_previousPoint + previousOffset;
        vec2 currentBase = u_currentPoint + currentOffset;
        vec2 previousTip = previousBase + previousDirection * u_bristleLength;
        vec2 currentTip = currentBase + currentDirection * u_bristleLength;

        nearest = min(nearest, segmentDistance(v_documentPosition, previousBase, previousTip));
        nearest = min(nearest, segmentDistance(v_documentPosition, currentBase, currentTip));
        nearest = min(nearest, segmentDistance(v_documentPosition, previousBase, currentBase));
        nearest = min(nearest, segmentDistance(v_documentPosition, previousTip, currentTip));
        insideSweep = max(insideSweep, max(
          insideTriangle(v_documentPosition, previousBase, previousTip, currentTip),
          insideTriangle(v_documentPosition, previousBase, currentTip, currentBase)
        ));
      }
    }

    float edgeMask = 1.0 - smoothstep(
      max(0.0, u_bristleRadius - 0.55),
      u_bristleRadius + 0.55,
      nearest
    );
    float mask = max(edgeMask, insideSweep);
    gl_FragColor = vec4(mask, mask, mask, mask);
  }
`;


export function createOilPaintGpuSystem(gl, initialWidth, initialHeight, options = {}) {
  let width = Math.max(1, Math.floor(Number(initialWidth) || 1));
  let height = Math.max(1, Math.floor(Number(initialHeight) || 1));
  let destroyed = false;
  let paintType = gl.UNSIGNED_BYTE;
  const floatTextures = gl.getExtension('OES_texture_float');
  const floatBuffers = gl.getExtension('WEBGL_color_buffer_float');
  if (floatTextures && floatBuffers) paintType = gl.FLOAT;

  const PRESSURE_JACOBI_ITERATIONS = 2;
  const SIMULATION_LIFETIME_FRAMES = 60;
  const FLUID_TIME_STEP = 1 / 60;

  const quadProgram = createProgram(gl, QUAD_VERTEX, COPY_FRAGMENT);
  const tubeStrokeProgram = createProgram(gl, DOCUMENT_VERTEX, TUBE_STROKE_FRAGMENT);
  const fluidVelocitySplatProgram = createProgram(gl, DOCUMENT_VERTEX, FLUID_VELOCITY_SPLAT_FRAGMENT);
  const fluidAdvectProgram = createProgram(gl, QUAD_VERTEX, FLUID_ADVECT_FRAGMENT);
  const fluidDivergenceProgram = createProgram(gl, QUAD_VERTEX, FLUID_DIVERGENCE_FRAGMENT);
  const fluidJacobiProgram = createProgram(gl, QUAD_VERTEX, FLUID_JACOBI_FRAGMENT);
  const fluidSubtractProgram = createProgram(gl, QUAD_VERTEX, FLUID_SUBTRACT_FRAGMENT);
  const stickinessFlowProgram = createProgram(gl, QUAD_VERTEX, STICKINESS_FLOW_FRAGMENT);
  const stickinessGatherProgram = createProgram(gl, QUAD_VERTEX, STICKINESS_GATHER_FRAGMENT);
  const brushFlowProgram = createProgram(gl, DOCUMENT_VERTEX, BRUSH_FLOW_FRAGMENT);
  const paintRelaxFlowProgram = createProgram(gl, QUAD_VERTEX, PAINT_RELAX_FLOW_FRAGMENT);
  const massGatherProgram = createProgram(gl, QUAD_VERTEX, MASS_GATHER_FRAGMENT);
  const updateProgram = createProgram(gl, DOCUMENT_VERTEX, PAINT_UPDATE_FRAGMENT);
  const brushMaskProgram = createProgram(gl, DOCUMENT_VERTEX, BRUSH_MASK_FRAGMENT);
  const knifePickupMassProgram = createProgram(gl, DOCUMENT_VERTEX, KNIFE_PICKUP_MASS_FRAGMENT);
  const knifePickupCountProgram = createProgram(gl, DOCUMENT_VERTEX, KNIFE_PICKUP_COUNT_FRAGMENT);
  const reduceSumProgram = createProgram(gl, QUAD_VERTEX, REDUCE_SUM_FRAGMENT);
  const knifeStateUpdateProgram = createProgram(gl, QUAD_VERTEX, KNIFE_STATE_UPDATE_FRAGMENT);
  const knifeSweepMaskProgram = createProgram(gl, DOCUMENT_VERTEX, KNIFE_SWEEP_MASK_FRAGMENT);
  const knifeDryClearProgram = createProgram(gl, QUAD_VERTEX, KNIFE_DRY_CLEAR_FRAGMENT);
  const brushDryClearProgram = createProgram(gl, DOCUMENT_VERTEX, BRUSH_DRY_CLEAR_FRAGMENT);
  const importedPaintProgram = createProgram(gl, DOCUMENT_VERTEX, IMPORTED_PAINT_FRAGMENT);
  const importedDryClearProgram = createProgram(gl, DOCUMENT_VERTEX, IMPORTED_DRY_CLEAR_FRAGMENT);
  const knifeWetFloorProgram = createProgram(gl, QUAD_VERTEX, KNIFE_WET_FLOOR_FRAGMENT);
  const dryProgram = createProgram(gl, QUAD_VERTEX, DRY_FRAGMENT);
  const displayProgram = createProgram(gl, QUAD_VERTEX, DISPLAY_FRAGMENT);
  const loadCaptureProgram = createProgram(gl, QUAD_VERTEX, LOAD_CAPTURE_FRAGMENT);
  const encodeColourProgram = createProgram(gl, QUAD_VERTEX, ENCODE_COLOUR_FRAGMENT);
  const encodeHeightProgram = createProgram(gl, QUAD_VERTEX, ENCODE_HEIGHT_FRAGMENT);
  const decodeProgram = createProgram(gl, QUAD_VERTEX, DECODE_FRAGMENT);
  const quadBuffer = gl.createBuffer();
  const documentBuffer = gl.createBuffer();
  const brushMaskBuffer = gl.createBuffer();
  if (!quadBuffer || !documentBuffer || !brushMaskBuffer) throw new Error('Unable to create oil paint buffers.');
  gl.bindBuffer(gl.ARRAY_BUFFER, quadBuffer);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]), gl.STATIC_DRAW);

  const layers = new Map();
  const strokeSessions = new Map();
  let scratch = null;
  let stickinessFlowTarget = null;
  let paintFlowTarget = null;
  let brushMaskTarget = null;
  let brushSideMaskTarget = null;
  let exportTarget = null;
  let moveSession = null;
  let simulationAnimationFrame = null;
  const layerDebugMetrics = new Map();
  let displaySettings = {
    view: 'final',
    liquid: 0,
    stickiness: 82,
    gloss: 78,
    lightAngle: 315,
    lightElevation: 35,
  };

  function createTexture(textureWidth = width, textureHeight = height, type = paintType) {
    const texture = gl.createTexture();
    if (!texture) throw new Error('Unable to create oil paint texture.');
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, textureWidth, textureHeight, 0, gl.RGBA, type, null);
    gl.bindTexture(gl.TEXTURE_2D, null);
    return texture;
  }

  function createFramebuffer(texture) {
    if (gl.isContextLost()) return null;
    const framebuffer = gl.createFramebuffer();
    if (!framebuffer) return null;
    gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texture, 0);
    const status = gl.checkFramebufferStatus(gl.FRAMEBUFFER);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    if (status !== gl.FRAMEBUFFER_COMPLETE) {
      if (!gl.isContextLost() && gl.isFramebuffer(framebuffer)) gl.deleteFramebuffer(framebuffer);
      return null;
    }
    return framebuffer;
  }

  function createTarget(textureWidth = width, textureHeight = height, type = paintType) {
    if (gl.isContextLost()) return null;
    const texture = createTexture(textureWidth, textureHeight, type);
    const framebuffer = createFramebuffer(texture);
    if (!framebuffer) {
      if (!gl.isContextLost() && gl.isTexture(texture)) gl.deleteTexture(texture);
      return null;
    }
    gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer);
    gl.viewport(0, 0, textureWidth, textureHeight);
    gl.disable(gl.BLEND);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    return { texture, framebuffer, width: textureWidth, height: textureHeight, type };
  }

  function deleteTarget(target) {
    if (!target || gl.isContextLost()) return;
    if (target.framebuffer && gl.isFramebuffer(target.framebuffer)) gl.deleteFramebuffer(target.framebuffer);
    if (target.texture && gl.isTexture(target.texture)) gl.deleteTexture(target.texture);
  }

  function createLoadTarget() {
    return createTarget(1, 1, paintType);
  }

  function clearTarget(target) {
    if (!target) return;
    gl.bindFramebuffer(gl.FRAMEBUFFER, target.framebuffer);
    gl.viewport(0, 0, target.width, target.height);
    gl.disable(gl.BLEND);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  }

  function createLayer(id) {
    const layer = {
      id,
      wet: createTarget(),
      dry: createTarget(),
      brushLoad: [createLoadTarget(), createLoadTarget()],
      brushLoadIndex: 0,
      brushPaintAmount: 0,
      brushPaintColour: [0, 0, 0],
      knifeLoad: [createLoadTarget(), createLoadTarget()],
      knifeLoadIndex: 0,
      knifeState: [createTarget(1, 1, gl.FLOAT), createTarget(1, 1, gl.FLOAT)],
      knifeStateIndex: 0,
      velocity: createTarget(width, height, paintType === gl.FLOAT ? gl.FLOAT : paintType),
      velocityTemp: createTarget(width, height, paintType === gl.FLOAT ? gl.FLOAT : paintType),
      divergence: createTarget(width, height, paintType === gl.FLOAT ? gl.FLOAT : paintType),
      pressure: createTarget(width, height, paintType === gl.FLOAT ? gl.FLOAT : paintType),
      pressureTemp: createTarget(width, height, paintType === gl.FLOAT ? gl.FLOAT : paintType),
      simulationAreas: [],
      simulationFrameNumber: 0,
      simulationLiquid: 0,
      knifePaintUnits: 0,
      knifePaintColour: [0, 0, 0],
      containsPaint: false,
    };
    layers.set(id, layer);
    return layer;
  }

  function ensureLayer(id) {
    const safeId = String(id || '').trim();
    if (!safeId) return null;
    return layers.get(safeId) || createLayer(safeId);
  }

  function deleteLayer(id) {
    const safeId = String(id || '').trim();
    const layer = layers.get(safeId);
    if (!layer) return;
    deleteTarget(layer.wet);
    deleteTarget(layer.dry);
    layer.brushLoad.forEach(deleteTarget);
    layer.knifeLoad.forEach(deleteTarget);
    layer.knifeState.forEach(deleteTarget);
    deleteTarget(layer.velocity);
    deleteTarget(layer.velocityTemp);
    deleteTarget(layer.divergence);
    deleteTarget(layer.pressure);
    deleteTarget(layer.pressureTemp);
    layers.delete(safeId);
    layerDebugMetrics.delete(safeId);
  }

  function ensureScratch() {
    if (!scratch || scratch.width !== width || scratch.height !== height || scratch.type !== paintType) {
      deleteTarget(scratch);
      scratch = createTarget();
    }
    return scratch;
  }

  function ensureBrushMaskTarget() {
    if (!brushMaskTarget || brushMaskTarget.width !== width || brushMaskTarget.height !== height) {
      deleteTarget(brushMaskTarget);
      brushMaskTarget = createTarget(width, height, gl.UNSIGNED_BYTE);
    }
    return brushMaskTarget;
  }

  function ensureBrushSideMaskTarget() {
    if (!brushSideMaskTarget || brushSideMaskTarget.width !== width || brushSideMaskTarget.height !== height) {
      deleteTarget(brushSideMaskTarget);
      brushSideMaskTarget = createTarget(width, height, gl.UNSIGNED_BYTE);
    }
    return brushSideMaskTarget;
  }


  function ensureStickinessFlowTarget() {
    if (!stickinessFlowTarget
      || stickinessFlowTarget.width !== width
      || stickinessFlowTarget.height !== height) {
      deleteTarget(stickinessFlowTarget);
      stickinessFlowTarget = createTarget(width, height, gl.UNSIGNED_BYTE);
    }
    return stickinessFlowTarget;
  }

  function ensurePaintFlowTarget() {
    if (!paintFlowTarget
      || paintFlowTarget.width !== width
      || paintFlowTarget.height !== height) {
      deleteTarget(paintFlowTarget);
      paintFlowTarget = createTarget(width, height, gl.UNSIGNED_BYTE);
    }
    return paintFlowTarget;
  }

  function ensureExportTarget() {
    if (!exportTarget || exportTarget.width !== width || exportTarget.height !== height) {
      deleteTarget(exportTarget);
      exportTarget = createTarget(width, height, gl.UNSIGNED_BYTE);
    }
    return exportTarget;
  }

  function bindQuad(program) {
    gl.bindBuffer(gl.ARRAY_BUFFER, quadBuffer);
    const position = gl.getAttribLocation(program, 'a_position');
    gl.enableVertexAttribArray(position);
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);
    return position;
  }

  function drawTexture(sourceTexture, destinationFramebuffer, viewportWidth = width, viewportHeight = height, blend = false, offsetX = 0, offsetY = 0) {
    gl.bindFramebuffer(gl.FRAMEBUFFER, destinationFramebuffer);
    gl.viewport(offsetX, offsetY, viewportWidth, viewportHeight);
    if (blend) {
      gl.enable(gl.BLEND);
      gl.blendFuncSeparate(gl.ONE, gl.ONE_MINUS_SRC_ALPHA, gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    } else {
      gl.disable(gl.BLEND);
    }
    gl.useProgram(quadProgram);
    const position = bindQuad(quadProgram);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, sourceTexture);
    gl.uniform1i(gl.getUniformLocation(quadProgram, 'u_source'), 0);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
    gl.disableVertexAttribArray(position);
    gl.bindTexture(gl.TEXTURE_2D, null);
    if (blend) gl.disable(gl.BLEND);
  }

  function cloneTarget(source) {
    if (!source || gl.isContextLost()) return null;
    const target = createTarget(source.width, source.height, source.type);
    if (!target) return null;
    drawTexture(source.texture, target.framebuffer, source.width, source.height, false);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    return target;
  }

  function setLoadTargetColour(layer, kind, colour, amount) {
    const safeColour = Array.isArray(colour) && colour.length >= 3
      ? [
          clamp(colour[0], 0, 1),
          clamp(colour[1], 0, 1),
          clamp(colour[2], 0, 1),
        ]
      : [0, 0, 0];
    const safeAmount = clamp(amount, 0, 1);
    const targets = kind === 'knife' ? layer.knifeLoad : layer.brushLoad;
    const indexKey = kind === 'knife' ? 'knifeLoadIndex' : 'brushLoadIndex';
    const nextIndex = 1 - layer[indexKey];
    const target = targets[nextIndex];
    gl.bindFramebuffer(gl.FRAMEBUFFER, target.framebuffer);
    gl.viewport(0, 0, 1, 1);
    gl.disable(gl.BLEND);
    gl.clearColor(safeColour[0], safeColour[1], safeColour[2], safeAmount);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    layer[indexKey] = nextIndex;
    if (kind === 'knife') {
      layer.knifePaintColour = safeAmount > 0.000001 ? [...safeColour] : [0, 0, 0];
    } else {
      layer.brushPaintAmount = safeAmount;
      if (safeAmount > 0.000001) layer.brushPaintColour = [...safeColour];
    }
  }

  function setLoadTarget(layer, kind, colourValue, amount) {
    setLoadTargetColour(layer, kind, hexToColour(colourValue), amount);
  }

  function syncDisplayedBrushLoad(layer) {
    if (!layer) return;
    syncPaintBrushLoadInfo(layer.brushPaintAmount || 0);
  }

  function consumeBrushLoad(layer, radius, segmentDistance, pressure, sideDepositsEnabled) {
    if (!layer) return 0;
    const currentAmount = clamp(layer.brushPaintAmount, 0, 1);
    if (currentAmount <= 0.000001) {
      if (layer.brushPaintAmount !== 0) {
        layer.brushPaintAmount = 0;
        setLoadTargetColour(layer, 'brush', layer.brushPaintColour || [0, 0, 0], 0);
      }
      syncDisplayedBrushLoad(layer);
      return 0;
    }
    const safeRadius = Math.max(1, Number(radius) || 1);
    const safeDistance = Math.max(0, Number(segmentDistance) || 0);
    const safePressure = clamp(Number(pressure) || 0, 0.01, 1);
    const bodyUse = clamp((safeDistance / Math.max(6, safeRadius * 8.5)) * (0.020 + safePressure * 0.055), 0.0015, 0.065);
    const sideUse = sideDepositsEnabled ? bodyUse * 0.42 : 0;
    const consumed = Math.min(currentAmount, bodyUse + sideUse);
    const nextAmount = Math.max(0, currentAmount - consumed);
    setLoadTargetColour(layer, 'brush', layer.brushPaintColour || [0, 0, 0], nextAmount);
    syncDisplayedBrushLoad(layer);
    return consumed;
  }

  function captureLoad(layer, kind, sourceTexture, point, addedAmount) {
    const targets = kind === 'knife' ? layer.knifeLoad : layer.brushLoad;
    const indexKey = kind === 'knife' ? 'knifeLoadIndex' : 'brushLoadIndex';
    const currentIndex = layer[indexKey];
    const nextIndex = 1 - currentIndex;
    gl.bindFramebuffer(gl.FRAMEBUFFER, targets[nextIndex].framebuffer);
    gl.viewport(0, 0, 1, 1);
    gl.disable(gl.BLEND);
    gl.useProgram(loadCaptureProgram);
    const position = bindQuad(loadCaptureProgram);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, sourceTexture);
    gl.uniform1i(gl.getUniformLocation(loadCaptureProgram, 'u_source'), 0);
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, targets[currentIndex].texture);
    gl.uniform1i(gl.getUniformLocation(loadCaptureProgram, 'u_oldLoad'), 1);
    gl.uniform2f(
      gl.getUniformLocation(loadCaptureProgram, 'u_sourceUv'),
      clamp(Number(point && point.x) / width, 0, 1),
      clamp(1 - Number(point && point.y) / height, 0, 1),
    );
    gl.uniform1f(gl.getUniformLocation(loadCaptureProgram, 'u_addedAmount'), clamp(addedAmount, 0, 1));
    gl.drawArrays(gl.TRIANGLES, 0, 6);
    gl.disableVertexAttribArray(position);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.activeTexture(gl.TEXTURE0);
    layer[indexKey] = nextIndex;
  }

  function syncLoads(layer, settings) {
    const brushLoad = clamp(settings.brushLoad ?? settings.brushLoadPercent / 100, 0, 1);
    const knifeLoad = clamp(settings.knifeLoad ?? settings.knifeLoadPercent / 100, 0, 1);
    if (settings.brushLoadRevision !== layer.brushLoadRevision) {
      setLoadTarget(layer, 'brush', settings.brushLoadColor || settings.paintColor, brushLoad);
      layer.brushLoadRevision = settings.brushLoadRevision;
      syncDisplayedBrushLoad(layer);
    }
    if (settings.knifeLoadRevision !== layer.knifeLoadRevision && !settings.knifeLoadGpuManaged) {
      setLoadTarget(layer, 'knife', settings.knifeLoadColor || settings.paintColor, knifeLoad);
      layer.knifeLoadRevision = settings.knifeLoadRevision;
    }
  }

  function getSelection() {
    const value = typeof options.getSelection === 'function' ? options.getSelection() : null;
    return value && value.texture ? value : { enabled: false, texture: null };
  }

  function beginStroke(layerId, strokeId, settings = {}, strokeOptions = {}) {
    const layer = ensureLayer(layerId);
    if (!layer) return false;
    const safeStrokeId = String(strokeId || '').trim() || `paint-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    endStroke(safeStrokeId);
    const tool = String(settings.tool || 'tube').toLowerCase();
    const session = {
      id: safeStrokeId,
      layerId: String(layerId),
      tool,
      baseWet: tool === 'tube' ? cloneTarget(layer.wet) : null,
      tubeStroke: tool === 'tube' ? createTarget(width, height, paintType) : null,
      tubeSegmentId: 0,
      knifeSourceWet: tool === 'knife' ? cloneTarget(layer.wet) : null,
      knifeSourceDry: tool === 'knife' ? cloneTarget(layer.dry) : null,
      knifeSweep: tool === 'knife' ? createTarget(width, height, gl.UNSIGNED_BYTE) : null,
      knifePickupMass: tool === 'knife' ? createTarget(width, height, gl.FLOAT) : null,
      knifePickupCounts: tool === 'knife' ? createTarget(width, height, gl.FLOAT) : null,
      knifeMassReductionTargets: [],
      knifeCountReductionTargets: [],
      knifeBounds: null,
      brushDirection: Array.isArray(strokeOptions.paintBrushDirection)
        ? normaliseStrokeDirection(strokeOptions.paintBrushDirection, [1, 0])
        : null,
      brushCursorBristles: normaliseCursorBristlesFromOptions(strokeOptions.paintBrushBristles),
      previousBrushCursorBristles: [],
      brushSnake: null,
      knifeDirection: null,
      lastPoint: null,
      buttons: Number(strokeOptions.buttons || 1),
      paintBounds: null,
      paintSettings: { ...settings },
    };
    strokeSessions.set(safeStrokeId, session);
    return safeStrokeId;
  }


  function normaliseCursorBristlesFromOptions(bristles) {
    if (!Array.isArray(bristles)) return [];
    return bristles
      .map((bristle) => ({
        sourceIndex: Number(bristle?.sourceIndex) || 0,
        nodes: Array.isArray(bristle?.nodes)
          ? bristle.nodes.map((node) => ({
              x: Number(node?.x) || 0,
              y: Number(node?.y) || 0,
              pressure: 1,
            }))
          : [],
      }))
      .filter((bristle) => bristle.nodes.length > 1);
  }

  function endStroke(strokeId) {
    const safeStrokeId = String(strokeId || '').trim();
    if (!safeStrokeId) return false;
    const session = strokeSessions.get(safeStrokeId);
    if (!session) return false;
    const sessionLayer = layers.get(String(session.layerId || '').trim());
    if (session.tool === 'knife') {
      const layer = sessionLayer;
      if (layer) {
        layer.knifeLoad.forEach(clearTarget);
        layer.knifeLoadIndex = 0;
        layer.knifeState.forEach(clearTarget);
        layer.knifeStateIndex = 0;
        layer.knifePaintUnits = 0;
        layer.knifePaintColour = [0, 0, 0];
      }
    }
    deleteTarget(session.baseWet);
    deleteTarget(session.tubeStroke);
    deleteTarget(session.knifeSourceWet);
    deleteTarget(session.knifeSourceDry);
    deleteTarget(session.knifeSweep);
    deleteTarget(session.knifePickupMass);
    deleteTarget(session.knifePickupCounts);
    session.knifeMassReductionTargets.forEach(deleteTarget);
    session.knifeCountReductionTargets.forEach(deleteTarget);
    strokeSessions.delete(safeStrokeId);
    if (typeof options.requestRender === 'function') options.requestRender();
    return true;
  }

  function normaliseStrokeDirection(direction, fallback = [1, 0]) {
    const x = Number(direction && direction[0]) || 0;
    const y = Number(direction && direction[1]) || 0;
    const length = Math.hypot(x, y);
    if (length <= 0.000001) return [...fallback];
    return [x / length, y / length];
  }

  function resolveKnifeDirection(session, start, end, radius) {
    const explicitRotation = Number(end && end.knifeRotation);
    if (Number.isFinite(explicitRotation)) {
      const explicitDirection = [Math.cos(explicitRotation), Math.sin(explicitRotation)];
      if (session) session.knifeDirection = explicitDirection;
      return explicitDirection;
    }
    const deltaX = Number(end && end.x) - Number(start && start.x);
    const deltaY = Number(end && end.y) - Number(start && start.y);
    const distance = Math.hypot(deltaX, deltaY);
    const previous = Array.isArray(session && session.knifeDirection)
      ? normaliseStrokeDirection(session.knifeDirection)
      : null;
    if (distance <= 0.0001) return previous || [1, 0];

    const raw = [deltaX / distance, deltaY / distance];
    if (!previous) {
      if (session) session.knifeDirection = raw;
      return raw;
    }

    const dot = clamp(previous[0] * raw[0] + previous[1] * raw[1], -1, 1);
    const cross = previous[0] * raw[1] - previous[1] * raw[0];
    const angle = Math.atan2(cross, dot);
    const deadZone = Math.max(1.25, Number(radius) * 0.08);
    const correctionLimit = Math.PI / 10;
    if (distance <= deadZone && Math.abs(angle) >= correctionLimit) {
      return previous;
    }

    const maximumTurn = clamp(
      (distance / Math.max(4, Number(radius) * 0.65)) * 0.28,
      0.012,
      0.36,
    );
    const appliedTurn = clamp(angle, -maximumTurn, maximumTurn);
    const cosine = Math.cos(appliedTurn);
    const sine = Math.sin(appliedTurn);
    const next = normaliseStrokeDirection([
      previous[0] * cosine - previous[1] * sine,
      previous[0] * sine + previous[1] * cosine,
    ], previous);
    if (session) session.knifeDirection = next;
    return next;
  }

  function mergeKnifeBounds(previous, next) {
    if (!previous) return { ...next };
    return {
      minimumX: Math.min(previous.minimumX, next.minimumX),
      minimumY: Math.min(previous.minimumY, next.minimumY),
      maximumX: Math.max(previous.maximumX, next.maximumX),
      maximumY: Math.max(previous.maximumY, next.maximumY),
    };
  }

  function buildDocumentVertices(bounds) {
    return new Float32Array([
      bounds.minimumX, bounds.minimumY,
      bounds.maximumX, bounds.minimumY,
      bounds.minimumX, bounds.maximumY,
      bounds.minimumX, bounds.maximumY,
      bounds.maximumX, bounds.minimumY,
      bounds.maximumX, bounds.maximumY,
    ]);
  }

  function applyScissor(bounds) {
    gl.enable(gl.SCISSOR_TEST);
    gl.scissor(
      bounds.minimumX,
      Math.max(0, height - bounds.maximumY),
      Math.max(1, bounds.maximumX - bounds.minimumX),
      Math.max(1, bounds.maximumY - bounds.minimumY),
    );
  }

  function copyScratchRegionToTarget(target, bounds) {
    gl.bindFramebuffer(gl.FRAMEBUFFER, ensureScratch().framebuffer);
    gl.bindTexture(gl.TEXTURE_2D, target.texture);
    gl.copyTexSubImage2D(
      gl.TEXTURE_2D,
      0,
      bounds.minimumX,
      Math.max(0, height - bounds.maximumY),
      bounds.minimumX,
      Math.max(0, height - bounds.maximumY),
      bounds.maximumX - bounds.minimumX,
      bounds.maximumY - bounds.minimumY,
    );
    gl.bindTexture(gl.TEXTURE_2D, null);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.activeTexture(gl.TEXTURE0);
  }


  function clearTargetRegion(target, bounds) {
    if (!target || !bounds) return;
    gl.bindFramebuffer(gl.FRAMEBUFFER, target.framebuffer);
    gl.viewport(0, 0, target.width, target.height);
    applyScissor(bounds);
    gl.disable(gl.BLEND);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.disable(gl.SCISSOR_TEST);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  }

  function resetLayerFluidState(layer) {
    if (!layer) return;
    clearTarget(layer.velocity);
    clearTarget(layer.velocityTemp);
    clearTarget(layer.divergence);
    clearTarget(layer.pressure);
    clearTarget(layer.pressureTemp);
    layer.simulationAreas = [];
    layer.simulationFrameNumber = 0;
  }

  function fluidBoundsToBottomLeft(bounds) {
    return {
      minimumX: bounds.minimumX,
      minimumY: Math.max(0, height - bounds.maximumY),
      maximumX: bounds.maximumX,
      maximumY: Math.max(0, height - bounds.minimumY),
    };
  }

  function mergeBounds(a, b) {
    if (!a) return b ? { ...b } : null;
    if (!b) return { ...a };
    return {
      minimumX: Math.min(a.minimumX, b.minimumX),
      minimumY: Math.min(a.minimumY, b.minimumY),
      maximumX: Math.max(a.maximumX, b.maximumX),
      maximumY: Math.max(a.maximumY, b.maximumY),
    };
  }

  function addFluidSimulationArea(layer, bounds, liquid) {
    if (!layer || !bounds) return;
    const expanded = expandPaintBounds(bounds, 4 + clamp(liquid, 0, 1) * 10);
    layer.simulationLiquid = clamp(liquid, 0, 1);
    layer.simulationAreas.unshift({
      bounds: expanded,
      frameNumber: Number(layer.simulationFrameNumber) || 0,
    });
    if (layer.simulationAreas.length > 24) layer.simulationAreas.length = 24;
  }

  function collectFluidSimulationBounds(layer) {
    if (!layer) return null;
    const currentFrame = Number(layer.simulationFrameNumber) || 0;
    const active = [];
    let union = null;
    for (const area of Array.isArray(layer.simulationAreas) ? layer.simulationAreas : []) {
      if (!area || !area.bounds) continue;
      if (currentFrame - (Number(area.frameNumber) || 0) > SIMULATION_LIFETIME_FRAMES) continue;
      active.push(area);
      union = mergeBounds(union, area.bounds);
    }
    layer.simulationAreas = active;
    return union ? expandPaintBounds(union, 2) : null;
  }

  function swapLayerTargets(layer, a, b) {
    const temp = layer[a];
    layer[a] = layer[b];
    layer[b] = temp;
  }

  function runFluidFullscreenPass(program, destination, bounds, setupUniforms) {
    gl.bindFramebuffer(gl.FRAMEBUFFER, destination.framebuffer);
    gl.viewport(0, 0, width, height);
    applyScissor(bounds);
    gl.disable(gl.BLEND);
    gl.useProgram(program);
    const position = bindQuad(program);
    if (typeof setupUniforms === 'function') setupUniforms();
    gl.drawArrays(gl.TRIANGLES, 0, 6);
    gl.disableVertexAttribArray(position);
    gl.disable(gl.SCISSOR_TEST);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.activeTexture(gl.TEXTURE0);
  }

  function stepFluidLayerSimulation(layer, steps = 1) {
    if (!layer) return false;
    let simulated = false;
    for (let step = 0; step < Math.max(1, Math.floor(steps)); step += 1) {
      const bounds = collectFluidSimulationBounds(layer);
      if (!bounds) break;
      const simBounds = fluidBoundsToBottomLeft(bounds);
      const liquid = clamp(layer.simulationLiquid, 0, 1);
      const fluidity = 0.6 + liquid * 0.3;

      runFluidFullscreenPass(fluidDivergenceProgram, layer.divergence, bounds, () => {
        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, layer.velocity.texture);
        gl.uniform1i(gl.getUniformLocation(fluidDivergenceProgram, 'u_velocityTexture'), 0);
        gl.uniform2f(gl.getUniformLocation(fluidDivergenceProgram, 'u_resolution'), width, height);
      });

      clearTargetRegion(layer.pressure, bounds);
      clearTargetRegion(layer.pressureTemp, bounds);
      for (let iteration = 0; iteration < PRESSURE_JACOBI_ITERATIONS; iteration += 1) {
        runFluidFullscreenPass(fluidJacobiProgram, layer.pressureTemp, bounds, () => {
          gl.activeTexture(gl.TEXTURE0);
          gl.bindTexture(gl.TEXTURE_2D, layer.pressure.texture);
          gl.uniform1i(gl.getUniformLocation(fluidJacobiProgram, 'u_pressureTexture'), 0);
          gl.activeTexture(gl.TEXTURE1);
          gl.bindTexture(gl.TEXTURE_2D, layer.divergence.texture);
          gl.uniform1i(gl.getUniformLocation(fluidJacobiProgram, 'u_divergenceTexture'), 1);
          gl.uniform2f(gl.getUniformLocation(fluidJacobiProgram, 'u_resolution'), width, height);
        });
        swapLayerTargets(layer, 'pressure', 'pressureTemp');
      }

      runFluidFullscreenPass(fluidSubtractProgram, layer.velocityTemp, bounds, () => {
        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, layer.pressure.texture);
        gl.uniform1i(gl.getUniformLocation(fluidSubtractProgram, 'u_pressureTexture'), 0);
        gl.activeTexture(gl.TEXTURE1);
        gl.bindTexture(gl.TEXTURE_2D, layer.velocity.texture);
        gl.uniform1i(gl.getUniformLocation(fluidSubtractProgram, 'u_velocityTexture'), 1);
        gl.uniform2f(gl.getUniformLocation(fluidSubtractProgram, 'u_resolution'), width, height);
      });
      swapLayerTargets(layer, 'velocity', 'velocityTemp');

      runFluidFullscreenPass(fluidAdvectProgram, ensureScratch(), bounds, () => {
        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, layer.velocity.texture);
        gl.uniform1i(gl.getUniformLocation(fluidAdvectProgram, 'u_velocityTexture'), 0);
        gl.activeTexture(gl.TEXTURE1);
        gl.bindTexture(gl.TEXTURE_2D, layer.wet.texture);
        gl.uniform1i(gl.getUniformLocation(fluidAdvectProgram, 'u_inputTexture'), 1);
        gl.uniform1f(gl.getUniformLocation(fluidAdvectProgram, 'u_deltaTime'), FLUID_TIME_STEP);
        gl.uniform1f(gl.getUniformLocation(fluidAdvectProgram, 'u_dissipation'), 1.0);
        gl.uniform2f(gl.getUniformLocation(fluidAdvectProgram, 'u_resolution'), width, height);
        gl.uniform2f(gl.getUniformLocation(fluidAdvectProgram, 'u_min'), simBounds.minimumX, simBounds.minimumY);
        gl.uniform2f(gl.getUniformLocation(fluidAdvectProgram, 'u_max'), simBounds.maximumX, simBounds.maximumY);
      });
      copyScratchRegionToTarget(layer.wet, bounds);

      runFluidFullscreenPass(fluidAdvectProgram, layer.velocityTemp, bounds, () => {
        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, layer.velocity.texture);
        gl.uniform1i(gl.getUniformLocation(fluidAdvectProgram, 'u_velocityTexture'), 0);
        gl.activeTexture(gl.TEXTURE1);
        gl.bindTexture(gl.TEXTURE_2D, layer.velocity.texture);
        gl.uniform1i(gl.getUniformLocation(fluidAdvectProgram, 'u_inputTexture'), 1);
        gl.uniform1f(gl.getUniformLocation(fluidAdvectProgram, 'u_deltaTime'), FLUID_TIME_STEP);
        gl.uniform1f(gl.getUniformLocation(fluidAdvectProgram, 'u_dissipation'), fluidity);
        gl.uniform2f(gl.getUniformLocation(fluidAdvectProgram, 'u_resolution'), width, height);
        gl.uniform2f(gl.getUniformLocation(fluidAdvectProgram, 'u_min'), simBounds.minimumX, simBounds.minimumY);
        gl.uniform2f(gl.getUniformLocation(fluidAdvectProgram, 'u_max'), simBounds.maximumX, simBounds.maximumY);
      });
      swapLayerTargets(layer, 'velocity', 'velocityTemp');

      layer.simulationFrameNumber = (Number(layer.simulationFrameNumber) || 0) + 1;
      simulated = true;
    }

    if (!collectFluidSimulationBounds(layer)) {
      clearTarget(layer.velocity);
      clearTarget(layer.velocityTemp);
    }
    return simulated;
  }

  function stepAllFluidLayers() {
    let simulated = false;
    for (const layer of layers.values()) {
      simulated = stepFluidLayerSimulation(layer, 1) || simulated;
    }
    return simulated;
  }

  function runFluidSimulationFrame() {
    simulationAnimationFrame = null;
    if (destroyed) return;
    const simulated = stepAllFluidLayers();
    if (simulated && typeof options.requestRender === 'function') options.requestRender();
    if (Array.from(layers.values()).some((layer) => Array.isArray(layer.simulationAreas) && layer.simulationAreas.length)) {
      scheduleFluidSimulation();
    }
  }

  function scheduleFluidSimulation() {
    if (simulationAnimationFrame != null || destroyed) return;
    if (typeof requestAnimationFrame !== 'function') return;
    simulationAnimationFrame = requestAnimationFrame(runFluidSimulationFrame);
  }

  function splatFluidVelocity(layer, bounds, start, end, radius, speed, direction, selection) {
    if (!layer || !bounds) return;
    const vertices = buildDocumentVertices(bounds);
    gl.bindFramebuffer(gl.FRAMEBUFFER, layer.velocity.framebuffer);
    gl.viewport(0, 0, width, height);
    applyScissor(bounds);
    gl.enable(gl.BLEND);
    gl.blendEquation(gl.FUNC_ADD);
    gl.blendFuncSeparate(gl.ONE, gl.ONE, gl.ZERO, gl.ZERO);
    gl.useProgram(fluidVelocitySplatProgram);
    gl.bindBuffer(gl.ARRAY_BUFFER, documentBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STREAM_DRAW);
    const position = gl.getAttribLocation(fluidVelocitySplatProgram, 'a_position');
    gl.enableVertexAttribArray(position);
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);
    gl.uniform2f(gl.getUniformLocation(fluidVelocitySplatProgram, 'u_resolution'), width, height);
    gl.uniform2f(gl.getUniformLocation(fluidVelocitySplatProgram, 'u_start'), Number(start.x) || 0, Number(start.y) || 0);
    gl.uniform2f(gl.getUniformLocation(fluidVelocitySplatProgram, 'u_end'), Number(end.x) || 0, Number(end.y) || 0);
    gl.uniform2f(gl.getUniformLocation(fluidVelocitySplatProgram, 'u_direction'), Number(direction[0]) || 0, Number(direction[1]) || 0);
    gl.uniform1f(gl.getUniformLocation(fluidVelocitySplatProgram, 'u_radius'), Math.max(1.0, radius));
    gl.uniform1f(gl.getUniformLocation(fluidVelocitySplatProgram, 'u_speed'), clamp(speed, 0, 1));
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, selection.texture);
    gl.uniform1i(gl.getUniformLocation(fluidVelocitySplatProgram, 'u_selectionMask'), 0);
    gl.uniform1f(gl.getUniformLocation(fluidVelocitySplatProgram, 'u_selectionEnabled'), selection.enabled && selection.texture ? 1 : 0);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
    gl.disableVertexAttribArray(position);
    gl.disable(gl.SCISSOR_TEST);
    gl.disable(gl.BLEND);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.activeTexture(gl.TEXTURE0);
  }

  function queueFluidSimulation(layer, bounds, settings = {}, context = {}) {
    if (!layer || !bounds) return false;
    const liquid = clamp(Number(settings && settings.liquid) / 100, 0, 1);
    const start = context.start || { x: (bounds.minimumX + bounds.maximumX) * 0.5, y: (bounds.minimumY + bounds.maximumY) * 0.5 };
    const end = context.end || start;
    const direction = normaliseStrokeDirection(context.direction || [Number(end.x) - Number(start.x), Number(end.y) - Number(start.y)], [1, 0]);
    const radius = Math.max(1, Number(context.radius) || 1);
    const speed = clamp(Number(context.speed), 0, 1);
    const selection = context.selection && context.selection.texture ? context.selection : getSelection();
    splatFluidVelocity(layer, bounds, start, end, radius, speed, direction, selection);
    addFluidSimulationArea(layer, bounds, liquid);
    const simulated = stepFluidLayerSimulation(layer, 1);
    scheduleFluidSimulation();
    return simulated;
  }


  function expandPaintBounds(bounds, padding) {
    const safePadding = Math.max(0, Math.ceil(Number(padding) || 0));
    return {
      minimumX: Math.max(0, Math.floor(Number(bounds.minimumX) - safePadding)),
      minimumY: Math.max(0, Math.floor(Number(bounds.minimumY) - safePadding)),
      maximumX: Math.min(width, Math.ceil(Number(bounds.maximumX) + safePadding)),
      maximumY: Math.min(height, Math.ceil(Number(bounds.maximumY) + safePadding)),
    };
  }

  function cloneStrokePoint(point = {}) {
    const knifeRotation = Number(point && point.knifeRotation);
    return {
      x: Number(point.x) || 0,
      y: Number(point.y) || 0,
      pressure: clamp(point && point.pressure != null ? Number(point.pressure) : 1, 0.01, 1),
      knifeRotation: Number.isFinite(knifeRotation) ? knifeRotation : undefined,
    };
  }

  function interpolateStrokePoint(start, end, t) {
    const safeT = clamp(t, 0, 1);
    const startRotation = Number(start && start.knifeRotation);
    const endRotation = Number(end && end.knifeRotation);
    let knifeRotation;
    if (Number.isFinite(startRotation) && Number.isFinite(endRotation)) {
      let resolvedEndRotation = endRotation;
      const reversedEndRotation = endRotation + Math.PI;
      const directDifference = Math.atan2(
        Math.sin(resolvedEndRotation - startRotation),
        Math.cos(resolvedEndRotation - startRotation),
      );
      const reversedDifference = Math.atan2(
        Math.sin(reversedEndRotation - startRotation),
        Math.cos(reversedEndRotation - startRotation),
      );
      if (Math.abs(reversedDifference) < Math.abs(directDifference)) {
        resolvedEndRotation = reversedEndRotation;
      }
      const difference = Math.atan2(
        Math.sin(resolvedEndRotation - startRotation),
        Math.cos(resolvedEndRotation - startRotation),
      );
      knifeRotation = startRotation + difference * safeT;
    } else if (Number.isFinite(endRotation)) {
      knifeRotation = endRotation;
    } else if (Number.isFinite(startRotation)) {
      knifeRotation = startRotation;
    }
    return {
      x: Number(start.x) + (Number(end.x) - Number(start.x)) * safeT,
      y: Number(start.y) + (Number(end.y) - Number(start.y)) * safeT,
      pressure: clamp(
        Number(start.pressure || 1) + ((Number(end.pressure || 1) - Number(start.pressure || 1)) * safeT),
        0.01,
        1,
      ),
      knifeRotation,
    };
  }

  function strokePointsDiffer(first, second) {
    if (!first || !second) return true;
    return Math.abs(Number(first.x) - Number(second.x)) > 0.001
      || Math.abs(Number(first.y) - Number(second.y)) > 0.001
      || Math.abs(Number(first.pressure || 1) - Number(second.pressure || 1)) > 0.001;
  }

  const BRUSH_CHAIN_VERTEX_COUNT = 10;

  function initialiseBrushSnake(session, point, direction, length) {
    const safeLength = Math.max(1, Number(length) || 1);
    const safeDirection = normaliseStrokeDirection(direction, [1, 0]);
    const segmentCount = BRUSH_CHAIN_VERTEX_COUNT - 1;
    const actualSegmentLength = safeLength / segmentCount;

    // Match the reference brush: every bristle exists at its complete length
    // as soon as the brush touches the canvas. Movement then bends/follows the
    // already-extended chain instead of pulling a collapsed chain out of one dot.
    session.brushSnake = Array.from({ length: BRUSH_CHAIN_VERTEX_COUNT }, (_, index) => ({
      x: Number(point.x) - safeDirection[0] * actualSegmentLength * index,
      y: Number(point.y) - safeDirection[1] * actualSegmentLength * index,
      pressure: point.pressure,
    }));
    session.brushSnakeLength = safeLength;
    session.brushSnakeSegmentLength = actualSegmentLength;
    session.brushDirection = safeDirection;
  }

  function moveBrushSnake(session, point, direction, length) {
    const safeLength = Math.max(1, Number(length) || 1);
    const safeDirection = normaliseStrokeDirection(direction, session.brushDirection || [1, 0]);
    if (!session.brushSnake
      || Math.abs(Number(session.brushSnakeLength) - safeLength) > 0.001) {
      initialiseBrushSnake(session, point, safeDirection, safeLength);
    }

    const previous = session.brushSnake.map((node) => ({ ...node }));
    const next = session.brushSnake.map((node) => ({ ...node }));
    next[0] = cloneStrokePoint(point);
    const segmentLength = Math.max(0.25, Number(session.brushSnakeSegmentLength) || safeLength);

    // Position-based chain constraint, equivalent to the reference app's
    // distance passes: each link keeps a fixed length while retaining its
    // previous position, producing a flexible bristle rather than a rigid line.
    for (let pass = 0; pass < 3; pass += 1) {
      next[0] = cloneStrokePoint(point);
      for (let index = 1; index < next.length; index += 1) {
        const leader = next[index - 1];
        const follower = next[index];
        const deltaX = follower.x - leader.x;
        const deltaY = follower.y - leader.y;
        const distance = Math.hypot(deltaX, deltaY);
        if (distance <= 0.0001) {
          next[index] = {
            x: leader.x - safeDirection[0] * segmentLength,
            y: leader.y - safeDirection[1] * segmentLength,
            pressure: point.pressure,
          };
          continue;
        }
        next[index] = {
          x: leader.x + (deltaX / distance) * segmentLength,
          y: leader.y + (deltaY / distance) * segmentLength,
          pressure: point.pressure,
        };
      }
    }

    session.brushSnake = next;
    session.brushDirection = safeDirection;
    return { previous, next };
  }

  function getBrushBristleOffset(sourceIndex, bristleCount, brushRadius) {
    const safeCount = Math.max(1, Number(bristleCount) || 1);
    const safeRadius = Math.max(0, Number(brushRadius) || 0);
    const seed = Math.sin((sourceIndex + 1) * 12.9898) * 43758.5453;
    const seed01 = seed - Math.floor(seed);
    const radial = Math.sqrt((sourceIndex + 0.5) / safeCount);
    const angle = sourceIndex * 2.399963229728653 + seed01 * 0.9;
    const rootRadius = safeRadius * 0.88 * radial;
    return {
      x: Math.cos(angle) * rootRadius,
      y: Math.sin(angle) * rootRadius,
    };
  }

  function getSnakeNodeFrame(nodes, nodeIndex, fallbackDirection) {
    const current = nodes[nodeIndex] || nodes[0] || { x: 0, y: 0 };
    const before = nodes[Math.max(0, nodeIndex - 1)] || current;
    const after = nodes[Math.min(nodes.length - 1, nodeIndex + 1)] || current;
    let tangentX = Number(after.x) - Number(before.x);
    let tangentY = Number(after.y) - Number(before.y);
    let tangentLength = Math.hypot(tangentX, tangentY);
    if (tangentLength <= 0.0001) {
      const fallback = normaliseStrokeDirection(fallbackDirection, [1, 0]);
      tangentX = fallback[0];
      tangentY = fallback[1];
      tangentLength = 1;
    }
    tangentX /= tangentLength;
    tangentY /= tangentLength;
    return {
      tangentX,
      tangentY,
      normalX: -tangentY,
      normalY: tangentX,
    };
  }

  function offsetSnakeNode(node, frame, offset, taper = 1) {
    const localAlong = offset.x * frame.tangentX + offset.y * frame.tangentY;
    const localAcross = offset.x * frame.normalX + offset.y * frame.normalY;
    return {
      x: Number(node.x) + (frame.tangentX * localAlong + frame.normalX * localAcross) * taper,
      y: Number(node.y) + (frame.tangentY * localAlong + frame.normalY * localAcross) * taper,
      pressure: node.pressure,
    };
  }


  function drawBrushSnakeBody(layer, session, snakeNodes, direction, settings = {}, strokeOptions = {}) {
    let drew = false;
    const bristleDiameter = Math.max(0.7, Math.min(8, (Number(settings.bristleThickness) || 35) / 22));
    const bristleRadius = bristleDiameter * 0.5;
    const brushRadius = Math.max(bristleRadius, Number(settings.toolSize || strokeOptions.size) * 0.5 || bristleRadius);
    const bristleCount = Math.max(1, Math.min(250, Math.round(Number(settings.bristleCount) || 1)));
    const bodySettings = {
      ...settings,
      bristleCount: 1,
      bristleThickness: 100,
    };

    for (let bristleIndex = 0; bristleIndex < bristleCount; bristleIndex += 1) {
      const baseOffset = getBrushBristleOffset(bristleIndex, bristleCount, brushRadius);
      for (let index = 1; index < snakeNodes.length; index += 1) {
        const startNodeIndex = index - 1;
        const endNodeIndex = index;
        const startFrame = getSnakeNodeFrame(snakeNodes, startNodeIndex, direction);
        const endFrame = getSnakeNodeFrame(snakeNodes, endNodeIndex, direction);
        const startTaper = 1 - (startNodeIndex / Math.max(1, snakeNodes.length - 1)) * 0.22;
        const endTaper = 1 - (endNodeIndex / Math.max(1, snakeNodes.length - 1)) * 0.22;
        const linkStart = offsetSnakeNode(snakeNodes[startNodeIndex], startFrame, baseOffset, startTaper);
        const linkEnd = offsetSnakeNode(snakeNodes[endNodeIndex], endFrame, baseOffset, endTaper);
        drew = updateRegion(
          layer,
          session,
          linkStart,
          linkEnd,
          bodySettings,
          {
            ...strokeOptions,
            brushSnakePart: index - 1,
            brushSnakeParts: snakeNodes.length - 1,
            brushSnakeBristle: bristleIndex,
            brushSnakeBristles: bristleCount,
            brushSnakeBody: true,
            brushDirectBristle: true,
            brushSnakeRadius: bristleRadius,
          },
        ) || drew;
      }
    }

    return drew;
  }

  function drawBrushStampBody(layer, session, point, direction, settings = {}, strokeOptions = {}) {
    let drew = false;
    const bristleDiameter = Math.max(0.7, Math.min(8, (Number(settings.bristleThickness) || 35) / 22));
    const bristleRadius = bristleDiameter * 0.5;
    const brushRadius = Math.max(bristleRadius, Number(settings.toolSize || strokeOptions.size) * 0.5 || bristleRadius);
    const bristleCount = Math.max(1, Math.min(250, Math.round(Number(settings.bristleCount) || 1)));
    const bristleLength = Math.max(1, Number(settings.bristleLength) || 40);
    const safeDirection = normaliseStrokeDirection(direction, [1, 0]);
    const bodySettings = {
      ...settings,
      bristleCount: 1,
      bristleThickness: 100,
    };

    for (let bristleIndex = 0; bristleIndex < bristleCount; bristleIndex += 1) {
      const baseOffset = getBrushBristleOffset(bristleIndex, bristleCount, brushRadius);
      const startPoint = {
        x: Number(point.x) + baseOffset.x - safeDirection[0] * bristleLength,
        y: Number(point.y) + baseOffset.y - safeDirection[1] * bristleLength,
        pressure: point.pressure,
      };
      const endPoint = {
        x: Number(point.x) + baseOffset.x,
        y: Number(point.y) + baseOffset.y,
        pressure: point.pressure,
      };
      drew = updateRegion(
        layer,
        session,
        startPoint,
        endPoint,
        bodySettings,
        {
          ...strokeOptions,
          brushSnakeStamp: true,
          brushSnakeBody: true,
          brushSnakePart: 0,
          brushSnakeParts: 1,
          brushSnakeBristle: bristleIndex,
          brushSnakeBristles: bristleCount,
          brushSnakeRadius: bristleRadius,
        },
      ) || drew;
    }

    return drew;
  }

  function drawCursorBristlesExactly(layer, session, bristles, settings = {}, strokeOptions = {}) {
    if (!Array.isArray(bristles) || !bristles.length) return false;

    const bristleDiameter = Math.max(0.7, Math.min(8, (Number(settings.bristleThickness) || 35) / 22));
    const bristleRadius = bristleDiameter * 0.5;
    const sideDepositWidth = 2;
    const sideDepositRadius = sideDepositWidth * 0.5;
    const sideDepositOffset = bristleRadius + sideDepositRadius;
    const bodyVertices = [];
    const sideVertices = [];
    let minimumX = width;
    let minimumY = height;
    let maximumX = 0;
    let maximumY = 0;

    const recordBounds = (point, radiusValue) => {
      minimumX = Math.min(minimumX, point.x - radiusValue);
      minimumY = Math.min(minimumY, point.y - radiusValue);
      maximumX = Math.max(maximumX, point.x + radiusValue);
      maximumY = Math.max(maximumY, point.y + radiusValue);
    };

    const pushTriangle = (targetVertices, a, b, c) => {
      targetVertices.push(a.x, a.y, b.x, b.y, c.x, c.y);
    };

    const pushCircle = (targetVertices, centre, circleRadius) => {
      const sides = 8;
      for (let index = 0; index < sides; index += 1) {
        const firstAngle = (index / sides) * Math.PI * 2;
        const secondAngle = ((index + 1) / sides) * Math.PI * 2;
        pushTriangle(
          targetVertices,
          centre,
          { x: centre.x + Math.cos(firstAngle) * circleRadius, y: centre.y + Math.sin(firstAngle) * circleRadius },
          { x: centre.x + Math.cos(secondAngle) * circleRadius, y: centre.y + Math.sin(secondAngle) * circleRadius },
        );
      }
      recordBounds(centre, circleRadius);
    };

    const pushPolylineRibbon = (
      targetVertices,
      sourceNodes,
      ribbonRadius,
      offsetSign = 0,
      offsetDistance = 0,
    ) => {
      const cleanNodes = (Array.isArray(sourceNodes) ? sourceNodes : [])
        .map((node) => ({ x: Number(node?.x) || 0, y: Number(node?.y) || 0 }))
        .filter((node, index, nodes) => index === 0 || Math.hypot(
          node.x - nodes[index - 1].x,
          node.y - nodes[index - 1].y,
        ) > 0.0001);
      if (cleanNodes.length < 2) return;

      const leftPoints = [];
      const rightPoints = [];

      for (let nodeIndex = 0; nodeIndex < cleanNodes.length; nodeIndex += 1) {
        const previousNode = cleanNodes[Math.max(0, nodeIndex - 1)];
        const currentNode = cleanNodes[nodeIndex];
        const nextNode = cleanNodes[Math.min(cleanNodes.length - 1, nodeIndex + 1)];
        let tangentX = nextNode.x - previousNode.x;
        let tangentY = nextNode.y - previousNode.y;
        let tangentLength = Math.hypot(tangentX, tangentY);
        if (tangentLength <= 0.0001) {
          tangentX = 1;
          tangentY = 0;
          tangentLength = 1;
        }
        tangentX /= tangentLength;
        tangentY /= tangentLength;
        const normalX = -tangentY;
        const normalY = tangentX;
        const shiftedX = currentNode.x + normalX * offsetDistance * offsetSign;
        const shiftedY = currentNode.y + normalY * offsetDistance * offsetSign;
        leftPoints.push({
          x: shiftedX + normalX * ribbonRadius,
          y: shiftedY + normalY * ribbonRadius,
        });
        rightPoints.push({
          x: shiftedX - normalX * ribbonRadius,
          y: shiftedY - normalY * ribbonRadius,
        });
        recordBounds({ x: shiftedX, y: shiftedY }, ribbonRadius);
      }

      for (let nodeIndex = 1; nodeIndex < cleanNodes.length; nodeIndex += 1) {
        const previousLeft = leftPoints[nodeIndex - 1];
        const previousRight = rightPoints[nodeIndex - 1];
        const currentLeft = leftPoints[nodeIndex];
        const currentRight = rightPoints[nodeIndex];
        pushTriangle(targetVertices, previousLeft, previousRight, currentRight);
        pushTriangle(targetVertices, previousLeft, currentRight, currentLeft);
      }

      pushCircle(targetVertices, {
        x: (leftPoints[0].x + rightPoints[0].x) * 0.5,
        y: (leftPoints[0].y + rightPoints[0].y) * 0.5,
      }, ribbonRadius);
      const lastIndex = leftPoints.length - 1;
      pushCircle(targetVertices, {
        x: (leftPoints[lastIndex].x + rightPoints[lastIndex].x) * 0.5,
        y: (leftPoints[lastIndex].y + rightPoints[lastIndex].y) * 0.5,
      }, ribbonRadius);
    };

    bristles.forEach((bristle) => {
      const nodes = Array.isArray(bristle?.nodes) ? bristle.nodes : [];
      pushPolylineRibbon(bodyVertices, nodes, bristleRadius, 0, 0);
      if (settings.sideDeposits !== false) {
        pushPolylineRibbon(sideVertices, nodes, sideDepositRadius, 1, sideDepositOffset);
        pushPolylineRibbon(sideVertices, nodes, sideDepositRadius, -1, sideDepositOffset);
      }
    });

    if (!bodyVertices.length) return false;
    const sideDepositPadding = settings.sideDeposits === false ? 2 : 14;
    const bounds = {
      minimumX: Math.max(0, Math.floor(minimumX - sideDepositPadding)),
      minimumY: Math.max(0, Math.floor(minimumY - sideDepositPadding)),
      maximumX: Math.min(width, Math.ceil(maximumX + sideDepositPadding)),
      maximumY: Math.min(height, Math.ceil(maximumY + sideDepositPadding)),
    };
    if (bounds.maximumX <= bounds.minimumX || bounds.maximumY <= bounds.minimumY) return false;

    const renderMaskGeometry = (target, sourceVertices) => {
      gl.bindFramebuffer(gl.FRAMEBUFFER, target.framebuffer);
      gl.viewport(0, 0, width, height);
      gl.enable(gl.SCISSOR_TEST);
      gl.scissor(bounds.minimumX, Math.max(0, height - bounds.maximumY), bounds.maximumX - bounds.minimumX, bounds.maximumY - bounds.minimumY);
      gl.disable(gl.BLEND);
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.useProgram(brushMaskProgram);
      gl.bindBuffer(gl.ARRAY_BUFFER, brushMaskBuffer);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(sourceVertices), gl.STREAM_DRAW);
      const maskPosition = gl.getAttribLocation(brushMaskProgram, 'a_position');
      gl.enableVertexAttribArray(maskPosition);
      gl.vertexAttribPointer(maskPosition, 2, gl.FLOAT, false, 0, 0);
      gl.uniform2f(gl.getUniformLocation(brushMaskProgram, 'u_resolution'), width, height);
      gl.drawArrays(gl.TRIANGLES, 0, sourceVertices.length / 2);
      gl.disableVertexAttribArray(maskPosition);
      gl.disable(gl.SCISSOR_TEST);
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    };

    const bodyMaskTarget = ensureBrushMaskTarget();
    renderMaskGeometry(bodyMaskTarget, bodyVertices);

    const bodySettings = {
      ...settings,
      bristleCount: 1,
      bristleThickness: 100,
      sideDeposits: false,
    };

    const getSegmentEndpointsFromBristles = (sourceBristles, fallbackStart, fallbackEnd) => {
      let startX = 0;
      let startY = 0;
      let endX = 0;
      let endY = 0;
      let count = 0;
      (Array.isArray(sourceBristles) ? sourceBristles : []).forEach((bristle) => {
        const nodes = Array.isArray(bristle?.nodes) ? bristle.nodes : [];
        if (!nodes.length) return;
        const firstNode = nodes[0] || {};
        const lastNode = nodes[nodes.length - 1] || {};
        const firstX = Number(firstNode.x);
        const firstY = Number(firstNode.y);
        const lastX = Number(lastNode.x);
        const lastY = Number(lastNode.y);
        if (!Number.isFinite(firstX) || !Number.isFinite(firstY) || !Number.isFinite(lastX) || !Number.isFinite(lastY)) return;
        startX += firstX;
        startY += firstY;
        endX += lastX;
        endY += lastY;
        count += 1;
      });
      if (count <= 0) {
        return {
          start: { ...fallbackStart },
          end: { ...fallbackEnd },
        };
      }
      return {
        start: {
          x: startX / count,
          y: startY / count,
          pressure: Number(session?.lastPoint?.pressure) || 1,
        },
        end: {
          x: endX / count,
          y: endY / count,
          pressure: Number(session?.lastPoint?.pressure) || 1,
        },
      };
    };

    const fallbackCentre = {
      x: (bounds.minimumX + bounds.maximumX) * 0.5,
      y: (bounds.minimumY + bounds.maximumY) * 0.5,
      pressure: Number(session?.lastPoint?.pressure) || 1,
    };
    const segment = getSegmentEndpointsFromBristles(bristles, fallbackCentre, fallbackCentre);
    const movementLength = Math.hypot(
      segment.end.x - segment.start.x,
      segment.end.y - segment.start.y,
    );

    let drew = false;
    if (settings.sideDeposits !== false && sideVertices.length) {
      const sideMaskTarget = ensureBrushSideMaskTarget();
      renderMaskGeometry(sideMaskTarget, sideVertices);
      drew = updateRegion(layer, session, segment.start, segment.end, bodySettings, {
        ...strokeOptions,
        brushSnakeStamp: movementLength <= 0.0001,
        brushSnakeBody: true,
        brushMaskTexture: sideMaskTarget.texture,
        brushMaskBounds: bounds,
        brushMaskSidePass: true,
        brushSnakeRadius: bristleRadius,
      }) || drew;
    }

    drew = updateRegion(layer, session, segment.start, segment.end, bodySettings, {
      ...strokeOptions,
      brushSnakeStamp: movementLength <= 0.0001,
      brushSnakeBody: true,
      brushMaskTexture: bodyMaskTarget.texture,
      brushMaskBounds: bounds,
      brushSnakeRadius: bristleRadius,
    }) || drew;
    return drew;
  }

  function drawGpuBrushSweep(layer, session, previousPoint, currentPoint, settings = {}, strokeOptions = {}) {
    if (!previousPoint || !currentPoint) return false;
    const bristleDiameter = Math.max(0.7, Math.min(8, (Number(settings.bristleThickness) || 35) / 22));
    const bristleRadius = bristleDiameter * 0.5;
    const brushRadius = Math.max(bristleRadius, Number(settings.toolSize || strokeOptions.size) * 0.5 || bristleRadius);
    const bristleLength = Math.max(1, Number(settings.bristleLength) || 40);
    const bristleCount = Math.max(1, Math.min(250, Math.round(Number(settings.bristleCount) || 1)));
    const previousDirection = normaliseStrokeDirection(
      session?.previousBrushDirection || session?.brushDirection || strokeOptions.paintBrushDirection,
      [1, 0],
    );
    const movementX = Number(currentPoint.x) - Number(previousPoint.x);
    const movementY = Number(currentPoint.y) - Number(previousPoint.y);
    const movementLength = Math.hypot(movementX, movementY);
    const currentDirection = movementLength > 0.15
      ? [movementX / movementLength, movementY / movementLength]
      : normaliseStrokeDirection(strokeOptions.paintBrushDirection || session?.brushDirection, previousDirection);
    const padding = brushRadius + bristleLength + bristleRadius + 3;
    const bounds = {
      minimumX: Math.max(0, Math.floor(Math.min(previousPoint.x, currentPoint.x) - padding)),
      minimumY: Math.max(0, Math.floor(Math.min(previousPoint.y, currentPoint.y) - padding)),
      maximumX: Math.min(width, Math.ceil(Math.max(previousPoint.x, currentPoint.x) + padding)),
      maximumY: Math.min(height, Math.ceil(Math.max(previousPoint.y, currentPoint.y) + padding)),
    };
    if (bounds.maximumX <= bounds.minimumX || bounds.maximumY <= bounds.minimumY) return false;

    const maskTarget = ensureBrushMaskTarget();
    const vertices = buildDocumentVertices(bounds);
    gl.bindFramebuffer(gl.FRAMEBUFFER, maskTarget.framebuffer);
    gl.viewport(0, 0, width, height);
    gl.enable(gl.SCISSOR_TEST);
    gl.scissor(bounds.minimumX, Math.max(0, height - bounds.maximumY), bounds.maximumX - bounds.minimumX, bounds.maximumY - bounds.minimumY);
    gl.disable(gl.BLEND);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.useProgram(brushMaskProgram);
    gl.bindBuffer(gl.ARRAY_BUFFER, documentBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STREAM_DRAW);
    const maskPosition = gl.getAttribLocation(brushMaskProgram, 'a_position');
    gl.enableVertexAttribArray(maskPosition);
    gl.vertexAttribPointer(maskPosition, 2, gl.FLOAT, false, 0, 0);
    gl.uniform2f(gl.getUniformLocation(brushMaskProgram, 'u_resolution'), width, height);
    gl.uniform2f(gl.getUniformLocation(brushMaskProgram, 'u_previousPoint'), Number(previousPoint.x) || 0, Number(previousPoint.y) || 0);
    gl.uniform2f(gl.getUniformLocation(brushMaskProgram, 'u_currentPoint'), Number(currentPoint.x) || 0, Number(currentPoint.y) || 0);
    gl.uniform2f(gl.getUniformLocation(brushMaskProgram, 'u_previousDirection'), previousDirection[0], previousDirection[1]);
    gl.uniform2f(gl.getUniformLocation(brushMaskProgram, 'u_currentDirection'), currentDirection[0], currentDirection[1]);
    gl.uniform1f(gl.getUniformLocation(brushMaskProgram, 'u_brushRadius'), brushRadius);
    gl.uniform1f(gl.getUniformLocation(brushMaskProgram, 'u_bristleRadius'), bristleRadius);
    gl.uniform1f(gl.getUniformLocation(brushMaskProgram, 'u_bristleLength'), bristleLength);
    gl.uniform1f(gl.getUniformLocation(brushMaskProgram, 'u_bristleCount'), bristleCount);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
    gl.disableVertexAttribArray(maskPosition);
    gl.disable(gl.SCISSOR_TEST);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);

    if (session) {
      session.previousBrushDirection = currentDirection;
      session.brushDirection = currentDirection;
    }
    const bodySettings = { ...settings, bristleCount: 1, bristleThickness: 100 };
    const maskCentre = {
      x: (bounds.minimumX + bounds.maximumX) * 0.5,
      y: (bounds.minimumY + bounds.maximumY) * 0.5,
      pressure: Number(currentPoint.pressure) || 1,
    };
    const drew = updateRegion(layer, session, maskCentre, maskCentre, bodySettings, {
      ...strokeOptions,
      brushSnakeStamp: movementLength <= 0.0001,
      brushSnakeBody: true,
      brushMaskTexture: maskTarget.texture,
      brushMaskBounds: bounds,
      brushSnakeRadius: bristleRadius,
    });
    return drew;
  }

  function stampBrushSnakeAtPoint(layer, session, point, settings = {}, strokeOptions = {}) {
    const bristleLength = Math.max(1, Number(settings.bristleLength) || 40);
    const safeDirection = normaliseStrokeDirection(session && session.brushDirection, [1, 0]);

    if (session && Array.isArray(session.brushCursorBristles) && session.brushCursorBristles.length) {
      session.lastPoint = cloneStrokePoint(point);
      initialiseBrushSnake(session, point, safeDirection, bristleLength);
      return drawCursorBristlesExactly(
        layer,
        session,
        session.brushCursorBristles,
        settings,
        {
          ...strokeOptions,
          brushSnakeStamp: true,
        },
      );
    }

    initialiseBrushSnake(session, point, safeDirection, bristleLength);
    if (session) {
      session.lastPoint = cloneStrokePoint(point);
    }

    return drawBrushSnakeBody(
      layer,
      session,
      session.brushSnake,
      safeDirection,
      settings,
      {
        ...strokeOptions,
        brushSnakeStamp: true,
      },
    );
  }

  function drawBrushSnakeStep(layer, session, previousTip, nextTip, settings = {}, strokeOptions = {}) {
    const deltaX = Number(nextTip.x) - Number(previousTip.x);
    const deltaY = Number(nextTip.y) - Number(previousTip.y);
    const direction = [deltaX, deltaY];
    const bristleLength = settings.bristleLength || 40;

    if (!session.brushSnake) {
      initialiseBrushSnake(session, previousTip, direction, bristleLength);
    }

    const snake = moveBrushSnake(session, nextTip, direction, bristleLength);
    return drawBrushSnakeBody(layer, session, snake.next, direction, settings, strokeOptions);
  }

  function drawInterpolatedSegment(layer, session, start, end, settings = {}, strokeOptions = {}) {
    const tool = String(settings.tool || 'tube').toLowerCase();
    const safeStart = cloneStrokePoint(start);
    const safeEnd = cloneStrokePoint(end);
    const configuredRadius = Number(settings.toolSize || strokeOptions.size) * 0.5 || 2;
    const brushSnakeRadius = Number(strokeOptions && strokeOptions.brushSnakeRadius);
    const radius = strokeOptions && strokeOptions.brushSnakeBody === true
      ? Math.max(0.35, Number.isFinite(brushSnakeRadius) ? brushSnakeRadius : 0.75)
      : Math.max(2, configuredRadius);
    const deltaX = safeEnd.x - safeStart.x;
    const deltaY = safeEnd.y - safeStart.y;
    const distance = Math.hypot(deltaX, deltaY);

    // A brush stroke begins with a pointer-down point before there is any real
    // direction. Do not initialise the bristle chains from the default [1, 0]
    // direction, otherwise the first actual movement drags a horizontal row of
    // bristles into the intended stroke direction.
    if (tool === 'brush' && distance <= 0.0001) return false;

    const spacing = tool === 'brush'
      ? Math.max(1.25, Math.min(4.5, radius * 0.14))
      : tool === 'knife'
        ? Math.min(2.25, Math.max(0.75, radius * 0.055))
        : Math.max(0.75, radius * 0.45);
    const steps = Math.max(1, Math.ceil(distance / Math.max(0.0001, spacing)));
    let drew = false;
    let previousPoint = safeStart;
    for (let stepIndex = 1; stepIndex <= steps; stepIndex += 1) {
      const nextPoint = steps === 1
        ? safeEnd
        : interpolateStrokePoint(safeStart, safeEnd, stepIndex / steps);
      drew = tool === 'brush'
        ? drawBrushSnakeStep(layer, session, previousPoint, nextPoint, settings, strokeOptions) || drew
        : updateRegion(layer, session, previousPoint, nextPoint, settings, strokeOptions) || drew;
      previousPoint = nextPoint;
    }
    return drew;
  }

  function drawMassGather(layer, flowTarget, bounds, selection) {
    const scratchTarget = ensureScratch();
    const boundsUv = [
      bounds.minimumX / width,
      1 - bounds.maximumY / height,
      bounds.maximumX / width,
      1 - bounds.minimumY / height,
    ];

    gl.bindFramebuffer(gl.FRAMEBUFFER, scratchTarget.framebuffer);
    gl.viewport(0, 0, width, height);
    applyScissor(bounds);
    gl.disable(gl.BLEND);
    gl.useProgram(massGatherProgram);
    const position = bindQuad(massGatherProgram);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, layer.wet.texture);
    gl.uniform1i(gl.getUniformLocation(massGatherProgram, 'u_wet'), 0);
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, layer.dry.texture);
    gl.uniform1i(gl.getUniformLocation(massGatherProgram, 'u_dry'), 1);
    gl.activeTexture(gl.TEXTURE2);
    gl.bindTexture(gl.TEXTURE_2D, flowTarget.texture);
    gl.uniform1i(gl.getUniformLocation(massGatherProgram, 'u_flow'), 2);
    gl.activeTexture(gl.TEXTURE3);
    gl.bindTexture(gl.TEXTURE_2D, selection.texture);
    gl.uniform1i(gl.getUniformLocation(massGatherProgram, 'u_selectionMask'), 3);
    gl.uniform2f(gl.getUniformLocation(massGatherProgram, 'u_pixelStep'), 1 / width, 1 / height);
    gl.uniform4f(
      gl.getUniformLocation(massGatherProgram, 'u_bounds'),
      boundsUv[0],
      boundsUv[1],
      boundsUv[2],
      boundsUv[3],
    );
    gl.uniform1f(
      gl.getUniformLocation(massGatherProgram, 'u_selectionEnabled'),
      selection.enabled && selection.texture ? 1 : 0,
    );
    gl.drawArrays(gl.TRIANGLES, 0, 6);
    gl.disableVertexAttribArray(position);
    gl.disable(gl.SCISSOR_TEST);
    copyScratchRegionToTarget(layer.wet, bounds);
  }

  function applyBrushTransport(
    layer,
    bounds,
    start,
    end,
    radius,
    settings,
    strokeOptions,
    session,
    selection,
    pressure,
    speed,
  ) {
    if (!layer || !bounds) return false;
    const liquid = clamp(Number(settings && settings.liquid) / 100, 0, 1);
    const cohesion = clamp(Number(settings && settings.stickiness) / 100, 0, 1);
    const simulationBounds = expandPaintBounds(bounds, Math.max(4, radius * 0.28 + 3));
    if (simulationBounds.maximumX <= simulationBounds.minimumX
      || simulationBounds.maximumY <= simulationBounds.minimumY) return false;

    const deltaX = Number(end.x) - Number(start.x);
    const deltaY = Number(end.y) - Number(start.y);
    const distance = Math.hypot(deltaX, deltaY);
    const currentDirection = distance > 0.0001
      ? [deltaX / distance, deltaY / distance]
      : Array.isArray(session && session.brushDirection)
        ? session.brushDirection
        : [1, 0];
    const previousDirection = Array.isArray(session && session.brushDirection)
      ? session.brushDirection
      : currentDirection;
    const flowTarget = ensurePaintFlowTarget();
    const vertices = buildDocumentVertices(simulationBounds);
    const iterations = liquid >= 0.72 ? 3 : 2;

    for (let iteration = 0; iteration < iterations; iteration += 1) {
      gl.bindFramebuffer(gl.FRAMEBUFFER, flowTarget.framebuffer);
      gl.viewport(0, 0, width, height);
      applyScissor(simulationBounds);
      gl.disable(gl.BLEND);
      gl.useProgram(brushFlowProgram);
      gl.bindBuffer(gl.ARRAY_BUFFER, documentBuffer);
      gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STREAM_DRAW);
      const position = gl.getAttribLocation(brushFlowProgram, 'a_position');
      gl.enableVertexAttribArray(position);
      gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);
      gl.uniform2f(gl.getUniformLocation(brushFlowProgram, 'u_resolution'), width, height);
      gl.uniform2f(gl.getUniformLocation(brushFlowProgram, 'u_start'), Number(start.x) || 0, Number(start.y) || 0);
      gl.uniform2f(gl.getUniformLocation(brushFlowProgram, 'u_end'), Number(end.x) || 0, Number(end.y) || 0);
      gl.uniform2f(
        gl.getUniformLocation(brushFlowProgram, 'u_previousDirection'),
        Number(previousDirection[0]) || 0,
        Number(previousDirection[1]) || 0,
      );
      gl.uniform1f(gl.getUniformLocation(brushFlowProgram, 'u_radius'), radius);
      gl.uniform1f(gl.getUniformLocation(brushFlowProgram, 'u_pressure'), pressure);
      gl.uniform1f(gl.getUniformLocation(brushFlowProgram, 'u_bristleCount'), clamp(settings.bristleCount || 32, 1, 250));
      gl.uniform1f(gl.getUniformLocation(brushFlowProgram, 'u_bristleThickness'), clamp(Number(settings.bristleThickness) / 100, 0.01, 1));
      gl.uniform1f(gl.getUniformLocation(brushFlowProgram, 'u_liquid'), liquid);
      gl.uniform1f(gl.getUniformLocation(brushFlowProgram, 'u_cohesion'), cohesion);
      gl.uniform1f(gl.getUniformLocation(brushFlowProgram, 'u_speed'), speed);
      gl.uniform1f(gl.getUniformLocation(brushFlowProgram, 'u_seed'), Number(strokeOptions.strokeSeed || strokeOptions.seed || 1));
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, layer.wet.texture);
      gl.uniform1i(gl.getUniformLocation(brushFlowProgram, 'u_wet'), 0);
      gl.activeTexture(gl.TEXTURE1);
      gl.bindTexture(gl.TEXTURE_2D, layer.dry.texture);
      gl.uniform1i(gl.getUniformLocation(brushFlowProgram, 'u_dry'), 1);
      gl.activeTexture(gl.TEXTURE2);
      gl.bindTexture(gl.TEXTURE_2D, selection.texture);
      gl.uniform1i(gl.getUniformLocation(brushFlowProgram, 'u_selectionMask'), 2);
      gl.uniform1f(
        gl.getUniformLocation(brushFlowProgram, 'u_selectionEnabled'),
        selection.enabled && selection.texture ? 1 : 0,
      );
      gl.drawArrays(gl.TRIANGLES, 0, 6);
      gl.disableVertexAttribArray(position);
      gl.disable(gl.SCISSOR_TEST);
      drawMassGather(layer, flowTarget, simulationBounds, selection);
    }

    if (session) session.brushDirection = currentDirection;
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.activeTexture(gl.TEXTURE0);
    return true;
  }

  function applyLivePaintRelaxation(layer, bounds, settings, selection) {
    const liquid = clamp(Number(settings && settings.liquid) / 100, 0, 1);
    const cohesion = clamp(Number(settings && settings.stickiness) / 100, 0, 1);
    if (liquid <= 0.0001 && cohesion <= 0.0001) return false;

    // Wet paint must settle while it is being deposited, not as a delayed
    // conversion when the pointer is released. Repeated live updates provide
    // the continuing simulation; a stationary dab still gets enough passes to
    // visibly respond to the liquid setting immediately.
    const livePasses = liquid > 0.0001
      ? Math.max(2, Math.round(2 + Math.pow(liquid, 1.25) * 10))
      : 1;

    return applyPaintRelaxation(layer, bounds, settings, selection, livePasses);
  }

  function applyPaintRelaxation(layer, bounds, settings, selection, iterationOverride = null) {
    const liquid = clamp(Number(settings && settings.liquid) / 100, 0, 1);
    const cohesion = clamp(Number(settings && settings.stickiness) / 100, 0, 1);
    if (!layer || !bounds || (liquid <= 0.0001 && cohesion <= 0.0001)) return false;
    const simulationBounds = expandPaintBounds(bounds, 4 + liquid * 3);
    if (simulationBounds.maximumX <= simulationBounds.minimumX
      || simulationBounds.maximumY <= simulationBounds.minimumY) return false;

    const flowTarget = ensurePaintFlowTarget();
    const boundsUv = [
      simulationBounds.minimumX / width,
      1 - simulationBounds.maximumY / height,
      simulationBounds.maximumX / width,
      1 - simulationBounds.minimumY / height,
    ];
    const iterations = Number.isFinite(Number(iterationOverride))
      ? Math.max(1, Math.min(40, Math.round(Number(iterationOverride))))
      : (liquid >= 0.88 ? 6 : (liquid >= 0.62 ? 4 : (liquid >= 0.28 ? 2 : 1)));

    for (let iteration = 0; iteration < iterations; iteration += 1) {
      gl.bindFramebuffer(gl.FRAMEBUFFER, flowTarget.framebuffer);
      gl.viewport(0, 0, width, height);
      applyScissor(simulationBounds);
      gl.disable(gl.BLEND);
      gl.useProgram(paintRelaxFlowProgram);
      const position = bindQuad(paintRelaxFlowProgram);
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, layer.wet.texture);
      gl.uniform1i(gl.getUniformLocation(paintRelaxFlowProgram, 'u_wet'), 0);
      gl.activeTexture(gl.TEXTURE1);
      gl.bindTexture(gl.TEXTURE_2D, layer.dry.texture);
      gl.uniform1i(gl.getUniformLocation(paintRelaxFlowProgram, 'u_dry'), 1);
      gl.activeTexture(gl.TEXTURE2);
      gl.bindTexture(gl.TEXTURE_2D, selection.texture);
      gl.uniform1i(gl.getUniformLocation(paintRelaxFlowProgram, 'u_selectionMask'), 2);
      gl.uniform2f(gl.getUniformLocation(paintRelaxFlowProgram, 'u_pixelStep'), 1 / width, 1 / height);
      gl.uniform4f(
        gl.getUniformLocation(paintRelaxFlowProgram, 'u_bounds'),
        boundsUv[0],
        boundsUv[1],
        boundsUv[2],
        boundsUv[3],
      );
      gl.uniform1f(gl.getUniformLocation(paintRelaxFlowProgram, 'u_liquid'), liquid);
      gl.uniform1f(gl.getUniformLocation(paintRelaxFlowProgram, 'u_cohesion'), cohesion);
      gl.uniform1f(
        gl.getUniformLocation(paintRelaxFlowProgram, 'u_selectionEnabled'),
        selection.enabled && selection.texture ? 1 : 0,
      );
      gl.drawArrays(gl.TRIANGLES, 0, 6);
      gl.disableVertexAttribArray(position);
      gl.disable(gl.SCISSOR_TEST);
      drawMassGather(layer, flowTarget, simulationBounds, selection);
    }

    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.activeTexture(gl.TEXTURE0);
    return true;
  }

  function applyStickiness(layer, bounds, settings, selection, maximumIterations = 3, focus = null) {
    const strength = clamp(Number(settings && settings.stickiness) / 100, 0, 1);
    if (!layer || strength <= 0.0001 || !bounds) return false;

    const searchDistance = 5 + strength * 10;
    const iterations = Math.max(
      1,
      Math.min(
        Math.max(1, Math.floor(Number(maximumIterations) || 1)),
        strength >= 0.92 ? 2 : 1,
      ),
    );
    const simulationBounds = expandPaintBounds(bounds, searchDistance + iterations + 2);
    if (simulationBounds.maximumX <= simulationBounds.minimumX
      || simulationBounds.maximumY <= simulationBounds.minimumY) return false;

    const flowTarget = ensureStickinessFlowTarget();
    const scratchTarget = ensureScratch();
    const boundsUv = [
      simulationBounds.minimumX / width,
      1 - simulationBounds.maximumY / height,
      simulationBounds.maximumX / width,
      1 - simulationBounds.minimumY / height,
    ];

    for (let iteration = 0; iteration < iterations; iteration += 1) {
      gl.bindFramebuffer(gl.FRAMEBUFFER, flowTarget.framebuffer);
      gl.viewport(0, 0, width, height);
      applyScissor(simulationBounds);
      gl.disable(gl.BLEND);
      gl.useProgram(stickinessFlowProgram);
      let position = bindQuad(stickinessFlowProgram);
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, layer.wet.texture);
      gl.uniform1i(gl.getUniformLocation(stickinessFlowProgram, 'u_wet'), 0);
      gl.activeTexture(gl.TEXTURE1);
      gl.bindTexture(gl.TEXTURE_2D, layer.dry.texture);
      gl.uniform1i(gl.getUniformLocation(stickinessFlowProgram, 'u_dry'), 1);
      gl.activeTexture(gl.TEXTURE2);
      gl.bindTexture(gl.TEXTURE_2D, selection.texture);
      gl.uniform1i(gl.getUniformLocation(stickinessFlowProgram, 'u_selectionMask'), 2);
      gl.uniform2f(gl.getUniformLocation(stickinessFlowProgram, 'u_pixelStep'), 1 / width, 1 / height);
      gl.uniform4f(
        gl.getUniformLocation(stickinessFlowProgram, 'u_bounds'),
        boundsUv[0],
        boundsUv[1],
        boundsUv[2],
        boundsUv[3],
      );
      gl.uniform1f(gl.getUniformLocation(stickinessFlowProgram, 'u_strength'), strength);
      gl.uniform1f(
        gl.getUniformLocation(stickinessFlowProgram, 'u_selectionEnabled'),
        selection.enabled && selection.texture ? 1 : 0,
      );
      gl.uniform2f(
        gl.getUniformLocation(stickinessFlowProgram, 'u_focusCenter'),
        focus ? focus.centerX / width : 0.5,
        focus ? 1 - focus.centerY / height : 0.5,
      );
      gl.uniform2f(
        gl.getUniformLocation(stickinessFlowProgram, 'u_focusRadius'),
        focus ? focus.radiusX / width : 1,
        focus ? focus.radiusY / height : 1,
      );
      gl.uniform1f(
        gl.getUniformLocation(stickinessFlowProgram, 'u_focusEnabled'),
        focus ? 1 : 0,
      );
      gl.drawArrays(gl.TRIANGLES, 0, 6);
      gl.disableVertexAttribArray(position);
      gl.disable(gl.SCISSOR_TEST);

      gl.bindFramebuffer(gl.FRAMEBUFFER, scratchTarget.framebuffer);
      gl.viewport(0, 0, width, height);
      applyScissor(simulationBounds);
      gl.disable(gl.BLEND);
      gl.useProgram(stickinessGatherProgram);
      position = bindQuad(stickinessGatherProgram);
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, layer.wet.texture);
      gl.uniform1i(gl.getUniformLocation(stickinessGatherProgram, 'u_wet'), 0);
      gl.activeTexture(gl.TEXTURE1);
      gl.bindTexture(gl.TEXTURE_2D, layer.dry.texture);
      gl.uniform1i(gl.getUniformLocation(stickinessGatherProgram, 'u_dry'), 1);
      gl.activeTexture(gl.TEXTURE2);
      gl.bindTexture(gl.TEXTURE_2D, flowTarget.texture);
      gl.uniform1i(gl.getUniformLocation(stickinessGatherProgram, 'u_flow'), 2);
      gl.activeTexture(gl.TEXTURE3);
      gl.bindTexture(gl.TEXTURE_2D, selection.texture);
      gl.uniform1i(gl.getUniformLocation(stickinessGatherProgram, 'u_selectionMask'), 3);
      gl.uniform2f(gl.getUniformLocation(stickinessGatherProgram, 'u_pixelStep'), 1 / width, 1 / height);
      gl.uniform4f(
        gl.getUniformLocation(stickinessGatherProgram, 'u_bounds'),
        boundsUv[0],
        boundsUv[1],
        boundsUv[2],
        boundsUv[3],
      );
      gl.uniform1f(gl.getUniformLocation(stickinessGatherProgram, 'u_strength'), strength);
      gl.uniform1f(
        gl.getUniformLocation(stickinessGatherProgram, 'u_selectionEnabled'),
        selection.enabled && selection.texture ? 1 : 0,
      );
      gl.uniform2f(
        gl.getUniformLocation(stickinessGatherProgram, 'u_focusCenter'),
        focus ? focus.centerX / width : 0.5,
        focus ? 1 - focus.centerY / height : 0.5,
      );
      gl.uniform2f(
        gl.getUniformLocation(stickinessGatherProgram, 'u_focusRadius'),
        focus ? focus.radiusX / width : 1,
        focus ? focus.radiusY / height : 1,
      );
      gl.uniform1f(
        gl.getUniformLocation(stickinessGatherProgram, 'u_focusEnabled'),
        focus ? 1 : 0,
      );
      gl.drawArrays(gl.TRIANGLES, 0, 6);
      gl.disableVertexAttribArray(position);
      gl.disable(gl.SCISSOR_TEST);
      copyScratchRegionToTarget(layer.wet, simulationBounds);
    }

    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.activeTexture(gl.TEXTURE0);
    return true;
  }

  function updateKnifeSweepMask(session, start, end, startDirection, endDirection, radius, bounds, selection) {
    if (!session?.knifeSweep) return;
    const scratchTarget = ensureScratch();
    const vertices = buildDocumentVertices(bounds);

    gl.bindFramebuffer(gl.FRAMEBUFFER, scratchTarget.framebuffer);
    gl.viewport(0, 0, width, height);
    applyScissor(bounds);
    gl.disable(gl.BLEND);
    gl.useProgram(knifeSweepMaskProgram);
    gl.bindBuffer(gl.ARRAY_BUFFER, documentBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STREAM_DRAW);
    const position = gl.getAttribLocation(knifeSweepMaskProgram, 'a_position');
    gl.enableVertexAttribArray(position);
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);
    gl.uniform2f(gl.getUniformLocation(knifeSweepMaskProgram, 'u_resolution'), width, height);
    gl.uniform2f(gl.getUniformLocation(knifeSweepMaskProgram, 'u_start'), Number(start.x) || 0, Number(start.y) || 0);
    gl.uniform2f(gl.getUniformLocation(knifeSweepMaskProgram, 'u_end'), Number(end.x) || 0, Number(end.y) || 0);
    gl.uniform2f(
      gl.getUniformLocation(knifeSweepMaskProgram, 'u_startDirection'),
      Number(startDirection && startDirection[0]) || 0,
      Number(startDirection && startDirection[1]) || 0,
    );
    gl.uniform2f(
      gl.getUniformLocation(knifeSweepMaskProgram, 'u_endDirection'),
      Number(endDirection && endDirection[0]) || 0,
      Number(endDirection && endDirection[1]) || 0,
    );
    gl.uniform1f(gl.getUniformLocation(knifeSweepMaskProgram, 'u_radius'), radius);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, session.knifeSweep.texture);
    gl.uniform1i(gl.getUniformLocation(knifeSweepMaskProgram, 'u_oldMask'), 0);
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, selection.texture);
    gl.uniform1i(gl.getUniformLocation(knifeSweepMaskProgram, 'u_selectionMask'), 1);
    gl.uniform1f(
      gl.getUniformLocation(knifeSweepMaskProgram, 'u_selectionEnabled'),
      selection.enabled && selection.texture ? 1 : 0,
    );
    gl.drawArrays(gl.TRIANGLES, 0, 6);
    gl.disableVertexAttribArray(position);
    gl.disable(gl.SCISSOR_TEST);
    copyScratchRegionToTarget(session.knifeSweep, bounds);
    session.knifeBounds = mergeKnifeBounds(session.knifeBounds, bounds);
  }

  function forceKnifeWetFloor(layer, session) {
    if (!session?.knifeSweep || !session.knifeBounds) return;
    const scratchTarget = ensureScratch();
    const bounds = session.knifeBounds;

    gl.bindFramebuffer(gl.FRAMEBUFFER, scratchTarget.framebuffer);
    gl.viewport(0, 0, width, height);
    applyScissor(bounds);
    gl.disable(gl.BLEND);
    gl.useProgram(knifeWetFloorProgram);
    const position = bindQuad(knifeWetFloorProgram);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, layer.wet.texture);
    gl.uniform1i(gl.getUniformLocation(knifeWetFloorProgram, 'u_wet'), 0);
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, layer.dry.texture);
    gl.uniform1i(gl.getUniformLocation(knifeWetFloorProgram, 'u_dry'), 1);
    gl.activeTexture(gl.TEXTURE2);
    gl.bindTexture(gl.TEXTURE_2D, session.knifeSweep.texture);
    gl.uniform1i(gl.getUniformLocation(knifeWetFloorProgram, 'u_sweepMask'), 2);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
    gl.disableVertexAttribArray(position);
    gl.disable(gl.SCISSOR_TEST);
    copyScratchRegionToTarget(layer.wet, bounds);
  }

  function clearDryUnderKnife(layer, session) {
    if (!session?.knifeSweep || !session.knifeBounds) return;
    const scratchTarget = ensureScratch();
    const bounds = session.knifeBounds;

    gl.bindFramebuffer(gl.FRAMEBUFFER, scratchTarget.framebuffer);
    gl.viewport(0, 0, width, height);
    applyScissor(bounds);
    gl.disable(gl.BLEND);
    gl.useProgram(knifeDryClearProgram);
    const position = bindQuad(knifeDryClearProgram);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, layer.dry.texture);
    gl.uniform1i(gl.getUniformLocation(knifeDryClearProgram, 'u_dry'), 0);
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, session.knifeSweep.texture);
    gl.uniform1i(gl.getUniformLocation(knifeDryClearProgram, 'u_sweepMask'), 1);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
    gl.disableVertexAttribArray(position);
    gl.disable(gl.SCISSOR_TEST);
    copyScratchRegionToTarget(layer.dry, bounds);
  }

  function clearDryUnderBrush(layer, start, end, radius, bounds, settings, strokeOptions, selection) {
    const scratchTarget = ensureScratch();
    const vertices = buildDocumentVertices(bounds);

    gl.bindFramebuffer(gl.FRAMEBUFFER, scratchTarget.framebuffer);
    gl.viewport(0, 0, width, height);
    applyScissor(bounds);
    gl.disable(gl.BLEND);
    gl.useProgram(brushDryClearProgram);
    gl.bindBuffer(gl.ARRAY_BUFFER, documentBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STREAM_DRAW);
    const position = gl.getAttribLocation(brushDryClearProgram, 'a_position');
    gl.enableVertexAttribArray(position);
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);
    gl.uniform2f(gl.getUniformLocation(brushDryClearProgram, 'u_resolution'), width, height);
    gl.uniform2f(gl.getUniformLocation(brushDryClearProgram, 'u_start'), Number(start.x) || 0, Number(start.y) || 0);
    gl.uniform2f(gl.getUniformLocation(brushDryClearProgram, 'u_end'), Number(end.x) || 0, Number(end.y) || 0);
    gl.uniform1f(gl.getUniformLocation(brushDryClearProgram, 'u_radius'), radius);
    gl.uniform1f(gl.getUniformLocation(brushDryClearProgram, 'u_bristleCount'), clamp(settings.bristleCount || 32, 1, 250));
    gl.uniform1f(gl.getUniformLocation(brushDryClearProgram, 'u_bristleThickness'), clamp(Number(settings.bristleThickness) / 100, 0.01, 1));
    gl.uniform1f(gl.getUniformLocation(brushDryClearProgram, 'u_seed'), Number(strokeOptions.strokeSeed || strokeOptions.seed || 1));
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, layer.dry.texture);
    gl.uniform1i(gl.getUniformLocation(brushDryClearProgram, 'u_dry'), 0);
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, selection.texture);
    gl.uniform1i(gl.getUniformLocation(brushDryClearProgram, 'u_selectionMask'), 1);
    gl.uniform1f(
      gl.getUniformLocation(brushDryClearProgram, 'u_selectionEnabled'),
      selection.enabled && selection.texture ? 1 : 0,
    );
    gl.drawArrays(gl.TRIANGLES, 0, 6);
    gl.disableVertexAttribArray(position);
    gl.disable(gl.SCISSOR_TEST);
    copyScratchRegionToTarget(layer.dry, bounds);
  }

  function drawImportedDab(layerId, dab, tip, brush, settings = {}, strokeOptions = {}) {
    const layer = ensureLayer(layerId);
    if (!layer || !dab || !tip || !tip.texture) return false;
    syncLoads(layer, settings);

    const sourceMaximum = Math.max(1, Number(tip.width) || 1, Number(tip.height) || 1);
    const size = Math.max(
      0.5,
      (Number(settings.toolSize || strokeOptions.size) || 1)
        * Math.max(0.02, Number(dab.sizeMultiplier) || 1)
        * Math.max(0.05, Number(brush && brush.tipScale) || 1),
    );
    const halfWidth = Math.max(0.5, size * ((Number(tip.width) || 1) / sourceMaximum) * 0.5);
    const halfHeight = Math.max(0.5, size * ((Number(tip.height) || 1) / sourceMaximum) * 0.5);
    const radius = Math.hypot(halfWidth, halfHeight);
    const centerX = Number(dab.x) || 0;
    const centerY = Number(dab.y) || 0;
    const bounds = {
      minimumX: Math.max(0, Math.floor(centerX - radius - 2)),
      minimumY: Math.max(0, Math.floor(centerY - radius - 2)),
      maximumX: Math.min(width, Math.ceil(centerX + radius + 2)),
      maximumY: Math.min(height, Math.ceil(centerY + radius + 2)),
    };
    if (bounds.maximumX <= bounds.minimumX || bounds.maximumY <= bounds.minimumY) return false;

    const vertices = buildDocumentVertices(bounds);
    const selection = getSelection();
    const colour = hexToColour(settings.paintColor || strokeOptions.color || '#d52b24');
    const pointerPressure = clamp(Number(dab.pressure) || 1, 0.01, 1);
    const toolPressure = clamp(Number(settings.brushPressure) / 100, 0.01, 1);
    const opacity = clamp(Number(dab.opacity) || 1, 0.001, 1);
    const rotation = Number(dab.rotation) || 0;
    const scratchTarget = ensureScratch();

    gl.bindFramebuffer(gl.FRAMEBUFFER, scratchTarget.framebuffer);
    gl.viewport(0, 0, width, height);
    applyScissor(bounds);
    gl.disable(gl.BLEND);
    gl.useProgram(importedPaintProgram);
    gl.bindBuffer(gl.ARRAY_BUFFER, documentBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STREAM_DRAW);
    const position = gl.getAttribLocation(importedPaintProgram, 'a_position');
    gl.enableVertexAttribArray(position);
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);
    gl.uniform2f(gl.getUniformLocation(importedPaintProgram, 'u_resolution'), width, height);
    gl.uniform2f(gl.getUniformLocation(importedPaintProgram, 'u_center'), centerX, centerY);
    gl.uniform2f(gl.getUniformLocation(importedPaintProgram, 'u_halfSize'), halfWidth, halfHeight);
    gl.uniform3f(gl.getUniformLocation(importedPaintProgram, 'u_colour'), colour[0], colour[1], colour[2]);
    gl.uniform1f(gl.getUniformLocation(importedPaintProgram, 'u_rotation'), rotation);
    gl.uniform1f(gl.getUniformLocation(importedPaintProgram, 'u_opacity'), opacity);
    gl.uniform1f(gl.getUniformLocation(importedPaintProgram, 'u_pressure'), clamp(pointerPressure * toolPressure, 0.01, 1));
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, layer.wet.texture);
    gl.uniform1i(gl.getUniformLocation(importedPaintProgram, 'u_wet'), 0);
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, layer.dry.texture);
    gl.uniform1i(gl.getUniformLocation(importedPaintProgram, 'u_dry'), 1);
    gl.activeTexture(gl.TEXTURE2);
    gl.bindTexture(gl.TEXTURE_2D, tip.texture);
    gl.uniform1i(gl.getUniformLocation(importedPaintProgram, 'u_tip'), 2);
    gl.activeTexture(gl.TEXTURE3);
    gl.bindTexture(gl.TEXTURE_2D, selection.texture);
    gl.uniform1i(gl.getUniformLocation(importedPaintProgram, 'u_selectionMask'), 3);
    gl.uniform1f(gl.getUniformLocation(importedPaintProgram, 'u_selectionEnabled'), selection.enabled && selection.texture ? 1 : 0);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
    gl.disableVertexAttribArray(position);
    gl.disable(gl.SCISSOR_TEST);
    copyScratchRegionToTarget(layer.wet, bounds);

    queueFluidSimulation(layer, bounds, settings, {
      start: { x: centerX, y: centerY },
      end: { x: centerX, y: centerY },
      direction: [1, 0],
      radius,
      speed: opacity,
      selection,
    });

    gl.activeTexture(gl.TEXTURE0);
    layer.containsPaint = true;
    if (typeof options.requestRender === 'function') options.requestRender();
    return true;
  }

  function readLayerPeakHeight(id) {
    const layer = layers.get(String(id || '').trim());
    if (!layer || !layer.containsPaint) return 0;
    const pixelCount = width * height;
    let wetPixels;
    let dryPixels;
    gl.bindFramebuffer(gl.FRAMEBUFFER, layer.wet.framebuffer);
    if (layer.wet.type === gl.FLOAT) {
      wetPixels = new Float32Array(pixelCount * 4);
      gl.readPixels(0, 0, width, height, gl.RGBA, gl.FLOAT, wetPixels);
    } else {
      wetPixels = new Uint8Array(pixelCount * 4);
      gl.readPixels(0, 0, width, height, gl.RGBA, gl.UNSIGNED_BYTE, wetPixels);
    }
    gl.bindFramebuffer(gl.FRAMEBUFFER, layer.dry.framebuffer);
    if (layer.dry.type === gl.FLOAT) {
      dryPixels = new Float32Array(pixelCount * 4);
      gl.readPixels(0, 0, width, height, gl.RGBA, gl.FLOAT, dryPixels);
    } else {
      dryPixels = new Uint8Array(pixelCount * 4);
      gl.readPixels(0, 0, width, height, gl.RGBA, gl.UNSIGNED_BYTE, dryPixels);
    }
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    const wetScale = layer.wet.type === gl.FLOAT ? MAX_HEIGHT : MAX_HEIGHT / 255;
    const dryScale = layer.dry.type === gl.FLOAT ? MAX_HEIGHT : MAX_HEIGHT / 255;
    let peakHeight = 0;
    for (let index = 3; index < wetPixels.length; index += 4) {
      const totalHeight = Number(wetPixels[index]) * wetScale + Number(dryPixels[index]) * dryScale;
      if (totalHeight > peakHeight) peakHeight = totalHeight;
    }
    return Math.min(MAX_HEIGHT, peakHeight);
  }

  function getDebugMetrics(id) {
    const key = String(id || '').trim();
    const current = layerDebugMetrics.get(key) || {};
    let peakHeight = 0;
    for (const layerId of layers.keys()) {
      peakHeight = Math.max(peakHeight, readLayerPeakHeight(layerId));
    }
    return {
      pushReach: Number(current.pushReach) || 0,
      forwardReach: Number(current.forwardReach) || 0,
      sideReach: Number(current.sideReach) || 0,
      peakHeight,
      knifePaintUnits: Math.max(0, Number(layers.get(key)?.knifePaintUnits) || 0),
      knifePaintMicrograms: Math.max(0, Number(layers.get(key)?.knifePaintUnits) || 0),
    };
  }


  function reduceSumTexture(targets, sourceTarget) {
    let current = sourceTarget;
    let level = 0;
    while (current.width > 1 || current.height > 1) {
      const nextWidth = Math.max(1, Math.ceil(current.width / 2));
      const nextHeight = Math.max(1, Math.ceil(current.height / 2));
      let next = targets[level];
      if (!next || next.width !== nextWidth || next.height !== nextHeight || next.type !== gl.FLOAT) {
        deleteTarget(next);
        next = createTarget(nextWidth, nextHeight, gl.FLOAT);
        targets[level] = next;
      }
      gl.bindFramebuffer(gl.FRAMEBUFFER, next.framebuffer);
      gl.viewport(0, 0, nextWidth, nextHeight);
      gl.disable(gl.BLEND);
      gl.useProgram(reduceSumProgram);
      const position = bindQuad(reduceSumProgram);
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, current.texture);
      gl.uniform1i(gl.getUniformLocation(reduceSumProgram, 'u_source'), 0);
      gl.uniform2f(gl.getUniformLocation(reduceSumProgram, 'u_sourceSize'), current.width, current.height);
      gl.drawArrays(gl.TRIANGLES, 0, 6);
      gl.disableVertexAttribArray(position);
      current = next;
      level += 1;
    }
    return current;
  }

  function renderKnifePickupTarget(program, destination, layer, session, start, end, startDirection, endDirection, radius, bounds) {
    clearTarget(destination);
    const vertices = buildDocumentVertices(bounds);
    gl.bindFramebuffer(gl.FRAMEBUFFER, destination.framebuffer);
    gl.viewport(0, 0, width, height);
    applyScissor(bounds);
    gl.disable(gl.BLEND);
    gl.useProgram(program);
    gl.bindBuffer(gl.ARRAY_BUFFER, documentBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STREAM_DRAW);
    const position = gl.getAttribLocation(program, 'a_position');
    gl.enableVertexAttribArray(position);
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);
    gl.uniform2f(gl.getUniformLocation(program, 'u_resolution'), width, height);
    gl.uniform2f(gl.getUniformLocation(program, 'u_start'), Number(start.x) || 0, Number(start.y) || 0);
    gl.uniform2f(gl.getUniformLocation(program, 'u_end'), Number(end.x) || 0, Number(end.y) || 0);
    gl.uniform2f(gl.getUniformLocation(program, 'u_startDirection'), Number(startDirection[0]) || 0, Number(startDirection[1]) || 0);
    gl.uniform2f(gl.getUniformLocation(program, 'u_endDirection'), Number(endDirection[0]) || 0, Number(endDirection[1]) || 0);
    gl.uniform1f(gl.getUniformLocation(program, 'u_radius'), radius);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, session.knifeSourceWet.texture);
    gl.uniform1i(gl.getUniformLocation(program, 'u_sourceWet'), 0);
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, session.knifeSourceDry.texture);
    gl.uniform1i(gl.getUniformLocation(program, 'u_sourceDry'), 1);
    gl.activeTexture(gl.TEXTURE2);
    gl.bindTexture(gl.TEXTURE_2D, session.knifeSweep.texture);
    gl.uniform1i(gl.getUniformLocation(program, 'u_oldSweep'), 2);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
    gl.disableVertexAttribArray(position);
    gl.disable(gl.SCISSOR_TEST);
  }

  function prepareKnifeGpuPickup(layer, session, start, end, startDirection, endDirection, radius, bounds) {
    renderKnifePickupTarget(knifePickupMassProgram, session.knifePickupMass, layer, session, start, end, startDirection, endDirection, radius, bounds);
    renderKnifePickupTarget(knifePickupCountProgram, session.knifePickupCounts, layer, session, start, end, startDirection, endDirection, radius, bounds);
    return {
      mass: reduceSumTexture(session.knifeMassReductionTargets, session.knifePickupMass),
      counts: reduceSumTexture(session.knifeCountReductionTargets, session.knifePickupCounts),
    };
  }

  function commitKnifeGpuState(layer, pickup, radius, segmentDistance, pressure, capacityUnits) {
    const current = layer.knifeState[layer.knifeStateIndex];
    const nextIndex = 1 - layer.knifeStateIndex;
    const next = layer.knifeState[nextIndex];
    gl.bindFramebuffer(gl.FRAMEBUFFER, next.framebuffer);
    gl.viewport(0, 0, 1, 1);
    gl.disable(gl.BLEND);
    gl.useProgram(knifeStateUpdateProgram);
    const position = bindQuad(knifeStateUpdateProgram);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, current.texture);
    gl.uniform1i(gl.getUniformLocation(knifeStateUpdateProgram, 'u_oldState'), 0);
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, pickup.mass.texture);
    gl.uniform1i(gl.getUniformLocation(knifeStateUpdateProgram, 'u_pickupMass'), 1);
    gl.activeTexture(gl.TEXTURE2);
    gl.bindTexture(gl.TEXTURE_2D, pickup.counts.texture);
    gl.uniform1i(gl.getUniformLocation(knifeStateUpdateProgram, 'u_pickupCounts'), 2);
    gl.uniform1f(gl.getUniformLocation(knifeStateUpdateProgram, 'u_capacityUnits'), capacityUnits);
    gl.uniform1f(gl.getUniformLocation(knifeStateUpdateProgram, 'u_radius'), radius);
    gl.uniform1f(gl.getUniformLocation(knifeStateUpdateProgram, 'u_segmentDistance'), segmentDistance);
    gl.uniform1f(gl.getUniformLocation(knifeStateUpdateProgram, 'u_pressure'), pressure);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
    gl.disableVertexAttribArray(position);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    layer.knifeStateIndex = nextIndex;
  }

  function updateTubeStrokeTarget(session, start, end, radius, bounds, selection) {
    if (!session || !session.tubeStroke) return;
    session.tubeSegmentId = Math.min(1000000, Number(session.tubeSegmentId || 0) + 1);
    const scratchTarget = ensureScratch();
    const vertices = buildDocumentVertices(bounds);

    gl.bindFramebuffer(gl.FRAMEBUFFER, scratchTarget.framebuffer);
    gl.viewport(0, 0, width, height);
    applyScissor(bounds);
    gl.disable(gl.BLEND);
    gl.useProgram(tubeStrokeProgram);
    gl.bindBuffer(gl.ARRAY_BUFFER, documentBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STREAM_DRAW);
    const position = gl.getAttribLocation(tubeStrokeProgram, 'a_position');
    gl.enableVertexAttribArray(position);
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);
    gl.uniform2f(gl.getUniformLocation(tubeStrokeProgram, 'u_resolution'), width, height);
    gl.uniform2f(gl.getUniformLocation(tubeStrokeProgram, 'u_start'), Number(start.x) || 0, Number(start.y) || 0);
    gl.uniform2f(gl.getUniformLocation(tubeStrokeProgram, 'u_end'), Number(end.x) || 0, Number(end.y) || 0);
    gl.uniform1f(gl.getUniformLocation(tubeStrokeProgram, 'u_radius'), radius);
    gl.uniform1f(gl.getUniformLocation(tubeStrokeProgram, 'u_segmentId'), Number(session.tubeSegmentId || 0));
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, session.tubeStroke.texture);
    gl.uniform1i(gl.getUniformLocation(tubeStrokeProgram, 'u_tubeStroke'), 0);
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, selection.texture);
    gl.uniform1i(gl.getUniformLocation(tubeStrokeProgram, 'u_selectionMask'), 1);
    gl.uniform1f(
      gl.getUniformLocation(tubeStrokeProgram, 'u_selectionEnabled'),
      selection.enabled && selection.texture ? 1 : 0,
    );
    gl.drawArrays(gl.TRIANGLES, 0, 6);
    gl.disableVertexAttribArray(position);
    gl.disable(gl.SCISSOR_TEST);
    copyScratchRegionToTarget(session.tubeStroke, bounds);
  }

  function updateRegion(layer, session, start, end, settings, strokeOptions) {
    const safeEnd = end || start;
    const tool = String(settings.tool || 'tube').toLowerCase();
    const bodyBristleThickness = Math.max(0.5, Math.min(8, (Number(settings.bristleThickness) || 35) / 22));
    const requestedSnakeRadius = Number(strokeOptions && strokeOptions.brushSnakeRadius);
    const radius = tool === 'brush' && strokeOptions.brushSnakeBody
      ? Math.max(0.35, Number.isFinite(requestedSnakeRadius) ? requestedSnakeRadius : bodyBristleThickness * 0.5)
      : Math.max(2, Number(settings.toolSize || strokeOptions.size) * 0.5 || 2);
    const operation = tool === 'brush' ? 1 : tool === 'knife' ? 2 : 0;
    const padding = tool === 'knife' ? Math.max(radius * 2.8 + 8, 82) : tool === 'brush' ? radius * 2.25 + 8 : radius + 5;
    const requestedBounds = strokeOptions && strokeOptions.brushMaskBounds;
    const minimumX = requestedBounds ? Math.max(0, Math.floor(requestedBounds.minimumX)) : Math.max(0, Math.floor(Math.min(start.x, safeEnd.x) - padding));
    const minimumY = requestedBounds ? Math.max(0, Math.floor(requestedBounds.minimumY)) : Math.max(0, Math.floor(Math.min(start.y, safeEnd.y) - padding));
    const maximumX = requestedBounds ? Math.min(width, Math.ceil(requestedBounds.maximumX)) : Math.min(width, Math.ceil(Math.max(start.x, safeEnd.x) + padding));
    const maximumY = requestedBounds ? Math.min(height, Math.ceil(requestedBounds.maximumY)) : Math.min(height, Math.ceil(Math.max(start.y, safeEnd.y) + padding));
    if (maximumX <= minimumX || maximumY <= minimumY) return false;
    if (session && tool === 'brush') {
      const currentBounds = { minimumX, minimumY, maximumX, maximumY };
      session.paintBounds = session.paintBounds
        ? {
            minimumX: Math.min(session.paintBounds.minimumX, currentBounds.minimumX),
            minimumY: Math.min(session.paintBounds.minimumY, currentBounds.minimumY),
            maximumX: Math.max(session.paintBounds.maximumX, currentBounds.maximumX),
            maximumY: Math.max(session.paintBounds.maximumY, currentBounds.maximumY),
          }
        : currentBounds;
      session.paintSettings = { ...settings };
    }
    const vertices = new Float32Array([
      minimumX, minimumY,
      maximumX, minimumY,
      minimumX, maximumY,
      minimumX, maximumY,
      maximumX, minimumY,
      maximumX, maximumY,
    ]);
    const scratchTarget = ensureScratch();
    const colour = hexToColour(settings.paintColor || strokeOptions.color || '#d52b24');
    const pointerPressure = clamp(((Number(start.pressure) || 1) + (Number(safeEnd.pressure) || Number(start.pressure) || 1)) * 0.5, 0.01, 1);
    const toolPressure = tool === 'brush'
      ? clamp(Number(settings.brushPressure) / 100, 0.01, 1)
      : tool === 'knife'
        ? clamp(Number(settings.knifePressure) / 100, 0.01, 1)
        : 1;
    const selection = getSelection();
    const combinedPressure = clamp(pointerPressure * toolPressure, 0.01, 1);
    const segmentDistance = Math.hypot(Number(safeEnd.x) - Number(start.x), Number(safeEnd.y) - Number(start.y));
    if (tool === 'knife' && segmentDistance < 0.35) return false;
    const segmentSpeed = clamp(segmentDistance / Math.max(1, radius * 0.55), 0, 1);
    const currentDirection = segmentDistance > 0.0001
      ? [(Number(safeEnd.x) - Number(start.x)) / segmentDistance, (Number(safeEnd.y) - Number(start.y)) / segmentDistance]
      : Array.isArray(session && session.brushDirection)
        ? session.brushDirection
        : [1, 0];
    const previousDirection = Array.isArray(session && session.brushDirection)
      ? session.brushDirection
      : currentDirection;
    const knifeStartRotation = Number(start && start.knifeRotation);
    const knifeStartDirection = tool === 'knife' && Number.isFinite(knifeStartRotation)
      ? [Math.cos(knifeStartRotation), Math.sin(knifeStartRotation)]
      : Array.isArray(session && session.knifeDirection)
        ? normaliseStrokeDirection(session.knifeDirection)
        : currentDirection;
    const knifeDirection = tool === 'knife'
      ? resolveKnifeDirection(session, start, safeEnd, radius)
      : currentDirection;

    if (tool === 'knife') {
      layerDebugMetrics.set(String(layer.id || ''), { pushReach: 0, forwardReach: 0, sideReach: 0 });
    }

    if (tool === 'knife' && settings.knifeFloorOnly === true) {
      const knifeBounds = { minimumX, minimumY, maximumX, maximumY };
      updateKnifeSweepMask(session, start, safeEnd, knifeStartDirection, knifeDirection, radius, knifeBounds, selection);
      forceKnifeWetFloor(layer, session);
      clearDryUnderKnife(layer, session);
      layer.containsPaint = true;
      if (typeof options.requestRender === 'function') options.requestRender();
      return true;
    }

    if (tool === 'tube') {
      updateTubeStrokeTarget(
        session,
        start,
        safeEnd,
        radius,
        { minimumX, minimumY, maximumX, maximumY },
        selection,
      );
    }

    const knifeCapacityUnits = tool === 'knife'
      ? Math.max(1, radius * radius * Math.max(4, radius * 0.45) * 1.35)
      : 1;
    const knifePickup = tool === 'knife'
      ? prepareKnifeGpuPickup(
          layer,
          session,
          start,
          safeEnd,
          knifeStartDirection,
          knifeDirection,
          radius,
          { minimumX, minimumY, maximumX, maximumY },
        )
      : null;

    gl.bindFramebuffer(gl.FRAMEBUFFER, scratchTarget.framebuffer);
    gl.viewport(0, 0, width, height);
    gl.enable(gl.SCISSOR_TEST);
    gl.scissor(minimumX, Math.max(0, height - maximumY), Math.max(1, maximumX - minimumX), Math.max(1, maximumY - minimumY));
    gl.disable(gl.BLEND);
    gl.useProgram(updateProgram);
    gl.bindBuffer(gl.ARRAY_BUFFER, documentBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STREAM_DRAW);
    const position = gl.getAttribLocation(updateProgram, 'a_position');
    gl.enableVertexAttribArray(position);
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);
    gl.uniform2f(gl.getUniformLocation(updateProgram, 'u_resolution'), width, height);
    gl.uniform2f(gl.getUniformLocation(updateProgram, 'u_start'), Number(start.x) || 0, Number(start.y) || 0);
    gl.uniform2f(gl.getUniformLocation(updateProgram, 'u_end'), Number(safeEnd.x) || 0, Number(safeEnd.y) || 0);
    gl.uniform3f(gl.getUniformLocation(updateProgram, 'u_colour'), colour[0], colour[1], colour[2]);
    gl.uniform1f(gl.getUniformLocation(updateProgram, 'u_radius'), radius);
    gl.uniform1f(gl.getUniformLocation(updateProgram, 'u_operation'), operation);
    gl.uniform1f(gl.getUniformLocation(updateProgram, 'u_pressure'), combinedPressure);
    gl.uniform1f(gl.getUniformLocation(updateProgram, 'u_bristleCount'), clamp(settings.bristleCount || 32, 1, 250));
    gl.uniform1f(gl.getUniformLocation(updateProgram, 'u_bristleThickness'), clamp(Number(settings.bristleThickness) / 100, 0.01, 1));
    gl.uniform1f(gl.getUniformLocation(updateProgram, 'u_brushSnakeBody'), strokeOptions.brushSnakeBody ? 1 : 0);
    gl.uniform1f(gl.getUniformLocation(updateProgram, 'u_brushDirectBristle'), strokeOptions.brushDirectBristle ? 1 : 0);
    gl.uniform1f(gl.getUniformLocation(updateProgram, 'u_useBrushMask'), strokeOptions.brushMaskTexture ? 1 : 0);
    gl.uniform1f(gl.getUniformLocation(updateProgram, 'u_brushMaskSidePass'), strokeOptions.brushMaskSidePass ? 1 : 0);
    gl.uniform1f(gl.getUniformLocation(updateProgram, 'u_brushRadius'), Math.max(radius, Number(settings.toolSize || strokeOptions.size) * 0.5 || radius));
    gl.uniform1f(gl.getUniformLocation(updateProgram, 'u_brushSnakePart'), Number(strokeOptions.brushSnakePart) || 0);
    gl.uniform1f(gl.getUniformLocation(updateProgram, 'u_brushSnakeParts'), Math.max(1, Number(strokeOptions.brushSnakeParts) || 1));
    gl.uniform1f(gl.getUniformLocation(updateProgram, 'u_infinitePaint'), settings.infinitePaint === false ? 0 : 1);
    gl.uniform1f(gl.getUniformLocation(updateProgram, 'u_sideDeposits'), settings.sideDeposits === false ? 0 : 1);
    gl.uniform1f(gl.getUniformLocation(updateProgram, 'u_seed'), Number(strokeOptions.strokeSeed || strokeOptions.seed || 1));
    gl.uniform1f(gl.getUniformLocation(updateProgram, 'u_floorHeight'), FLOOR_HEIGHT);
    gl.uniform1f(gl.getUniformLocation(updateProgram, 'u_liquid'), clamp(Number(settings.liquid) / 100, 0, 1));
    gl.uniform1f(gl.getUniformLocation(updateProgram, 'u_cohesion'), clamp(Number(settings.stickiness) / 100, 0, 1));
    gl.uniform1f(gl.getUniformLocation(updateProgram, 'u_speed'), segmentSpeed);
    gl.uniform2f(
      gl.getUniformLocation(updateProgram, 'u_previousDirection'),
      Number(previousDirection[0]) || 0,
      Number(previousDirection[1]) || 0,
    );
    gl.uniform2f(
      gl.getUniformLocation(updateProgram, 'u_knifeStartDirection'),
      Number(knifeStartDirection[0]) || 0,
      Number(knifeStartDirection[1]) || 0,
    );
    gl.uniform2f(
      gl.getUniformLocation(updateProgram, 'u_knifeDirection'),
      Number(knifeDirection[0]) || 0,
      Number(knifeDirection[1]) || 0,
    );
    gl.uniform1f(
      gl.getUniformLocation(updateProgram, 'u_knifePaintUnits'),
      0,
    );
    gl.uniform1f(
      gl.getUniformLocation(updateProgram, 'u_knifeCapacityUnits'),
      knifeCapacityUnits,
    );
    gl.uniform1f(
      gl.getUniformLocation(updateProgram, 'u_knifeOverflowUnits'),
      0,
    );

    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, layer.wet.texture);
    gl.uniform1i(gl.getUniformLocation(updateProgram, 'u_wet'), 0);
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, layer.dry.texture);
    gl.uniform1i(gl.getUniformLocation(updateProgram, 'u_dry'), 1);
    gl.activeTexture(gl.TEXTURE2);
    gl.bindTexture(gl.TEXTURE_2D, session?.baseWet?.texture || layer.wet.texture);
    gl.uniform1i(gl.getUniformLocation(updateProgram, 'u_baseWet'), 2);
    gl.activeTexture(gl.TEXTURE3);
    gl.bindTexture(gl.TEXTURE_2D, session?.tubeStroke?.texture || layer.wet.texture);
    gl.uniform1i(gl.getUniformLocation(updateProgram, 'u_tubeStroke'), 3);
    gl.activeTexture(gl.TEXTURE4);
    gl.bindTexture(gl.TEXTURE_2D, layer.brushLoad[layer.brushLoadIndex].texture);
    gl.uniform1i(gl.getUniformLocation(updateProgram, 'u_brushLoad'), 4);
    gl.activeTexture(gl.TEXTURE5);
    gl.bindTexture(gl.TEXTURE_2D, layer.knifeLoad[layer.knifeLoadIndex].texture);
    gl.uniform1i(gl.getUniformLocation(updateProgram, 'u_knifeLoad'), 5);
    gl.activeTexture(gl.TEXTURE6);
    gl.bindTexture(gl.TEXTURE_2D, selection.texture);
    gl.uniform1i(gl.getUniformLocation(updateProgram, 'u_selectionMask'), 6);
    gl.activeTexture(gl.TEXTURE7);
    gl.bindTexture(gl.TEXTURE_2D, tool === 'knife' ? layer.knifeState[layer.knifeStateIndex].texture : layer.knifeLoad[layer.knifeLoadIndex].texture);
    gl.uniform1i(gl.getUniformLocation(updateProgram, 'u_knifeState'), 7);
    gl.activeTexture(gl.TEXTURE8);
    gl.bindTexture(gl.TEXTURE_2D, strokeOptions.brushMaskTexture || (tool === 'knife' && knifePickup ? knifePickup.mass.texture : layer.knifeLoad[layer.knifeLoadIndex].texture));
    gl.uniform1i(gl.getUniformLocation(updateProgram, 'u_knifePickupMass'), 8);
    gl.uniform1i(gl.getUniformLocation(updateProgram, 'u_brushMask'), 8);
    gl.uniform1f(gl.getUniformLocation(updateProgram, 'u_selectionEnabled'), selection.enabled && selection.texture ? 1 : 0);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
    gl.disableVertexAttribArray(position);
    gl.disable(gl.SCISSOR_TEST);

    gl.bindFramebuffer(gl.FRAMEBUFFER, scratchTarget.framebuffer);
    gl.bindTexture(gl.TEXTURE_2D, layer.wet.texture);
    gl.copyTexSubImage2D(
      gl.TEXTURE_2D,
      0,
      minimumX,
      Math.max(0, height - maximumY),
      minimumX,
      Math.max(0, height - maximumY),
      maximumX - minimumX,
      maximumY - minimumY,
    );
    gl.bindTexture(gl.TEXTURE_2D, null);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.activeTexture(gl.TEXTURE0);

    if (tool === 'brush' && !strokeOptions.brushMaskSidePass) {
      clearDryUnderBrush(
        layer,
        start,
        safeEnd,
        radius,
        { minimumX, minimumY, maximumX, maximumY },
        settings,
        strokeOptions,
        selection,
      );
    }

    if (tool === 'knife') {
      const knifeBounds = {
        minimumX,
        minimumY,
        maximumX,
        maximumY,
      };
      updateKnifeSweepMask(session, start, safeEnd, knifeStartDirection, knifeDirection, radius, knifeBounds, selection);
      forceKnifeWetFloor(layer, session);
      clearDryUnderKnife(layer, session);

      // Knife cohesion is handled only inside the deposited front and side blob masks.


      commitKnifeGpuState(
        layer,
        knifePickup,
        radius,
        segmentDistance,
        combinedPressure,
        knifeCapacityUnits,
      );
      layer.knifePaintUnits = 0;
      layer.knifePaintColour = [0, 0, 0];
    }

    layer.containsPaint = true;

    if (tool === 'brush' && settings.infinitePaint === false && !strokeOptions.brushMaskSidePass) {
      consumeBrushLoad(
        layer,
        radius,
        Math.hypot(safeEnd.x - start.x, safeEnd.y - start.y),
        combinedPressure,
        settings.sideDeposits !== false,
      );
    }

    queueFluidSimulation(layer, { minimumX, minimumY, maximumX, maximumY }, settings, {
      start,
      end: safeEnd,
      direction: currentDirection,
      radius,
      speed: segmentSpeed,
      selection,
    });

    return true;
  }

  function drawStroke(layerId, points, settings = {}, strokeOptions = {}) {
    if (destroyed || !Array.isArray(points) || !points.length) return false;
    const layer = ensureLayer(layerId);
    if (!layer) return false;
    displaySettings = { ...displaySettings, ...settings };
    syncLoads(layer, settings);
    const strokeId = String(strokeOptions.paintStrokeId || strokeOptions.strokeId || '').trim();
    let session = strokeId ? strokeSessions.get(strokeId) : null;
    if (!session) {
      const generated = beginStroke(layerId, strokeId, settings, strokeOptions);
      session = strokeSessions.get(generated);
    }
    if (session) {
      session.buttons = Number(strokeOptions.paintButtons || strokeOptions.buttons || session.buttons || 1);
      if (Array.isArray(strokeOptions.paintBrushDirection)) {
        session.brushDirection = normaliseStrokeDirection(strokeOptions.paintBrushDirection, session.brushDirection || [1, 0]);
      }
      const nextCursorBristles = normaliseCursorBristlesFromOptions(strokeOptions.paintBrushBristles);
      if (nextCursorBristles.length) {
        session.previousBrushCursorBristles = Array.isArray(session.brushCursorBristles) ? session.brushCursorBristles : [];
        session.brushCursorBristles = nextCursorBristles;
      }
    }
    const tool = String(settings.tool || 'tube').toLowerCase();
    const activeButtons = Number(strokeOptions.paintButtons || strokeOptions.buttons || session?.buttons || 1);
    if (tool === 'knife' && activeButtons !== 1) {
      if (session && points.length) session.lastPoint = cloneStrokePoint(points[points.length - 1]);
      return false;
    }
    const safePoints = points.map((point) => cloneStrokePoint(point));
    let drew = false;

    if (tool === 'brush' && !strokeOptions.brushSnakeBody) {
      if (session && Array.isArray(session.brushCursorBristles) && session.brushCursorBristles.length) {
        drew = drawCursorBristlesExactly(layer, session, session.brushCursorBristles, settings, strokeOptions) || drew;
        if (session) session.lastPoint = cloneStrokePoint(safePoints[safePoints.length - 1]);
        return drew;
      }
      let previousPoint = session?.lastPoint || safePoints[0];
      for (let index = 0; index < safePoints.length; index += 1) {
        const currentPoint = safePoints[index];
        drew = drawGpuBrushSweep(layer, session, previousPoint, currentPoint, settings, strokeOptions) || drew;
        previousPoint = currentPoint;
      }
      if (session) session.lastPoint = cloneStrokePoint(safePoints[safePoints.length - 1]);
      return drew;
    }

    if (tool === 'knife') {
      if (safePoints.length === 1) {
        const currentPoint = safePoints[0];
        if (session && session.lastPoint && strokePointsDiffer(session.lastPoint, currentPoint)) {
          drew = drawInterpolatedSegment(layer, session, session.lastPoint, currentPoint, settings, strokeOptions);
        }
        if (session) session.lastPoint = cloneStrokePoint(currentPoint);
      } else {
        if (session && session.lastPoint && strokePointsDiffer(session.lastPoint, safePoints[0])) {
          drew = drawInterpolatedSegment(layer, session, session.lastPoint, safePoints[0], settings, strokeOptions) || drew;
        }
        for (let index = 1; index < safePoints.length; index += 1) {
          drew = drawInterpolatedSegment(layer, session, safePoints[index - 1], safePoints[index], settings, strokeOptions) || drew;
        }
        if (session) session.lastPoint = cloneStrokePoint(safePoints[safePoints.length - 1]);
      }
    } else if (safePoints.length === 1) {
      drew = updateRegion(layer, session, safePoints[0], safePoints[0], settings, strokeOptions);
    } else {
      for (let index = 1; index < safePoints.length; index += 1) {
        drew = updateRegion(layer, session, safePoints[index - 1], safePoints[index], settings, strokeOptions) || drew;
      }
    }
    if (drew && typeof options.requestRender === 'function') options.requestRender();
    return drew;
  }

  function dryLayer(id) {
    const layer = ensureLayer(id);
    if (!layer || !layer.containsPaint) return false;
    const scratchTarget = ensureScratch();
    gl.bindFramebuffer(gl.FRAMEBUFFER, scratchTarget.framebuffer);
    gl.viewport(0, 0, width, height);
    gl.disable(gl.BLEND);
    gl.useProgram(dryProgram);
    const position = bindQuad(dryProgram);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, layer.wet.texture);
    gl.uniform1i(gl.getUniformLocation(dryProgram, 'u_wet'), 0);
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, layer.dry.texture);
    gl.uniform1i(gl.getUniformLocation(dryProgram, 'u_dry'), 1);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
    gl.disableVertexAttribArray(position);
    gl.bindTexture(gl.TEXTURE_2D, layer.dry.texture);
    gl.copyTexSubImage2D(gl.TEXTURE_2D, 0, 0, 0, 0, 0, width, height);
    gl.bindTexture(gl.TEXTURE_2D, null);
    clearTarget(layer.wet);
    resetLayerFluidState(layer);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    layer.containsPaint = true;
    if (typeof options.requestRender === 'function') options.requestRender();
    return true;
  }

  function clearLayer(id) {
    const layer = ensureLayer(id);
    if (!layer) return false;
    clearTarget(layer.wet);
    clearTarget(layer.dry);
    layer.brushLoad.forEach(clearTarget);
    layer.knifeLoad.forEach(clearTarget);
    layer.brushLoadIndex = 0;
    layer.brushPaintAmount = 0;
    layer.knifeLoadIndex = 0;
    layer.knifePaintUnits = 0;
    layer.knifePaintColour = [0, 0, 0];
    resetLayerFluidState(layer);
    layer.containsPaint = false;
    if (typeof options.requestRender === 'function') options.requestRender();
    return true;
  }

  function setDisplaySettings(value = {}) {
    displaySettings = { ...displaySettings, ...(value || {}) };
    if (typeof options.requestRender === 'function') options.requestRender();
  }

  function getViewMode() {
    const view = String(displaySettings.view || 'final').toLowerCase();
    if (view === 'color' || view === 'colour') return 1;
    if (view === 'height' || view === 'heightmap') return 2;
    return 0;
  }

  function renderLayer(id, destinationFramebuffer, viewportWidth, viewportHeight, opacity = 1, viewModeOverride = null) {
    const layer = layers.get(String(id || '').trim());
    if (!layer || !layer.containsPaint) return false;
    if (Array.isArray(layer.simulationAreas) && layer.simulationAreas.length) stepFluidLayerSimulation(layer, 1);
    gl.bindFramebuffer(gl.FRAMEBUFFER, destinationFramebuffer);
    gl.viewport(0, 0, viewportWidth, viewportHeight);
    gl.enable(gl.BLEND);
    gl.blendFuncSeparate(gl.ONE, gl.ONE_MINUS_SRC_ALPHA, gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    gl.useProgram(displayProgram);
    const position = bindQuad(displayProgram);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, layer.wet.texture);
    gl.uniform1i(gl.getUniformLocation(displayProgram, 'u_wet'), 0);
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, layer.dry.texture);
    gl.uniform1i(gl.getUniformLocation(displayProgram, 'u_dry'), 1);
    gl.uniform2f(gl.getUniformLocation(displayProgram, 'u_pixelStep'), 1 / width, 1 / height);
    gl.uniform2f(gl.getUniformLocation(displayProgram, 'u_canvasRatio'), width / Math.max(1, height), 1);
    gl.uniform1f(
      gl.getUniformLocation(displayProgram, 'u_relief'),
      0.45 + clamp(Number(displaySettings.stickiness) / 100, 0, 1) * 2.4,
    );
    gl.uniform1f(gl.getUniformLocation(displayProgram, 'u_gloss'), clamp(Number(displaySettings.gloss) / 100, 0, 1));
    gl.uniform1f(gl.getUniformLocation(displayProgram, 'u_liquid'), clamp(Number(displaySettings.liquid) / 100, 0, 1));
    gl.uniform1f(gl.getUniformLocation(displayProgram, 'u_lightAngle'), clamp(displaySettings.lightAngle || 315, 0, 360) * Math.PI / 180);
    gl.uniform1f(gl.getUniformLocation(displayProgram, 'u_lightElevation'), clamp(displaySettings.lightElevation || 35, 1, 89) * Math.PI / 180);
    gl.uniform1f(gl.getUniformLocation(displayProgram, 'u_opacity'), clamp(opacity, 0, 1));
    gl.uniform1f(
      gl.getUniformLocation(displayProgram, 'u_viewMode'),
      Number.isFinite(Number(viewModeOverride)) ? Number(viewModeOverride) : getViewMode(),
    );
    gl.drawArrays(gl.TRIANGLES, 0, 6);
    gl.disableVertexAttribArray(position);
    gl.disable(gl.BLEND);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.activeTexture(gl.TEXTURE0);
    return true;
  }

  function captureLayerSnapshot(id) {
    const layer = layers.get(String(id || '').trim());
    if (!layer || gl.isContextLost()) return null;

    const snapshot = {
      wet: null,
      dry: null,
      brushLoad: null,
      brushPaintAmount: layer.brushPaintAmount,
      brushPaintColour: Array.isArray(layer.brushPaintColour) ? [...layer.brushPaintColour] : [0, 0, 0],
      knifeLoad: null,
      knifePaintUnits: Math.max(0, Number(layer.knifePaintUnits) || 0),
      knifePaintColour: Array.isArray(layer.knifePaintColour) ? [...layer.knifePaintColour] : [0, 0, 0],
      velocity: null,
      velocityTemp: null,
      divergence: null,
      pressure: null,
      pressureTemp: null,
      simulationAreas: Array.isArray(layer.simulationAreas) ? layer.simulationAreas.map((area) => ({ bounds: { ...area.bounds }, frameNumber: area.frameNumber })) : [],
      simulationFrameNumber: Number(layer.simulationFrameNumber) || 0,
      simulationLiquid: clamp(layer.simulationLiquid, 0, 1),
      containsPaint: Boolean(layer.containsPaint),
    };

    const allocations = [
      ['wet', layer.wet],
      ['dry', layer.dry],
      ['brushLoad', layer.brushLoad[layer.brushLoadIndex]],
      ['knifeLoad', layer.knifeLoad[layer.knifeLoadIndex]],
      ['velocity', layer.velocity],
      ['velocityTemp', layer.velocityTemp],
      ['divergence', layer.divergence],
      ['pressure', layer.pressure],
      ['pressureTemp', layer.pressureTemp],
    ];

    for (const [key, source] of allocations) {
      snapshot[key] = cloneTarget(source);
      if (!snapshot[key]) {
        destroyLayerSnapshot(snapshot);
        return null;
      }
    }
    return snapshot;
  }

  function restoreLayerSnapshot(id, snapshot) {
    if (!snapshot) return false;
    const layer = ensureLayer(id);
    if (!layer) return false;
    if (snapshot.wet) drawTexture(snapshot.wet.texture, layer.wet.framebuffer, width, height, false);
    if (snapshot.dry) drawTexture(snapshot.dry.texture, layer.dry.framebuffer, width, height, false);
    if (snapshot.brushLoad) drawTexture(snapshot.brushLoad.texture, layer.brushLoad[0].framebuffer, 1, 1, false);
    layer.brushPaintAmount = clamp(snapshot.brushPaintAmount, 0, 1);
    layer.brushPaintColour = Array.isArray(snapshot.brushPaintColour) ? [...snapshot.brushPaintColour] : layer.brushPaintColour;
    syncDisplayedBrushLoad(layer);
    if (snapshot.knifeLoad) drawTexture(snapshot.knifeLoad.texture, layer.knifeLoad[0].framebuffer, 1, 1, false);
    layer.brushLoadIndex = 0;
    layer.knifeLoadIndex = 0;
    layer.knifePaintUnits = Math.max(0, Number(snapshot.knifePaintUnits) || 0);
    layer.knifePaintColour = Array.isArray(snapshot.knifePaintColour) ? [...snapshot.knifePaintColour] : [0, 0, 0];
    if (snapshot.velocity) drawTexture(snapshot.velocity.texture, layer.velocity.framebuffer, width, height, false);
    if (snapshot.velocityTemp) drawTexture(snapshot.velocityTemp.texture, layer.velocityTemp.framebuffer, width, height, false);
    if (snapshot.divergence) drawTexture(snapshot.divergence.texture, layer.divergence.framebuffer, width, height, false);
    if (snapshot.pressure) drawTexture(snapshot.pressure.texture, layer.pressure.framebuffer, width, height, false);
    if (snapshot.pressureTemp) drawTexture(snapshot.pressureTemp.texture, layer.pressureTemp.framebuffer, width, height, false);
    layer.simulationAreas = Array.isArray(snapshot.simulationAreas) ? snapshot.simulationAreas.map((area) => ({ bounds: { ...area.bounds }, frameNumber: area.frameNumber })) : [];
    layer.simulationFrameNumber = Number(snapshot.simulationFrameNumber) || 0;
    layer.simulationLiquid = clamp(snapshot.simulationLiquid, 0, 1);
    layer.containsPaint = Boolean(snapshot.containsPaint);
    return true;
  }

  function destroyLayerSnapshot(snapshot) {
    if (!snapshot) return;
    deleteTarget(snapshot.wet);
    deleteTarget(snapshot.dry);
    deleteTarget(snapshot.brushLoad);
    deleteTarget(snapshot.knifeLoad);
    deleteTarget(snapshot.velocity);
    deleteTarget(snapshot.velocityTemp);
    deleteTarget(snapshot.divergence);
    deleteTarget(snapshot.pressure);
    deleteTarget(snapshot.pressureTemp);
  }

  function beginMove(id) {
    endMove(false);
    const layer = layers.get(String(id || '').trim());
    if (!layer || !layer.containsPaint) return false;
    moveSession = {
      id: String(id),
      wet: cloneTarget(layer.wet),
      dry: cloneTarget(layer.dry),
    };
    return true;
  }

  function updateMove(offsetX, offsetY) {
    if (!moveSession) return false;
    const layer = layers.get(moveSession.id);
    if (!layer) return false;
    clearTarget(layer.wet);
    clearTarget(layer.dry);
    drawTexture(moveSession.wet.texture, layer.wet.framebuffer, width, height, false, Math.round(Number(offsetX) || 0), -Math.round(Number(offsetY) || 0));
    drawTexture(moveSession.dry.texture, layer.dry.framebuffer, width, height, false, Math.round(Number(offsetX) || 0), -Math.round(Number(offsetY) || 0));
    resetLayerFluidState(layer);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    if (typeof options.requestRender === 'function') options.requestRender();
    return true;
  }

  function endMove(cancel = false) {
    if (!moveSession) return false;
    if (cancel) updateMove(0, 0);
    deleteTarget(moveSession.wet);
    deleteTarget(moveSession.dry);
    moveSession = null;
    return true;
  }

  function renderEncoded(sourceTexture, program, canvas) {
    const target = ensureExportTarget();
    gl.bindFramebuffer(gl.FRAMEBUFFER, target.framebuffer);
    gl.viewport(0, 0, width, height);
    gl.disable(gl.BLEND);
    gl.useProgram(program);
    const position = bindQuad(program);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, sourceTexture);
    gl.uniform1i(gl.getUniformLocation(program, 'u_source'), 0);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
    gl.disableVertexAttribArray(position);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    drawTexture(target.texture, null, canvas.width, canvas.height, false);
    return canvas.toDataURL('image/png');
  }

  function exportLayerState(id, canvas) {
    const layer = layers.get(String(id || '').trim());
    if (!layer || !layer.containsPaint || !canvas) return null;
    return {
      version: 1,
      wetColour: renderEncoded(layer.wet.texture, encodeColourProgram, canvas),
      wetHeight: renderEncoded(layer.wet.texture, encodeHeightProgram, canvas),
      dryColour: renderEncoded(layer.dry.texture, encodeColourProgram, canvas),
      dryHeight: renderEncoded(layer.dry.texture, encodeHeightProgram, canvas),
    };
  }

  function loadImage(url) {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error('Unable to load oil paint state image.'));
      image.src = url;
    });
  }

  function createImageTexture(image) {
    const texture = gl.createTexture();
    if (!texture) throw new Error('Unable to create oil paint image texture.');
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
    return texture;
  }

  async function importLayerState(id, state) {
    if (!state || typeof state !== 'object') return false;
    const layer = ensureLayer(id);
    if (!layer) return false;
    const [wetColourImage, wetHeightImage, dryColourImage, dryHeightImage] = await Promise.all([
      loadImage(state.wetColour),
      loadImage(state.wetHeight),
      loadImage(state.dryColour),
      loadImage(state.dryHeight),
    ]);
    const pairs = [
      [wetColourImage, wetHeightImage, layer.wet],
      [dryColourImage, dryHeightImage, layer.dry],
    ];
    for (const [colourImage, heightImage, destination] of pairs) {
      const colourTexture = createImageTexture(colourImage);
      const heightTexture = createImageTexture(heightImage);
      gl.bindFramebuffer(gl.FRAMEBUFFER, destination.framebuffer);
      gl.viewport(0, 0, width, height);
      gl.disable(gl.BLEND);
      gl.useProgram(decodeProgram);
      const position = bindQuad(decodeProgram);
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, colourTexture);
      gl.uniform1i(gl.getUniformLocation(decodeProgram, 'u_colourImage'), 0);
      gl.activeTexture(gl.TEXTURE1);
      gl.bindTexture(gl.TEXTURE_2D, heightTexture);
      gl.uniform1i(gl.getUniformLocation(decodeProgram, 'u_heightImage'), 1);
      gl.drawArrays(gl.TRIANGLES, 0, 6);
      gl.disableVertexAttribArray(position);
      gl.deleteTexture(colourTexture);
      gl.deleteTexture(heightTexture);
    }
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.activeTexture(gl.TEXTURE0);
    resetLayerFluidState(layer);
    layer.containsPaint = true;
    if (typeof options.requestRender === 'function') options.requestRender();
    return true;
  }

  function resize(nextWidth, nextHeight) {
    width = Math.max(1, Math.floor(Number(nextWidth) || 1));
    height = Math.max(1, Math.floor(Number(nextHeight) || 1));
    for (const id of Array.from(layers.keys())) {
      deleteLayer(id);
      createLayer(id);
    }
    for (const session of strokeSessions.values()) {
      deleteTarget(session.baseWet);
      deleteTarget(session.knifeSourceWet);
      deleteTarget(session.knifeSourceDry);
      deleteTarget(session.knifeSweep);
    }
    strokeSessions.clear();
    deleteTarget(scratch);
    scratch = null;
    deleteTarget(stickinessFlowTarget);
    stickinessFlowTarget = null;
    deleteTarget(paintFlowTarget);
    paintFlowTarget = null;
    deleteTarget(exportTarget);
    exportTarget = null;
    if (simulationAnimationFrame != null && typeof cancelAnimationFrame === 'function') {
      cancelAnimationFrame(simulationAnimationFrame);
      simulationAnimationFrame = null;
    }
    endMove(false);
  }

  function hasPaint(id) {
    if (id != null) return Boolean(layers.get(String(id || '').trim())?.containsPaint);
    return Array.from(layers.values()).some((layer) => layer.containsPaint);
  }

  function destroy() {
    if (destroyed) return;
    destroyed = true;
    for (const id of Array.from(layers.keys())) deleteLayer(id);
    for (const session of strokeSessions.values()) {
      deleteTarget(session.baseWet);
      deleteTarget(session.knifeSourceWet);
      deleteTarget(session.knifeSourceDry);
      deleteTarget(session.knifeSweep);
    }
    strokeSessions.clear();
    deleteTarget(scratch);
    deleteTarget(brushMaskTarget);
    deleteTarget(brushSideMaskTarget);
    deleteTarget(stickinessFlowTarget);
    deleteTarget(paintFlowTarget);
    deleteTarget(exportTarget);
    if (simulationAnimationFrame != null && typeof cancelAnimationFrame === 'function') {
      cancelAnimationFrame(simulationAnimationFrame);
      simulationAnimationFrame = null;
    }
    endMove(false);
    gl.deleteBuffer(quadBuffer);
    gl.deleteBuffer(documentBuffer);
    gl.deleteBuffer(brushMaskBuffer);
    gl.deleteProgram(quadProgram);
    gl.deleteProgram(tubeStrokeProgram);
    gl.deleteProgram(fluidVelocitySplatProgram);
    gl.deleteProgram(fluidAdvectProgram);
    gl.deleteProgram(fluidDivergenceProgram);
    gl.deleteProgram(fluidJacobiProgram);
    gl.deleteProgram(fluidSubtractProgram);
    gl.deleteProgram(stickinessFlowProgram);
    gl.deleteProgram(stickinessGatherProgram);
    gl.deleteProgram(brushFlowProgram);
    gl.deleteProgram(paintRelaxFlowProgram);
    gl.deleteProgram(massGatherProgram);
    gl.deleteProgram(updateProgram);
    gl.deleteProgram(brushMaskProgram);
    gl.deleteProgram(knifeSweepMaskProgram);
    gl.deleteProgram(knifeDryClearProgram);
    gl.deleteProgram(brushDryClearProgram);
    gl.deleteProgram(importedPaintProgram);
    gl.deleteProgram(importedDryClearProgram);
    gl.deleteProgram(knifeWetFloorProgram);
    gl.deleteProgram(dryProgram);
    gl.deleteProgram(displayProgram);
    gl.deleteProgram(loadCaptureProgram);
    gl.deleteProgram(encodeColourProgram);
    gl.deleteProgram(encodeHeightProgram);
    gl.deleteProgram(decodeProgram);
  }

  return {
    beginMove,
    beginStroke,
    captureLayerSnapshot,
    clearLayer,
    deleteLayer,
    destroy,
    destroyLayerSnapshot,
    drawImportedDab,
    drawStroke,
    dryLayer,
    endMove,
    endStroke,
    ensureLayer,
    exportLayerState,
    getDebugMetrics,
    getViewMode,
    hasPaint,
    importLayerState,
    renderLayer,
    resize,
    restoreLayerSnapshot,
    setDisplaySettings,
    updateMove,
  };
}
